// This file is auto-generated by @hey-api/openapi-ts

/**
 * API type for AIR (ICM_YLT) per-location results.
 *
 * ICM_YLT data is persisted in the identical ``RMSLocationResult`` JSONB
 * shape via the shared elt_persist pipeline, so this is structurally a copy.
 * It exists purely to give the AIR-backed ``air`` field a vendor-accurate
 * name in the public API / generated client, distinct from the RMS results.
 */
export type AirLocationResult = {
  location_id?: string;
  location_number?: string;
  location_name?: string;
  street_address?: string;
  city?: string;
  aal_gu?: number;
  address_id: string;
  aal_gu_by_peril?: {
    [key: string]: number;
  };
  aal_gr_by_peril?: {
    [key: string]: number;
  };
  aal_rl_by_peril?: {
    [key: string]: number;
  };
  location_detail?: RmsLocationDetail | null;
  is_foreign_exposure?: boolean;
};

export type AalPremiumGateBar = {
  gate: string;
  aal_budget: number;
  bound_aal: number;
  quoted_aal: number;
  current_aal: number | null;
  loss_ratio: AalPremiumGateLossRatio;
};

export type AalPremiumGateLossRatio = {
  target: number;
  bound: number | null;
  bound_and_quoted: number | null;
  effect_on_bound: number | null;
};

export type AalPremiumGateResponse = {
  capacity_breakpoints: Array<GateBreakpoint>;
  bars: Array<AalPremiumGateBar>;
};

export type AalPremiumGateTrackingRequest = {
  report_id: string;
  layer_id: string;
};

/**
 * One storm the NHC is currently issuing advisories for.
 */
export type ActiveStorm = {
  id: string;
  name: string;
  classification: string;
  intensity_kt: number;
  lat: number;
  lon: number;
  bin_number: string;
  last_update: string;
};

/**
 * Every storm the NHC is advising on. Empty out of season.
 */
export type ActiveStorms = {
  storms: Array<StormWithTrack>;
};

export type AddCommentRequest = {
  report_id: string;
  message: string;
  target_type: CommentTargetType | null;
  target_key: string | null;
};

export type AddCommentResponse = {
  comments: Comments;
};

export type AddManualAddressRequest = {
  report_id: string;
  address: RawAddress;
};

export type AddManualAddressResponse = {
  report: ReportResponse;
};

export type AddSavedFilterRequest = {
  url: string;
  nickname: string;
  is_default: boolean;
};

export type AdditionalAssigneeInfo = {
  assignees: {
    [key: string]: string;
  };
};

export type AdditionalFile = {
  name: string;
  uploaded_at: string | null;
};

export type Address = {
  address_id: string;
  is_deleted?: boolean | null;
  default_sort_key?: string | null;
  private_computed_values: RawAddress;
  private_original_values: {
    [key: string]: string | null;
  };
  private_overrides: {
    [key: string]: string | number | null;
  };
  private_flags: {
    [key: string]: Array<
      | "missing_coverage_headers"
      | "date_parsing_error"
      | "number_parsing_error"
      | "large_number_warning"
      | "suspicious_year_warning"
      | "year_out_of_range_warning"
      | "future_year_warning"
      | "general_mismatch"
      | "low_confidence"
      | "medium_confidence"
      | "non_property_insurance_value"
      | "unrecognized_central_alarm_value"
      | "unrecognized_yes_no_value"
      | "low_sprinkler_percentage"
      | "unrecognized_sprinkler_value"
      | "zip_parsing_error"
      | "blank_tiv_value"
      | "non_ascii_removed"
      | "address_corrected_by_geocoding"
      | "geocoding_failed"
      | "location_number_mismatch"
      | "year_defaulted_to_year_built"
      | "approximate_geocoding"
      | "high_protection_class"
      | "tier_1_wind_zone"
      | "itv_calc_warning"
      | "bi_estimate_warning"
      | "coverage_adjusted_by_reconciliation"
      | "zip_unrecognized"
      | "zip_not_rater_valid"
      | "zip_state_mismatch"
      | "number_of_buildings_replaced"
      | UnknownCoverageColumnFlag
      | UnknownInsuranceSubjectFlag
      | AddressCorrectedByModel
      | AddressFlaggedByGeocoding
      | CoverageComputedFromColumnSum
    >;
  };
  ofac_result?: OfacResult | null;
};

export type AddressCorrectedByModel = {
  flag_id: "address_corrected_by_llm_parse";
  original: string;
};

export type AddressFlaggedByGeocoding = {
  flag_id: "address_flagged_by_geocoding";
  geocoded: string | null;
};

export type AddressIntegrationData = {
  rms?: RmsLocationResult | null;
  air?: AirLocationResult | null;
  spatialkat?: SpatialKatLocationResult | null;
  geographic_enrichment?: GeographicEnrichmentData | null;
  hazard_hub?: HazardHubData | null;
  nearmap?: NearmapData | null;
  nearmap_ai?: NearmapAiData | null;
  cape?: CapeData | null;
  sindri?: SindriRow | null;
};

export type AddressList = {
  addresses: Array<Address>;
};

export type AdvancedDeductible = {
  peril_type:
    | "aop"
    | "earthquake"
    | "earth_movement"
    | "named_storm"
    | "scs"
    | "flood"
    | "wildfire"
    | "equipment_breakdown"
    | "wind_and_hail";
  short_deductible_description: string;
  executable_python_function_str: string;
  executable_python_function_override?: string | null;
  override_authored_against_form?: string | null;
  direct_excerpts?: {
    [key: string]: string;
  } | null;
  manual_excerpt_overrides?: {
    [key: string]: string;
  } | null;
  removed_excerpt_names?: Array<string> | null;
  excerpt_sort_keys?: {
    [key: string]: string;
  } | null;
};

export type AdvancedDeductibleInfo = {
  extracted_deductibles: {
    [key: string]: {
      [key: string]: AdvancedDeductible;
    };
  };
  manual_deductibles: {
    [key: string]: AdvancedDeductible;
  };
  extraction_instructions?: {
    [key: string]: string;
  };
};

export type AdvancedLimit = {
  peril_type:
    | "aop"
    | "earthquake"
    | "earth_movement"
    | "named_storm"
    | "scs"
    | "flood"
    | "wildfire"
    | "equipment_breakdown"
    | "wind_and_hail";
  short_limit_description: string;
  document_quotation: string;
  executable_python_function_str: string;
  executable_python_function_override?: string | null;
  override_authored_against_form?: string | null;
  direct_excerpts?: {
    [key: string]: string;
  } | null;
  manual_excerpt_overrides?: {
    [key: string]: string;
  } | null;
  removed_excerpt_names?: Array<string> | null;
  excerpt_sort_keys?: {
    [key: string]: string;
  } | null;
};

export type AdvancedLimitInfo = {
  extracted_limits: {
    [key: string]: {
      [key: string]: AdvancedLimit;
    };
  };
  manual_limits: {
    [key: string]: AdvancedLimit;
  };
  extraction_instructions?: {
    [key: string]: string;
  };
};

export type Appetite = {
  appetite_result:
    | "in_appetite"
    | "sic_codes_invalid"
    | "no_sov_file"
    | "no_loss_run_file"
    | "far_away_effective_date"
    | "low_tiv"
    | "prior_submission"
    | "updated_submission"
    | "missing_cope_info"
    | "foreign_exposure";
  appetite_results?: Array<
    | "in_appetite"
    | "sic_codes_invalid"
    | "no_sov_file"
    | "no_loss_run_file"
    | "far_away_effective_date"
    | "low_tiv"
    | "prior_submission"
    | "updated_submission"
    | "missing_cope_info"
    | "foreign_exposure"
  >;
};

export type appetite_result =
  | "in_appetite"
  | "sic_codes_invalid"
  | "no_sov_file"
  | "no_loss_run_file"
  | "far_away_effective_date"
  | "low_tiv"
  | "prior_submission"
  | "updated_submission"
  | "missing_cope_info"
  | "foreign_exposure";

export const appetite_result = {
  IN_APPETITE: "in_appetite",
  SIC_CODES_INVALID: "sic_codes_invalid",
  NO_SOV_FILE: "no_sov_file",
  NO_LOSS_RUN_FILE: "no_loss_run_file",
  FAR_AWAY_EFFECTIVE_DATE: "far_away_effective_date",
  LOW_TIV: "low_tiv",
  PRIOR_SUBMISSION: "prior_submission",
  UPDATED_SUBMISSION: "updated_submission",
  MISSING_COPE_INFO: "missing_cope_info",
  FOREIGN_EXPOSURE: "foreign_exposure",
} as const;

/**
 * Application-local state stored in ``policy_application.working_set`` —
 * only the overlay and underwriter notes, never a copy of report data.
 */
export type ApplicationWorkingSet = {
  overlay: PolicyFieldOverlay;
  notes: string | null;
};

/**
 * What sits inside a drawn shape, and what a loss there would reach.
 *
 * Severity is the request's ``damage_ratio``.
 */
export type AreaExposure = {
  locations: number;
  tiv: number;
  policy_count: number;
  reachable_line: number;
  blocked_line: number;
  policies_without_layers: number;
  policies: Array<PolicyExposure>;
  measured: Array<Array<LatLon>>;
  truncated: boolean;
};

export type AreaExposureRequest = {
  lob: string;
  statuses: Array<
    | "pre_underwriting_review"
    | "blocked"
    | "not_cleared_prior_submission"
    | "not_cleared_incomplete_submission"
    | "not_cleared_out_of_appetite"
    | "not_cleared_effective_date_too_far"
    | "not_cleared_unapproved_broker"
    | "not_cleared_low_tiv"
    | "not_cleared_foreign_exposure"
    | "cleared"
    | "pending"
    | "modeling_complete"
    | "referral_sent"
    | "flagged_for_review"
    | "indicated"
    | "quoted"
    | "declined"
    | "closed"
    | "closed_bor"
    | "not_reviewed"
    | "lost"
    | "quoted_not_taken_up"
    | "bound"
    | "issued"
  > | null;
  polygon: Array<LatLon>;
  buffer_miles: number;
  track_radii_miles: Array<number> | null;
  damage_ratio: number;
};

/**
 * Assessor fields beyond the COPE construction set (``enhanced_property.assessment``).
 */
export type AssessmentData = {
  total_assessed_value?: number | null;
  tax_market_value?: number | null;
  assessed_improvement_value?: number | null;
  assessed_land_value?: number | null;
  tax_amount?: number | null;
  number_of_bedrooms?: number | null;
  number_of_baths?: number | null;
  garage_cars?: number | null;
  lot_size_acres?: number | null;
  lot_size_square_feet?: number | null;
  building_area_1?: number | null;
  owner_occupied?: string | null;
};

export type Attachment = {
  name: string;
  from_email: boolean;
  original_mapping:
    | "sov_spreadsheet"
    | "acord_app"
    | "loss_run"
    | "none"
    | "screenshot"
    | "rater"
    | "loss_control_report"
    | "policy_document"
    | "manuscript_form"
    | "final_manuscript_form"
    | "specs_sheet"
    | "record_keeping"
    | "luminor_quote"
    | "luminor_tria"
    | "luminor_binder"
    | "luminor_policy"
    | "lead_quote"
    | "lead_binder"
    | "lead_policy"
    | "bound_sov"
    | "sl_letter"
    | "endorsement_file"
    | "endorsement_sov"
    | "engineering_report"
    | "rate_matrix"
    | "form_review";
  manual_mapping:
    | "sov_spreadsheet"
    | "acord_app"
    | "loss_run"
    | "none"
    | "screenshot"
    | "rater"
    | "loss_control_report"
    | "policy_document"
    | "manuscript_form"
    | "final_manuscript_form"
    | "specs_sheet"
    | "record_keeping"
    | "luminor_quote"
    | "luminor_tria"
    | "luminor_binder"
    | "luminor_policy"
    | "lead_quote"
    | "lead_binder"
    | "lead_policy"
    | "bound_sov"
    | "sl_letter"
    | "endorsement_file"
    | "endorsement_sov"
    | "engineering_report"
    | "rate_matrix"
    | "form_review"
    | null;
  source?: "email" | "screenshot_tool" | "user_upload" | "generated" | null;
  conversation_id?: string | null;
  policy_application_id?: string | null;
  endorsement_id?: string | null;
};

export type original_mapping =
  | "sov_spreadsheet"
  | "acord_app"
  | "loss_run"
  | "none"
  | "screenshot"
  | "rater"
  | "loss_control_report"
  | "policy_document"
  | "manuscript_form"
  | "final_manuscript_form"
  | "specs_sheet"
  | "record_keeping"
  | "luminor_quote"
  | "luminor_tria"
  | "luminor_binder"
  | "luminor_policy"
  | "lead_quote"
  | "lead_binder"
  | "lead_policy"
  | "bound_sov"
  | "sl_letter"
  | "endorsement_file"
  | "endorsement_sov"
  | "engineering_report"
  | "rate_matrix"
  | "form_review";

export const original_mapping = {
  SOV_SPREADSHEET: "sov_spreadsheet",
  ACORD_APP: "acord_app",
  LOSS_RUN: "loss_run",
  NONE: "none",
  SCREENSHOT: "screenshot",
  RATER: "rater",
  LOSS_CONTROL_REPORT: "loss_control_report",
  POLICY_DOCUMENT: "policy_document",
  MANUSCRIPT_FORM: "manuscript_form",
  FINAL_MANUSCRIPT_FORM: "final_manuscript_form",
  SPECS_SHEET: "specs_sheet",
  RECORD_KEEPING: "record_keeping",
  LUMINOR_QUOTE: "luminor_quote",
  LUMINOR_TRIA: "luminor_tria",
  LUMINOR_BINDER: "luminor_binder",
  LUMINOR_POLICY: "luminor_policy",
  LEAD_QUOTE: "lead_quote",
  LEAD_BINDER: "lead_binder",
  LEAD_POLICY: "lead_policy",
  BOUND_SOV: "bound_sov",
  SL_LETTER: "sl_letter",
  ENDORSEMENT_FILE: "endorsement_file",
  ENDORSEMENT_SOV: "endorsement_sov",
  ENGINEERING_REPORT: "engineering_report",
  RATE_MATRIX: "rate_matrix",
  FORM_REVIEW: "form_review",
} as const;

export type AttachmentInfo = {
  attachments: Array<Attachment>;
};

export type AutoAssignmentStyle = {
  assignment_type: "round_robin" | "override_in_email";
  original_assignee: string;
};

export type assignment_type = "round_robin" | "override_in_email";

export const assignment_type = {
  ROUND_ROBIN: "round_robin",
  OVERRIDE_IN_EMAIL: "override_in_email",
} as const;

export type BatchCommentsRequest = {
  report_ids: Array<string>;
  target_type: CommentTargetType | null;
};

export type BatchCommentsResponse = {
  comments_by_report_id: {
    [key: string]: Comments;
  };
};

export type BodyAsgardApplicationQuoteUnknowns = {
  application_id: string;
  template?: (Blob | File) | null;
};

export type BodyAsgardCreateEndorsement = {
  report_id: string;
  policy_application_id: string;
  effective_date: string;
  description: string;
  premium_basis: EndorsementPremiumBasis;
  tiv_target?: number | null;
  custom_term_premium_delta?: number | null;
  files?: Array<Blob | File> | null;
};

export type BodyAsgardCreateTemplate = {
  template: Blob | File;
};

export type BodyAsgardGenerateApplicationQuote = {
  application_id: string;
  output_format: "docx" | "pdf";
  save_to_report: boolean;
  persist_template: boolean;
  full_package: boolean;
  template?: (Blob | File) | null;
};

export type output_format = "docx" | "pdf";

export const output_format = {
  DOCX: "docx",
  PDF: "pdf",
} as const;

export type BodyAsgardSaveApplicationQuoteTemplate = {
  application_id: string;
  template: Blob | File;
};

export type BodyAsgardUploadAdditionalFile = {
  files: Array<Blob | File>;
};

export type BodyAsgardUploadEmailThread = {
  files: Array<Blob | File>;
};

export type BodyAsgardUploadEndorsementDocuments = {
  endorsement_id: string;
  files: Array<Blob | File>;
};

export type BodyBdxPerilBreakdown = {
  file: Blob | File;
};

export type BodyLogin = {
  grant_type?: string | null;
  username: string;
  password: string;
  scope?: string;
  client_id?: string | null;
  client_secret?: string | null;
};

export type BookSummaryResponse = {
  total_tiv: number;
  states: Array<StateSummary>;
};

/**
 * One sent broker email, stored in BorLetter.emails_sent keyed by step.
 */
export type BorEmailRecord = {
  sent_at: string;
  recipients: Array<string>;
  subject: string;
  email_type: string;
};

export type BorEmailRequest = {
  bor_id: string;
  step:
    | "outgoing_broker_notified_at"
    | "incoming_broker_notified_at"
    | "rescind_letter_received_at"
    | "finalized_at"
    | "outgoing_broker_resolution_sent_at"
    | "incoming_broker_resolution_sent_at";
};

export type step =
  | "outgoing_broker_notified_at"
  | "incoming_broker_notified_at"
  | "rescind_letter_received_at"
  | "finalized_at"
  | "outgoing_broker_resolution_sent_at"
  | "incoming_broker_resolution_sent_at";

export const step = {
  OUTGOING_BROKER_NOTIFIED_AT: "outgoing_broker_notified_at",
  INCOMING_BROKER_NOTIFIED_AT: "incoming_broker_notified_at",
  RESCIND_LETTER_RECEIVED_AT: "rescind_letter_received_at",
  FINALIZED_AT: "finalized_at",
  OUTGOING_BROKER_RESOLUTION_SENT_AT: "outgoing_broker_resolution_sent_at",
  INCOMING_BROKER_RESOLUTION_SENT_AT: "incoming_broker_resolution_sent_at",
} as const;

export type BorLetterListResponse = {
  bor_letters: Array<BorLetterResponse>;
};

export type BorLetterResponse = {
  id: string;
  client_id: string;
  cleared_report_id: string;
  incoming_report_id: string;
  effective_date: string | null;
  notes: string | null;
  created_by: string | null;
  received_at: string;
  outgoing_broker_notified_at: string | null;
  incoming_broker_notified_at: string | null;
  rescind_letter_received_at: string | null;
  finalized_at: string | null;
  outgoing_broker_resolution_sent_at: string | null;
  incoming_broker_resolution_sent_at: string | null;
  rescind_deadline: string | null;
  is_resolved: boolean;
  notification_sla_deadline: string | null;
  notifications_sent: {
    [key: string]: string;
  };
  emails_sent: {
    [key: string]: BorEmailRecord;
  };
};

/**
 * One building/structure on the parcel, joining buildings + roof_age + geoJson.
 */
export type BuildingData = {
  id?: string | null;
  roof_age_years?: number | null;
  materials?: Array<string> | null;
  shapes?: Array<string> | null;
};

export type BulkEditRequest = {
  report_id: string;
  field: string;
  instructions: string;
};

export type BulkEditResponse = {
  before: Array<Address>;
  after: Array<Address>;
};

export type BulkEnrichmentReportResult = {
  report_id: string;
  company_name: string | null;
  num_addresses: number;
  enqueued_sources: Array<"nearmap" | "hazard_hub" | "cape">;
  skipped_reason: string | null;
};

export type BulkEnrichmentRequest = {
  report_ids: Array<string>;
  sources: Array<"nearmap" | "hazard_hub" | "cape">;
};

export type BulkEnrichmentResponse = {
  results: Array<BulkEnrichmentReportResult>;
  enqueued_count: number;
  skipped_count: number;
};

export type CalculatePremiumRequest = {
  report_id: string;
  batch_id?: string | null;
};

export type CalculatedPremiumResponse = {
  layers: Array<LayerPremiumBreakdown>;
  errors?: Array<PremiumError>;
};

export type CancelJobRequest = {
  report_integration_id: string;
};

export type CancelJobResponse = unknown;

/**
 * Curated subset of the Cape Analytics commercial property response.
 *
 * Construct from a validated Cape envelope with :meth:`from_cape`. The model
 * stays a plain round-trippable struct (``model_validate`` / ``model_dump``)
 * so it can be persisted and re-read without re-parsing. Kept deliberately
 * slim (this is stored per-address); the full response is archived to GCS.
 */
export type CapeData = {
  structure_count?: number | null;
  weighted_roof_condition_rating?: number | null;
  pool_count?: number | null;
  wildfire_hazard_potential?: string | null;
  primary_structure?: CapeStructureData | null;
  derived_modifiers?: {
    [key: string]: string;
  };
};

export type CapeInitiateRequest = {
  report_id: string;
};

export type CapeInitiateResponse = {
  request_id: string;
};

export type CapeJobHistoryItem = {
  batch_id: string;
  status: WorkStatus;
  started_at: string;
  finished_at: string | null;
  num_addresses: number;
  enriched_count: number;
};

export type CapeJobHistoryResponse = {
  jobs?: Array<CapeJobHistoryItem>;
};

/**
 * Displayed attributes of one structure/building, flattened from ``structures[]``.
 *
 * Deliberately slim: only the scalars the SOV grid renders are kept, so the
 * per-address row we persist stays small. The full structure detail (all
 * attributes, every structure) lives in the raw envelope archived to GCS and
 * can be backfilled here later if a consumer needs more.
 */
export type CapeStructureData = {
  roof_covering?: string | null;
  roof_geometry?: string | null;
  roof_age_years?: number | null;
  roof_replacement_year?: number | null;
  solar_panel?: boolean | null;
  tree_overhang?: boolean | null;
  number_of_stories?: string | null;
  year_built?: number | null;
  construction_type?: string | null;
  structure_exterior?: string | null;
  structure_foundation?: string | null;
};

/**
 * Result of promoting a run to the report's live results for its vendor.
 */
export type CatActivateResult = {
  integration_type: "spatialkat" | "rms_elt" | "rms_22" | "icm_ylt";
  batch_id: string;
};

export type integration_type = "spatialkat" | "rms_elt" | "rms_22" | "icm_ylt";

export const integration_type = {
  SPATIALKAT: "spatialkat",
  RMS_ELT: "rms_elt",
  RMS_22: "rms_22",
  ICM_YLT: "icm_ylt",
} as const;

export type CatAggregateData = {
  aal_combined_groundup?: number | null;
  aal_per_peril_groundup?: {
    [key: string]: number;
  };
  layers?: Array<CatLayerResult>;
  ep_curves?: Array<EpRow>;
  standard_deviation_combined_groundup?: number | null;
  standard_deviation_per_peril_groundup?: {
    [key: string]: number;
  };
  pmi?: CatPmiData | null;
};

export type CatJobHistoryItem = {
  type: "spatialkat" | "rms_elt" | "rms_22" | "icm_ylt";
  batch_id: string;
  status: WorkStatus;
  started_at: string;
  finished_at: string | null;
  num_addresses?: number;
  total_aal?: number | null;
  total_tiv?: number | null;
  foreign_tiv?: number | null;
  num_layers?: number;
  enriched?: boolean;
  enrichment_source?: string | null;
  is_test?: boolean;
  sub_tasks_count?: number;
  sub_tasks_completed?: number;
  is_active?: boolean;
  auto_run?: boolean;
  run_type?: "underwriting" | "test_run";
  term: "annual" | "policy" | null;
};

export type type = "spatialkat" | "rms_elt" | "rms_22" | "icm_ylt";

export const type = {
  SPATIALKAT: "spatialkat",
  RMS_ELT: "rms_elt",
  RMS_22: "rms_22",
  ICM_YLT: "icm_ylt",
} as const;

export type run_type = "underwriting" | "test_run";

export const run_type = {
  UNDERWRITING: "underwriting",
  TEST_RUN: "test_run",
} as const;

export type CatJobHistoryResponse = {
  jobs?: Array<CatJobHistoryItem>;
};

export type CatLayerResult = {
  layer_guid: string;
  perils?: {
    [key: string]: CatPerilLayerData;
  };
  totals?: CatPerilLayerData;
  ep_curves?: Array<EpRow>;
  locations?: Array<CatLocationWithAddress>;
};

export type CatLocationWithAddress = {
  aal_combined?: number | null;
  aal_per_peril?: {
    [key: string]: number;
  };
  address?: LocationAddress | null;
  is_foreign_exposure?: boolean;
};

/**
 * What a modeling or rating job ran under.
 */
export type CatMetadata = {
  enrichment_source: "nearmap" | "cape" | null;
};

export type CatPerilLayerData = {
  aal?: number | null;
  pml_per_period?: {
    [key: string]: number;
  };
};

/**
 * Portfolio Marginal Impact: this account's contribution to the
 * combined in-force book's PML at each return period — its average loss
 * across the catalog years ranked around that PML's threshold, at 100%
 * of each enabled layer (gross, matching the PML columns; pinned against
 * participation edits). Account-wide, per peril only (no all-peril
 * figure — cross-peril year alignment in the catalog is undocumented);
 * PMIs do not sum across perils or layers.
 */
export type CatPmiData = {
  per_peril?: {
    [key: string]: {
      [key: string]: number;
    };
  };
  as_of: string;
  portfolio_report_count: number;
};

/**
 * What the user sees on cat tab load — human-readable warnings about
 * foreign exposure, geocoding gaps, and location limits, per vendor.
 */
export type CatPrecheckResponse = {
  warnings: Array<string>;
  blocking_reasons: Array<string>;
  icm: VendorPrecheck;
  rms: VendorPrecheck;
  rms_22: VendorPrecheck;
  spatialkat: VendorPrecheck;
};

/**
 * Result of kicking off a financial-only re-run for a cat integration.
 *
 * ``batch_id`` is the new batch created for the re-run (poll it for
 * progress); ``source_batch_id`` is the original batch whose cached
 * model artifacts are reused.
 */
export type CatRerunFinancialResult = {
  integration_type: "spatialkat" | "rms_elt" | "rms_22" | "icm_ylt";
  batch_id: string;
  source_batch_id: string;
};

export type CatResultsResponse = {
  rms_elt?: CatSourceData | null;
  rms_22?: CatSourceData | null;
  spatialkat?: CatSourceData | null;
  icm_ylt?: CatSourceData | null;
};

/**
 * Result of resuming a cat integration's batch in place. ``batch_id``
 * is the resumed batch — poll it for progress, exactly as for a normal
 * run.
 */
export type CatResumeResult = {
  integration_type: "spatialkat" | "rms_elt" | "rms_22" | "icm_ylt";
  batch_id: string;
};

/**
 * What has changed on a report since its last complete CAT run. A ``str``
 * enum so it serializes to its value over the API.
 */
export type CatRunChange =
  | "no_complete_run"
  | "unchanged"
  | "terms_changed"
  | "full_remodel"
  | "no_baseline";

/**
 * What has changed on a report since its last complete CAT run. A ``str``
 * enum so it serializes to its value over the API.
 */
export const CatRunChange = {
  NO_COMPLETE_RUN: "no_complete_run",
  UNCHANGED: "unchanged",
  TERMS_CHANGED: "terms_changed",
  FULL_REMODEL: "full_remodel",
  NO_BASELINE: "no_baseline",
} as const;

/**
 * A specific trigger behind a ``CatRunChange``, for the UI to render into
 * user-facing copy. A ``str`` enum so it serializes to its value over the API.
 */
export type CatRunChangeReason =
  | "addresses"
  | "effective_date"
  | "expiration_date"
  | "limits"
  | "deductibles"
  | "layers"
  | "new_modelable_peril"
  | "enrichment"
  | "no_snapshot";

/**
 * A specific trigger behind a ``CatRunChange``, for the UI to render into
 * user-facing copy. A ``str`` enum so it serializes to its value over the API.
 */
export const CatRunChangeReason = {
  ADDRESSES: "addresses",
  EFFECTIVE_DATE: "effective_date",
  EXPIRATION_DATE: "expiration_date",
  LIMITS: "limits",
  DEDUCTIBLES: "deductibles",
  LAYERS: "layers",
  NEW_MODELABLE_PERIL: "new_modelable_peril",
  ENRICHMENT: "enrichment",
  NO_SNAPSHOT: "no_snapshot",
} as const;

export type CatRunChangeReasonDetail = {
  reason: CatRunChangeReason;
  before: string | null;
  after: string | null;
};

export type CatRunChangeResult = {
  change: CatRunChange;
  reasons: Array<CatRunChangeReasonDetail>;
  is_auto_run_shadow: boolean;
};

/**
 * The run hit an unexpected error while starting. It has been reported.
 */
export type CatRunFailed = {
  status: "failed";
  type:
    | "spatialkat"
    | "rms_elt"
    | "rms_22"
    | "icm_ylt"
    | "rater"
    | "eqbd_rater";
  detail: string;
};

/**
 * A baseline run was refused because one is already in flight.
 */
export type CatRunInFlight = {
  status: "in_flight";
  type: "spatialkat" | "rms_elt" | "rms_22" | "icm_ylt";
  detail: string;
};

export type CatRunRef = {
  type:
    | "spatialkat"
    | "rms_elt"
    | "rms_22"
    | "icm_ylt"
    | "rater"
    | "eqbd_rater";
  request_id: string;
};

/**
 * The run is not available to this user or client on this report.
 */
export type CatRunRefused = {
  status: "refused";
  type:
    | "spatialkat"
    | "rms_elt"
    | "rms_22"
    | "icm_ylt"
    | "rater"
    | "eqbd_rater";
  detail: string;
};

export type CatRunRequest = {
  runs: Array<CatRunSpec | RaterRunSpec | EqbdRaterRunSpec>;
};

export type CatRunResult = {
  runs: Array<CatRunStarted | CatRunInFlight | CatRunRefused | CatRunFailed>;
};

export type CatRunSpec = {
  type: "spatialkat" | "rms_elt" | "rms_22" | "icm_ylt";
  activate: boolean;
  enrichment_source: "nearmap" | "cape" | null;
  mode: "default" | "full" | "finance";
  run_type: "underwriting" | "test_run";
};

export type mode = "default" | "full" | "finance";

export const mode = {
  DEFAULT: "default",
  FULL: "full",
  FINANCE: "finance",
} as const;

export type CatRunStarted = {
  status: "started";
  type:
    | "spatialkat"
    | "rms_elt"
    | "rms_22"
    | "icm_ylt"
    | "rater"
    | "eqbd_rater";
  request_id: string;
  action: "reprice" | "activate" | "unchanged" | "full";
};

export type action = "reprice" | "activate" | "unchanged" | "full";

export const action = {
  REPRICE: "reprice",
  ACTIVATE: "activate",
  UNCHANGED: "unchanged",
  FULL: "full",
} as const;

export type CatRunStatus = {
  type:
    | "spatialkat"
    | "rms_elt"
    | "rms_22"
    | "icm_ylt"
    | "rater"
    | "eqbd_rater";
  request_id: string;
  status: IntegrationStatusResult | null;
};

/**
 * Aggregate + location results for one modeling source.
 */
export type CatSourceData = {
  aggregate?: CatAggregateData | null;
  top_locations_by_peril?: {
    [key: string]: Array<CatTopLocation>;
  };
  model_profiles?: Array<PerilModelProfile>;
};

export type CatStatusRequest = {
  runs: Array<CatRunRef>;
};

export type CatStatusResult = {
  runs: Array<CatRunStatus>;
};

export type CatTopLocation = {
  aal: number;
  tiv_pct?: number | null;
  aal_pct?: number | null;
  address?: LocationAddress | null;
  is_foreign_exposure?: boolean;
};

export type Chart = {
  title: string;
  data: Array<DataPoint>;
};

export type ChartGroup = {
  title: string;
  charts: Array<Chart>;
};

export type ClassifyStagedAttachmentRequest = {
  report_id: string;
  staging_id: string;
  attachment_name: string;
};

export type ClassifyStagedAttachmentResponse = {
  classification:
    | "sov_spreadsheet"
    | "acord_app"
    | "loss_run"
    | "none"
    | "screenshot"
    | "rater"
    | "loss_control_report"
    | "policy_document"
    | "manuscript_form"
    | "final_manuscript_form"
    | "specs_sheet"
    | "record_keeping"
    | "luminor_quote"
    | "luminor_tria"
    | "luminor_binder"
    | "luminor_policy"
    | "lead_quote"
    | "lead_binder"
    | "lead_policy"
    | "bound_sov"
    | "sl_letter"
    | "endorsement_file"
    | "endorsement_sov"
    | "engineering_report"
    | "rate_matrix"
    | "form_review";
};

export type classification =
  | "sov_spreadsheet"
  | "acord_app"
  | "loss_run"
  | "none"
  | "screenshot"
  | "rater"
  | "loss_control_report"
  | "policy_document"
  | "manuscript_form"
  | "final_manuscript_form"
  | "specs_sheet"
  | "record_keeping"
  | "luminor_quote"
  | "luminor_tria"
  | "luminor_binder"
  | "luminor_policy"
  | "lead_quote"
  | "lead_binder"
  | "lead_policy"
  | "bound_sov"
  | "sl_letter"
  | "endorsement_file"
  | "endorsement_sov"
  | "engineering_report"
  | "rate_matrix"
  | "form_review";

export const classification = {
  SOV_SPREADSHEET: "sov_spreadsheet",
  ACORD_APP: "acord_app",
  LOSS_RUN: "loss_run",
  NONE: "none",
  SCREENSHOT: "screenshot",
  RATER: "rater",
  LOSS_CONTROL_REPORT: "loss_control_report",
  POLICY_DOCUMENT: "policy_document",
  MANUSCRIPT_FORM: "manuscript_form",
  FINAL_MANUSCRIPT_FORM: "final_manuscript_form",
  SPECS_SHEET: "specs_sheet",
  RECORD_KEEPING: "record_keeping",
  LUMINOR_QUOTE: "luminor_quote",
  LUMINOR_TRIA: "luminor_tria",
  LUMINOR_BINDER: "luminor_binder",
  LUMINOR_POLICY: "luminor_policy",
  LEAD_QUOTE: "lead_quote",
  LEAD_BINDER: "lead_binder",
  LEAD_POLICY: "lead_policy",
  BOUND_SOV: "bound_sov",
  SL_LETTER: "sl_letter",
  ENDORSEMENT_FILE: "endorsement_file",
  ENDORSEMENT_SOV: "endorsement_sov",
  ENGINEERING_REPORT: "engineering_report",
  RATE_MATRIX: "rate_matrix",
  FORM_REVIEW: "form_review",
} as const;

export type ClearanceCheck = {
  id:
    | "appetite"
    | "tiv_matches_target"
    | "addresses_reasonable"
    | "file_classifications"
    | "limits_and_deductibles"
    | "functions_execute";
  label: string;
  status: ClearanceCheckStatus;
  summary: string;
  details?: Array<string>;
};

export type id =
  | "appetite"
  | "tiv_matches_target"
  | "addresses_reasonable"
  | "file_classifications"
  | "limits_and_deductibles"
  | "functions_execute";

export const id = {
  APPETITE: "appetite",
  TIV_MATCHES_TARGET: "tiv_matches_target",
  ADDRESSES_REASONABLE: "addresses_reasonable",
  FILE_CLASSIFICATIONS: "file_classifications",
  LIMITS_AND_DEDUCTIBLES: "limits_and_deductibles",
  FUNCTIONS_EXECUTE: "functions_execute",
} as const;

export type ClearanceCheckStatus = "pass" | "fail" | "unknown";

export const ClearanceCheckStatus = {
  PASS: "pass",
  FAIL: "fail",
  UNKNOWN: "unknown",
} as const;

export type ClearanceEmailRecipientsRequest = {
  report_id: string;
};

export type ClearanceEmailRecipientsResponse = {
  recipients: Array<string>;
  locked_emails: Array<string>;
};

/**
 * Outcome of one general clearance review.
 */
export type ClearanceReview = {
  outcome: "clear" | "needs_review" | "skipped";
  skip_reason: string | null;
  checks: Array<ClearanceCheck>;
  finished_at: string;
};

export type outcome = "clear" | "needs_review" | "skipped";

export const outcome = {
  CLEAR: "clear",
  NEEDS_REVIEW: "needs_review",
  SKIPPED: "skipped",
} as const;

export type Comment = {
  message: string;
  created_at_ts: string;
  last_updated_ts: string;
  user_id: string;
  id: string;
  deleted?: boolean;
  target_type?: CommentTargetType | null;
  target_key?: string | null;
};

/**
 * What a comment is attached to within a report.
 *
 * A comment with no ``target_type`` (None) is report-level — the original
 * behavior. ``LOSS_YEAR`` scopes a comment to a single policy year of the
 * loss run; its ``target_key`` is the normalized ``"YYYY-YYYY"`` policy-year
 * string, which is stable across reprocess (unlike individual claims).
 * ``ATTACHMENT`` scopes a comment to one attachment; its ``target_key`` is the
 * attachment name.
 */
export type CommentTargetType = "loss_year" | "attachment";

/**
 * What a comment is attached to within a report.
 *
 * A comment with no ``target_type`` (None) is report-level — the original
 * behavior. ``LOSS_YEAR`` scopes a comment to a single policy year of the
 * loss run; its ``target_key`` is the normalized ``"YYYY-YYYY"`` policy-year
 * string, which is stable across reprocess (unlike individual claims).
 * ``ATTACHMENT`` scopes a comment to one attachment; its ``target_key`` is the
 * attachment name.
 */
export const CommentTargetType = {
  LOSS_YEAR: "loss_year",
  ATTACHMENT: "attachment",
} as const;

export type Comments = {
  all_comments: Array<Comment>;
};

export type CommercialReport = {
  report_id: string;
  naics_info: NaicsInfo;
  sovs: Array<Sov>;
  loss_run: LossRunInfo;
  company_info: CompanyInfo;
  layer_info: Array<Layer>;
  ofac_result?: OfacResult | null;
  policy_info?: PolicyInfo | null;
  policy_questions?: {
    [key: string]: Array<PolicyQuestion>;
  } | null;
  additional_files_used?: Array<string> | null;
  loss_control_reports?: Array<LossControlReport> | null;
  attachment_info?: AttachmentInfo | null;
  parsed_sources?: {
    [key: string]: Array<string>;
  } | null;
  referral_text?: ReferralText | null;
  policy_questions_for_policy_documents?: PolicyQuestionsForDocuments | null;
  limit_and_sublimit_info?: LimitAndSublimitInfo | null;
  limit_info?: AdvancedLimitInfo | null;
  sublimit_info?: SublimitInfo | null;
  deductible_info?: AdvancedDeductibleInfo | null;
  submission_recipients?: Array<string> | null;
  policy_fly_url?: string | null;
  policy_fly_info?: PolicyFlyInfo | null;
  email_threads?: {
    [key: string]: string;
  } | null;
};

export type CommericalReportMetadata = {
  report_id: string;
  lob: string;
  naics_info: NaicsInfo;
  company_info: CompanyInfo;
  precomputed_metadata: PrecomputedMetadata;
};

export type CompanyInfo = {
  private_computed_values: RawCompanyInfo;
  private_original_values: {
    [key: string]: Array<string> | string | null;
  };
  private_overrides: {
    [key: string]: string | Array<string> | number | null;
  };
  private_flags: {
    [key: string]: Array<
      | "low_confidence"
      | "medium_confidence"
      | "modified_by_sanitization"
      | "default_used"
      | "could_not_sanitize"
      | "po_box_insured_address"
      | YearIncorporatedDerivedFromSearch
      | MultipleCompanyNamesFlag
    >;
  };
  additional_interests?: Array<OverrideableListElementRawAdditionalInterestLiteralTodo> | null;
};

export type CompetitorPricing = {
  carrier_name: string;
  price: number | null;
  notes: string | null;
};

export type ComputedLossRunByYear = {
  policy_year: string;
  description_of_losses: string;
  calc_mode: "historical" | "as_if" | null;
  source_details: SourceDetails;
  source: "computed_from_claims";
  claims: Array<ParsedLoss | UserGeneratedLoss>;
};

export type ConstructionMix = {
  construction_description: string;
  tiv: number;
  pct_tiv: number;
};

/**
 * Contact type enum for categorizing contacts.
 */
export type ContactType = "broker";

/**
 * Contact type enum for categorizing contacts.
 */
export const ContactType = {
  BROKER: "broker",
} as const;

/**
 * A bare position, for a ring or a path that carries nothing else.
 */
export type Coordinate = {
  lat: number;
  lon: number;
};

export type CoverageComputedFromColumnSum = {
  flag_id: "coverage_computed_from_column_sum";
  columns: Array<string>;
};

export type CreateBorLetterRequest = {
  cleared_report_id: string;
  incoming_report_id: string;
  effective_date?: string | null;
  notes?: string | null;
};

/**
 * Request body for creating a contact.
 */
export type CreateContactRequest = {
  email: string;
  contact_type: ContactType;
  full_name: string | null;
  organization_name?: string | null;
  office_phone?: string | null;
  cell_phone?: string | null;
  role?: string | null;
};

export type CreateContactResponse = {
  contact: ReportContact;
};

export type DataPoint = {
  label: string;
  value: number;
  color_code:
    | "green"
    | "yellow"
    | "red"
    | "blue"
    | "purple"
    | "teal"
    | "gray"
    | "orange"
    | "pink"
    | "indigo"
    | "cyan"
    | "lime";
  type: "percentage" | "monetary" | "count";
};

export type color_code =
  | "green"
  | "yellow"
  | "red"
  | "blue"
  | "purple"
  | "teal"
  | "gray"
  | "orange"
  | "pink"
  | "indigo"
  | "cyan"
  | "lime";

export const color_code = {
  GREEN: "green",
  YELLOW: "yellow",
  RED: "red",
  BLUE: "blue",
  PURPLE: "purple",
  TEAL: "teal",
  GRAY: "gray",
  ORANGE: "orange",
  PINK: "pink",
  INDIGO: "indigo",
  CYAN: "cyan",
  LIME: "lime",
} as const;

export type type2 = "percentage" | "monetary" | "count";

export const type2 = {
  PERCENTAGE: "percentage",
  MONETARY: "monetary",
  COUNT: "count",
} as const;

export type DeductibleEntry = {
  name: string;
  description: string;
};

export type DeductibleSummary = {
  kind: "site" | "per_unit" | "occurrence" | "aggregate";
  value: number;
  zone: string;
};

export type kind = "site" | "per_unit" | "occurrence" | "aggregate";

export const kind = {
  SITE: "site",
  PER_UNIT: "per_unit",
  OCCURRENCE: "occurrence",
  AGGREGATE: "aggregate",
} as const;

export type DeductibleSummaryRequest = {
  report_id: string;
  address_ids: Array<string>;
};

export type DeductibleSummaryResponse = {
  addresses: {
    [key: string]: {
      [key: string]: DeductibleSummary;
    };
  };
};

export type DeleteAdditionalFilesRequest = {
  files: Array<string>;
};

export type DeleteAttachmentRequest = {
  report_id: string;
  filename: string;
};

export type DeleteCommentRequest = {
  report_id: string;
  comment_id: string;
  target_type: CommentTargetType | null;
};

export type DeleteCommentResponse = {
  comments: Comments;
};

export type DeleteEmailThreadRequest = {
  conversation_id: string;
};

export type DeleteEmailThreadResponse = {
  deleted_files: Array<string>;
};

export type DeleteEndorsementResponse = {
  deleted_documents: Array<string>;
};

export type DeleteReportRequest = {
  report_id: string;
};

export type DeleteReportResponse = unknown;

export type DeleteSavedFilterRequest = {
  id: string;
};

export type DeleteTemplate = {
  template_id: string;
};

export type DownloadAdditionalFileRequest = {
  report_id: string;
  file: string;
};

export type DownloadLossRunRequest = {
  loss_run_years: Array<UserGeneratedLossRunByYear>;
};

export type DownloadReportFileRequest = {
  report_id: string;
  filename: string;
};

export type DownloadReportHistoryRequest = {
  report_ids: Array<string>;
  download_type: "account" | "simple_location" | "complex_location";
  export_format?: "xlsx" | "csv";
};

export type download_type = "account" | "simple_location" | "complex_location";

export const download_type = {
  ACCOUNT: "account",
  SIMPLE_LOCATION: "simple_location",
  COMPLEX_LOCATION: "complex_location",
} as const;

export type export_format = "xlsx" | "csv";

export const export_format = {
  XLSX: "xlsx",
  CSV: "csv",
} as const;

export type DownloadTemplateRequest = {
  template_id: string;
};

export type DownloadThreadAttachmentRequest = {
  report_id: string;
  thread_url: string;
  message_id: string;
  attachment_id: string;
  filename: string;
};

export type DraftResponse = {
  draft_url: string;
};

export type EpRow = {
  peril: string;
  perspective: string;
  metric_type: string;
  return_period_values?: {
    [key: string]: number;
  };
};

export type EditCommentRequest = {
  report_id: string;
  comment_id: string;
  message: string;
  target_type: CommentTargetType | null;
};

export type EditCommentResponse = {
  comments: Comments;
};

export type EmailThread = {
  thread_url: string;
  messages: Array<EmailThreadMessage>;
};

export type EmailThreadAttachment = {
  filename: string;
  mime_type: string;
  size: number;
  attachment_id: string;
  message_id: string;
};

export type EmailThreadMessage = {
  from?: string;
  to?: string;
  cc?: string;
  subject?: string;
  date?: string;
  body_text?: string;
  body_html?: string;
  attachments?: Array<EmailThreadAttachment>;
};

export type EmailThreadsResponse = {
  threads: Array<EmailThread>;
};

export type EmlPreviewRequest = {
  report_id: string;
};

export type EmlPreviewResponse = {
  subject: string;
  eml: string;
};

/**
 * A policy's premium position: the live base (the resolved layer premium,
 * i.e. the Layers tab number) plus the sums over verified ledger entries.
 * ``owed_premium`` = base + prorated deltas (what is billed for the term);
 * ``term_premium`` = base + full-term deltas (the in-force premium for the
 * whole term and the account-rate numerator). ``base_premium`` is None when the layer has
 * neither a resolved premium nor a target — the sums are deltas-only then.
 */
export type EndorsementLedgerTotals = {
  policy_application_id: string;
  base_premium: number | null;
  owed_premium: number;
  term_premium: number;
};

export type EndorsementPremiumBasis = "account_rate" | "no_change" | "custom";

export const EndorsementPremiumBasis = {
  ACCOUNT_RATE: "account_rate",
  NO_CHANGE: "no_change",
  CUSTOM: "custom",
} as const;

/**
 * One endorsement's premium delta, negative for a return premium.
 *
 * ``term_premium_delta`` is the full-term amount (annual only when the policy
 * term is twelve months) and ``prorated_premium_delta`` is what is charged
 * for the remainder of the policy. Both are stored (not derived on read): the ledger must re-read years
 * later without re-running date math against records that may have changed.
 * They are linked by ``proration_factor`` = days from the endorsement's
 * effective date to the policy expiration / days in the term.
 */
export type EndorsementPremiumChange = {
  term_premium_delta: number;
  prorated_premium_delta: number;
  rate_used: number | null;
  proration_factor: number;
  edited: boolean;
};

export type EndorsementRequest = {
  endorsement_id: string;
};

export type EndorsementSovDiffResponse = {
  added: Array<SovDiffLocation>;
  removed: Array<SovDiffLocation>;
  revalued: Array<SovDiffRevaluedLocation>;
  unchanged_count: number;
  baseline_description: string;
};

export type EndorsementStatus =
  | "draft"
  | "generating"
  | "needs_review"
  | "verified"
  | "voided";

export const EndorsementStatus = {
  DRAFT: "draft",
  GENERATING: "generating",
  NEEDS_REVIEW: "needs_review",
  VERIFIED: "verified",
  VOIDED: "voided",
} as const;

/**
 * API shape for one ledger entry. ``new_sov`` stays server-side (it can be
 * large); the view carries whether a generated schedule exists.
 */
export type EndorsementView = {
  id: string;
  report_id: string;
  policy_application_id: string;
  endorsement_number: number;
  status: EndorsementStatus;
  effective_date: string;
  description: string;
  tiv_target: number | null;
  premium_basis: EndorsementPremiumBasis;
  premium_change: EndorsementPremiumChange | null;
  tiv_before: number | null;
  tiv_achieved: number | null;
  generation_job_id: string | null;
  generation_summary: string | null;
  generation_error: string | null;
  document_ids: Array<string>;
  has_generated_sov: boolean;
  created_by: string;
  verified_by: string | null;
  created_at_ts: string;
  modified_at_ts: string;
};

export type EqbdAccountResult = {
  pd_total: number;
  bi_total: number;
  pd_options_total: number;
  bi_options_total: number;
  utility_total: number;
  other_options_total: number;
  structural_total: number;
  locations_subtotal: number;
  non_owned_premium: number;
  grand_total: number;
  min_premium_applied: boolean;
  refer_count: number;
  warning_counts?: {
    [key: string]: number;
  };
  by_layer?: {
    [key: string]: number;
  };
};

export type EqbdBiComponents = {
  base_rate: number;
  coverage_mod: number;
  limit_factor: number;
  deductible_factor: number;
  exposure_factor: number;
  risk_factor: number;
  coinsurance_factor: number;
  location_discount: number;
  final_rate: number;
  annual_value: number;
  credit_factor: number;
  state_multiplier: number;
  uw_modifier: number;
};

export type EqbdLocationResult = {
  address_id: string;
  premises_number: string;
  building_name: string | null;
  state: string;
  sic_code: string;
  occupancy_code: string | null;
  pd_premium: number;
  bi_premium: number;
  pd_options_premium: number;
  bi_options_premium: number;
  utility_premium: number;
  other_options_premium: number;
  structural_premium: number;
  total_premium: number;
  pd_components: EqbdPdComponents | null;
  bi_components: EqbdBiComponents | null;
  refer: boolean;
  warnings?: Array<string>;
};

/**
 * Property-damage rate breakdown (each is a multiplier unless noted).
 */
export type EqbdPdComponents = {
  base_rate: number;
  coverage_mod: number;
  limit_factor: number;
  deductible_factor: number;
  risk_factor: number;
  location_discount: number;
  acv_adjustment: number;
  final_rate: number;
  exposure: number;
  credit_factor: number;
  state_multiplier: number;
  uw_modifier: number;
};

export type EqbdRaterResultsResponse = {
  locations: Array<EqbdLocationResult>;
  account: EqbdAccountResult | null;
};

export type EqbdRaterRunSpec = {
  type: "eqbd_rater";
  enrichment_source: "nearmap" | "cape" | null;
};

export type EstimateEndorsementPremiumRequest = {
  report_id: string;
  policy_application_id: string;
  effective_date: string;
  tiv_target: number;
};

/**
 * A reorder of the excerpt rows within one peril group of a limit/deductible.
 *
 * The target ``AdvancedLimit``/``AdvancedDeductible`` is identified by
 * (``group``, ``filename``, ``peril_type``): ``group="manual"`` targets the
 * manual entry (``filename`` must be None); ``group="extracted"`` targets a
 * source file's entry. ``excerpt_keys`` maps excerpt name -> new fractional
 * key, carrying only the rows whose key changed (one for a single move, or the
 * whole group when seeding). Keys for excerpts not listed are left untouched.
 */
export type ExcerptSortUpdate = {
  group: "manual" | "extracted";
  filename: string | null;
  peril_type:
    | "aop"
    | "earthquake"
    | "earth_movement"
    | "named_storm"
    | "scs"
    | "flood"
    | "wildfire"
    | "equipment_breakdown"
    | "wind_and_hail";
  excerpt_keys: {
    [key: string]: string;
  };
};

export type group = "manual" | "extracted";

export const group = {
  MANUAL: "manual",
  EXTRACTED: "extracted",
} as const;

/**
 * Exposure (E) from COPE — external hazard exposure beyond peril scores.
 */
export type ExposureData = {
  fema_flood_zone?: string | null;
  wind_pool_score?: string | null;
};

export type ExposureMapLocation = {
  address_id: string;
  lat: number;
  lon: number;
  tiv: number | null;
  status:
    | "pre_underwriting_review"
    | "blocked"
    | "not_cleared_prior_submission"
    | "not_cleared_incomplete_submission"
    | "not_cleared_out_of_appetite"
    | "not_cleared_effective_date_too_far"
    | "not_cleared_unapproved_broker"
    | "not_cleared_low_tiv"
    | "not_cleared_foreign_exposure"
    | "cleared"
    | "pending"
    | "modeling_complete"
    | "referral_sent"
    | "flagged_for_review"
    | "indicated"
    | "quoted"
    | "declined"
    | "closed"
    | "closed_bor"
    | "not_reviewed"
    | "lost"
    | "quoted_not_taken_up"
    | "bound"
    | "issued";
  report_id: string;
  named_insured: string | null;
  city: string | null;
  county: string | null;
  state: string;
};

export type status =
  | "pre_underwriting_review"
  | "blocked"
  | "not_cleared_prior_submission"
  | "not_cleared_incomplete_submission"
  | "not_cleared_out_of_appetite"
  | "not_cleared_effective_date_too_far"
  | "not_cleared_unapproved_broker"
  | "not_cleared_low_tiv"
  | "not_cleared_foreign_exposure"
  | "cleared"
  | "pending"
  | "modeling_complete"
  | "referral_sent"
  | "flagged_for_review"
  | "indicated"
  | "quoted"
  | "declined"
  | "closed"
  | "closed_bor"
  | "not_reviewed"
  | "lost"
  | "quoted_not_taken_up"
  | "bound"
  | "issued";

export const status = {
  PRE_UNDERWRITING_REVIEW: "pre_underwriting_review",
  BLOCKED: "blocked",
  NOT_CLEARED_PRIOR_SUBMISSION: "not_cleared_prior_submission",
  NOT_CLEARED_INCOMPLETE_SUBMISSION: "not_cleared_incomplete_submission",
  NOT_CLEARED_OUT_OF_APPETITE: "not_cleared_out_of_appetite",
  NOT_CLEARED_EFFECTIVE_DATE_TOO_FAR: "not_cleared_effective_date_too_far",
  NOT_CLEARED_UNAPPROVED_BROKER: "not_cleared_unapproved_broker",
  NOT_CLEARED_LOW_TIV: "not_cleared_low_tiv",
  NOT_CLEARED_FOREIGN_EXPOSURE: "not_cleared_foreign_exposure",
  CLEARED: "cleared",
  PENDING: "pending",
  MODELING_COMPLETE: "modeling_complete",
  REFERRAL_SENT: "referral_sent",
  FLAGGED_FOR_REVIEW: "flagged_for_review",
  INDICATED: "indicated",
  QUOTED: "quoted",
  DECLINED: "declined",
  CLOSED: "closed",
  CLOSED_BOR: "closed_bor",
  NOT_REVIEWED: "not_reviewed",
  LOST: "lost",
  QUOTED_NOT_TAKEN_UP: "quoted_not_taken_up",
  BOUND: "bound",
  ISSUED: "issued",
} as const;

export type ExposureMapLocations = {
  locations: Array<ExposureMapLocation>;
  total_matching: number;
  truncated: boolean;
};

export type ExposureMapRequest = {
  lob: string;
  statuses: Array<
    | "pre_underwriting_review"
    | "blocked"
    | "not_cleared_prior_submission"
    | "not_cleared_incomplete_submission"
    | "not_cleared_out_of_appetite"
    | "not_cleared_effective_date_too_far"
    | "not_cleared_unapproved_broker"
    | "not_cleared_low_tiv"
    | "not_cleared_foreign_exposure"
    | "cleared"
    | "pending"
    | "modeling_complete"
    | "referral_sent"
    | "flagged_for_review"
    | "indicated"
    | "quoted"
    | "declined"
    | "closed"
    | "closed_bor"
    | "not_reviewed"
    | "lost"
    | "quoted_not_taken_up"
    | "bound"
    | "issued"
  > | null;
  state: string | null;
  county: string | null;
  gate:
    | "Southeast Florida"
    | "Southwest Florida"
    | "Central Florida"
    | "Northeast Florida"
    | "Panhandle Florida"
    | "Georgia / South Carolina"
    | "North Carolina"
    | "Texas incl. Harris County"
    | "Louisiana"
    | "Mississippi"
    | "Alabama"
    | "Mid-Atlantic (VA/MD/DE)"
    | "Northeast (NY/NJ/CT/RI/MA/NH/ME)"
    | "CA High Hazard"
    | "Pacific NW"
    | "Intermountain West"
    | "New Madrid"
    | "Northeast"
    | "Alabama EQ"
    | "Alaska EQ"
    | "District of Columbia EQ"
    | "Florida EQ"
    | "Georgia EQ"
    | "Guam EQ"
    | "Hawaii EQ"
    | "Iowa EQ"
    | "Kansas EQ"
    | "Louisiana EQ"
    | "Michigan EQ"
    | "Minnesota EQ"
    | "Mississippi EQ"
    | "Nebraska EQ"
    | "New Hampshire EQ"
    | "North Carolina EQ"
    | "North Dakota EQ"
    | "Ohio EQ"
    | "Oklahoma EQ"
    | "Puerto Rico EQ"
    | "South Carolina EQ"
    | "South Dakota EQ"
    | "Texas EQ"
    | "US Virgin Islands EQ"
    | "Wisconsin EQ"
    | null;
};

export type ExposureMapSummary = {
  total_locations: number;
  total_tiv: number;
  policy_count: number;
  by_state: Array<PlaceExposure>;
  by_county: Array<PlaceExposure>;
  by_gate: Array<GateRollup>;
  top_policies: Array<PolicyExposure>;
  status_breakdown: Array<StatusExposure>;
  locations_without_coordinates: number;
  last_refreshed_at: string | null;
  reachable_line: number;
  blocked_line: number;
  policies_without_layers: number;
};

export type ExtractStagedAttachmentsRequest = {
  report_id: string;
  selections: Array<StagedAttachmentSelection>;
};

export type ExtractStagedAttachmentsResponse = {
  added: Array<string>;
  rerun_enqueued: boolean;
};

export type FieldSource = "inherited" | "overridden" | "added" | "removed";

export const FieldSource = {
  INHERITED: "inherited",
  OVERRIDDEN: "overridden",
  ADDED: "added",
  REMOVED: "removed",
} as const;

export type FinancialModelAudit = {
  schema_version: number;
  perils: Array<FinancialModelAuditPeril>;
};

/**
 * Averaged loss waterfall for one limit zone within a peril+layer.
 *
 * Holds exactly (in AAL terms):
 * ground_up_aal == deductible_savings_aal
 * + aggregate_deductible_savings_aal
 * + retained_below_attachment_aal
 * + limit_savings_aal
 * + aggregate_limit_savings_aal
 * + gross_aal
 */
export type FinancialModelAuditGroup = {
  limit_zone: string;
  ground_up_aal: number;
  deductible_savings_aal: number;
  aggregate_deductible_savings_aal: number;
  retained_below_attachment_aal: number;
  limit_savings_aal: number;
  aggregate_limit_savings_aal: number;
  gross_aal: number;
};

export type FinancialModelAuditLayer = {
  layer_id: string;
  groups: Array<FinancialModelAuditGroup>;
};

/**
 * One peril's audit. ``ground_up_aal`` / ``deductible_savings_aal`` are
 * layer-invariant (computed before any layer clipping) so they repeat across
 * this peril's layers; only the limit/gross figures differ per layer.
 *
 * The deductible/limit function strings are the resolved functions actually
 * used for this peril, captured so a reader can tie the numbers back to the
 * terms that produced them.
 */
export type FinancialModelAuditPeril = {
  peril:
    | "PWH"
    | "PSH"
    | "PWH_PSH"
    | "PES"
    | "PFL"
    | "PFL_PSH"
    | "PWB"
    | "PWW"
    | "PWT";
  total_periods: number;
  deductible_description: string | null;
  deductible_function_str: string | null;
  limit_description: string | null;
  limit_function_str: string | null;
  layers: Array<FinancialModelAuditLayer>;
};

export type peril =
  | "PWH"
  | "PSH"
  | "PWH_PSH"
  | "PES"
  | "PFL"
  | "PFL_PSH"
  | "PWB"
  | "PWW"
  | "PWT";

export const peril = {
  PWH: "PWH",
  PSH: "PSH",
  PWH_PSH: "PWH_PSH",
  PES: "PES",
  PFL: "PFL",
  PFL_PSH: "PFL_PSH",
  PWB: "PWB",
  PWW: "PWW",
  PWT: "PWT",
} as const;

export type FinancialModelAuditRequest = {
  report_id: string;
  source: "air" | "rms" | "spatialkat" | "rms_22" | "rms_22_air_50_50";
  batch_id: string | null;
};

export type source =
  | "air"
  | "rms"
  | "spatialkat"
  | "rms_22"
  | "rms_22_air_50_50";

export const source = {
  AIR: "air",
  RMS: "rms",
  SPATIALKAT: "spatialkat",
  RMS_22: "rms_22",
  RMS_22_AIR_50_50: "rms_22_air_50_50",
} as const;

/**
 * Outcome of one ``fix_sov`` sandbox run.
 */
export type FixSovRunSummary = {
  completed: boolean;
  end_reason: string | null;
  reupload_invoked: boolean;
  reupload_succeeded?: boolean | null;
  summary: string | null;
  tool_errors: Array<string>;
  cost_usd: number | null;
  num_turns: number | null;
  artifact_path: string | null;
  error: string | null;
  finished_at: string;
};

/**
 * Display grouping for a resolved policy form.
 */
export type FormGroup =
  | "mandatory"
  | "on_by_default"
  | "off_by_default"
  | "tria";

/**
 * Display grouping for a resolved policy form.
 */
export const FormGroup = {
  MANDATORY: "mandatory",
  ON_BY_DEFAULT: "on_by_default",
  OFF_BY_DEFAULT: "off_by_default",
  TRIA: "tria",
} as const;

/**
 * A default exclusion and its alternate, both present in ``forms``.
 */
export type FormPairView = {
  default_form_id: string;
  alternate_form_id: string;
  topic: string;
  applicable_sublimits: Array<string>;
};

/**
 * A single form within an application's materialized selections.
 *
 * ``form_id`` is a forms_catalog.FormId value, stored as a string so a snapshot
 * survives catalog edits. form_number/form_name/group/note/default_enabled are
 * snapshotted from the catalog at generation and never re-derived. ``enabled``
 * is the current state; it differs from ``default_enabled`` exactly when an
 * underwriter has changed this form. Locked forms keep
 * ``enabled == default_enabled``.
 */
export type FormSelection = {
  form_id: string;
  form_number: string;
  form_name: string;
  group: FormGroup;
  locked: boolean;
  note: string | null;
  default_enabled: boolean;
  enabled: boolean;
};

export type FormattedText = {
  text: string;
};

export type FreshnessRequest = {
  report_id: string | null;
  client_modified_at_ts: string | null;
  client_comments_modified_at_ts: string | null;
};

export type FreshnessResponse = {
  needs_refresh: boolean;
  comments_need_refresh: boolean;
  modified_at_ts: string | null;
  comments_modified_at_ts: string | null;
};

export type GateBreakpoint = {
  percent: number;
  label: string;
  color: string;
};

export type GateExposure = {
  gate: string;
  peril_family: "wind" | "eq";
  gate_tiv: number;
  gate_cap: number | null;
  gross_exposure: number;
  net_exposure: number;
  location_count: number;
  reasoning: string;
};

export type peril_family = "wind" | "eq";

export const peril_family = {
  WIND: "wind",
  EQ: "eq",
} as const;

export type GateExposureRequest = {
  report_id: string;
  layer_id: string;
};

export type GateExposureResponse = {
  layer: LayerGateExposureResult;
};

/**
 * What sits in one catastrophe accumulation gate.
 *
 * Not ``cat.gate_exposure.GateExposure``, which answers a harder question for
 * one layer: capped by per-occurrence limits, grossed up, netted down.
 */
export type GateRollup = {
  gate:
    | "Southeast Florida"
    | "Southwest Florida"
    | "Central Florida"
    | "Northeast Florida"
    | "Panhandle Florida"
    | "Georgia / South Carolina"
    | "North Carolina"
    | "Texas incl. Harris County"
    | "Louisiana"
    | "Mississippi"
    | "Alabama"
    | "Mid-Atlantic (VA/MD/DE)"
    | "Northeast (NY/NJ/CT/RI/MA/NH/ME)"
    | "CA High Hazard"
    | "Pacific NW"
    | "Intermountain West"
    | "New Madrid"
    | "Northeast"
    | "Alabama EQ"
    | "Alaska EQ"
    | "District of Columbia EQ"
    | "Florida EQ"
    | "Georgia EQ"
    | "Guam EQ"
    | "Hawaii EQ"
    | "Iowa EQ"
    | "Kansas EQ"
    | "Louisiana EQ"
    | "Michigan EQ"
    | "Minnesota EQ"
    | "Mississippi EQ"
    | "Nebraska EQ"
    | "New Hampshire EQ"
    | "North Carolina EQ"
    | "North Dakota EQ"
    | "Ohio EQ"
    | "Oklahoma EQ"
    | "Puerto Rico EQ"
    | "South Carolina EQ"
    | "South Dakota EQ"
    | "Texas EQ"
    | "US Virgin Islands EQ"
    | "Wisconsin EQ";
  peril_family: "wind" | "eq";
  locations: number;
  policies: number;
  tiv: number;
  reachable_line: number;
  blocked_line: number;
  policies_without_layers: number;
};

export type GateTrackingBar = {
  gate: string;
  peril_family: "wind" | "eq";
  is_per_state: boolean;
  tracking_base: number;
  bound_contribution: number;
  quoted_contribution: number;
  current_layer_contribution: number;
  current_layer_reasoning: string | null;
};

export type GateTrackingBaseRequest = {
  report_id: string;
  layer_id: string;
};

export type GateTrackingBaseResponse = {
  layer_id: string;
  wind_shown: boolean;
  eq_shown: boolean;
  breakpoints: Array<GateBreakpoint>;
  bars: Array<GateTrackingBar>;
};

export type GeneratePolicyFlyApplicationRequest = {
  report_id: string;
  perils_by_layer: {
    [key: string]: string;
  };
  additional_layer_id: string | null;
};

export type GeneratePolicyFlyApplicationResponse = {
  task_ids: Array<string>;
};

export type GeneratePolicyNumbersRequest = {
  report_id: string;
};

export type GenerateQuoteIndicationRequest = {
  report_id: string;
  layer_id: string;
  perils_insured: string;
  premium_range: string;
};

/**
 * `html_content` is the referral tab's editor HTML, sent as-is so the
 * draft matches any manual edits the underwriter has made.
 */
export type GenerateReferralDraftRequest = {
  report_id: string;
  html_content: string;
};

export type GenerateUnderwritingPdfRequest = {
  report_id: string;
  html_content: string;
};

export type GeneratedPolicyNumbers = {
  numbers: {
    [key: string]: string | null;
  };
};

export type GeographicEnrichmentData = {
  flood_zone?: string | null;
  earthquake_zone?: string | null;
};

export type GetBorLetterRequest = {
  bor_id: string;
};

export type GetBorLettersByReportRequest = {
  report_id: string;
};

export type GetBrokerAnalyticsResponse = {
  chart_groups: Array<ChartGroup>;
};

export type GetReportActionLogRequest = {
  report_id?: string | null;
  email_message_id?: string | null;
};

export type GetReportActionLogResponse = {
  company_name?: string | null;
  email_subject?: string | null;
  actions: Array<ReportActionLogEntryResponse>;
};

export type GoogleGroupStats = {
  google_group_email: string;
  ytd_year: number;
  submitted_count: number;
  quoted_count: number;
  bound_count: number;
  not_cleared_count: number;
  avg_tiv: number;
  quoted_premium_ytd: number;
  bound_premium_ytd: number;
  avg_bound_commission_ytd: number | null;
};

export type GoogleGroupStatsRequest = {
  google_group_email: string;
};

export type HttpValidationError = {
  detail?: Array<ValidationError>;
};

/**
 * Normalized subset of HazardHub response fields mapped to address fields.
 */
export type HazardHubData = {
  year_built?: string | null;
  number_of_stories?: string | null;
  number_of_buildings?: number | null;
  building_condition?: string | null;
  basement?: string | null;
  square_footage?: number | null;
  replacement_cost?: number | null;
  construction_quality?: string | null;
  construction_type?: string | null;
  primary_exterior?: string | null;
  primary_roof_covering?: string | null;
  roof_configuration?: string | null;
  distance_to_coast_km?: number | null;
  assessment?: AssessmentData;
  occupancy?: OccupancyData;
  protection?: ProtectionData;
  exposure?: ExposureData;
  peril_scores?: PerilScores;
  derived_modifiers?: {
    [key: string]: string;
  };
};

export type IcmJobProgress = {
  submitted: boolean;
  request_status: string | null;
  stage: IcmStage | null;
  location_count: number | null;
};

/**
 * Coarse ICM-side progress for a rating request, derived from which
 * pipeline timestamps the status response has populated.
 */
export type IcmStage = "submitted" | "cat_modeling" | "scoring" | "complete";

/**
 * Coarse ICM-side progress for a rating request, derived from which
 * pipeline timestamps the status response has populated.
 */
export const IcmStage = {
  SUBMITTED: "submitted",
  CAT_MODELING: "cat_modeling",
  SCORING: "scoring",
  COMPLETE: "complete",
} as const;

export type IntegrationDataRequest = {
  report_id: string;
  types: Array<
    | "rms"
    | "air"
    | "spatialkat"
    | "geographic_enrichment"
    | "hazard_hub"
    | "nearmap"
    | "nearmap_ai"
    | "cape"
    | "sindri"
  >;
  address_ids: Array<string>;
};

export type IntegrationDataResponse = {
  addresses: {
    [key: string]: AddressIntegrationData;
  };
};

export type IntegrationStatusResult = {
  is_complete: boolean;
  status: WorkStatus;
  error_detail?: string | null;
};

export type LatLon = {
  lat: number;
  lon: number;
};

export type Layer = {
  layer_id?: string;
  starting_point: number;
  range: number;
  target: number | null;
  rated: number | null;
  participation?: number | null;
  layer_source?: "extracted" | "default" | "manual" | null;
  is_enabled?: boolean | null;
  peril_overrides?: {
    [key: string]: PerilOverride;
  } | null;
  competitor_pricing?: Array<CompetitorPricing>;
  included_perils_override?: Array<
    | "aop"
    | "earthquake"
    | "earth_movement"
    | "named_storm"
    | "scs"
    | "flood"
    | "wildfire"
    | "equipment_breakdown"
    | "wind_and_hail"
  > | null;
  form_selection_override?: "ISO" | "Co-Participation" | "Manuscript" | null;
  broker_commission_percentage_override?: number | null;
};

export type LayerGateExposureResult = {
  layer_id: string;
  starting_point: number;
  range: number;
  participation: number;
  wind_shown: boolean;
  eq_shown: boolean;
  gates: Array<GateExposure>;
};

/**
 * On-demand per-layer PMI at a caller-chosen participation share,
 * computed against the current in-force book (not the persisted job-time
 * snapshot).
 */
export type LayerPmiResponse = {
  layer_id: string;
  share: number;
  rows?: Array<LayerPmiRow>;
  portfolio_report_count: number;
  as_of: string;
  subject_vectors_from_shadow_run: boolean;
};

export type LayerPmiRow = {
  peril: string;
  pmi_at_share?: {
    [key: string]: number;
  };
};

export type LayerPremiumBreakdown = {
  layer_id: string;
  peril_types: {
    [key: string]: PerilTypeBreakdown;
  };
  totals: {
    [key: string]: LayerTotals;
  };
  commission_adjustment_factor?: number;
  broker_commission_percentage?: number | null;
};

export type LayerSummary = {
  starting_point: number;
  range: number;
  participation: number | null;
};

/**
 * Precomputed totals for one source strategy on one layer.
 */
export type LayerTotals = {
  layer_total: number;
  share_total: number;
  has_missing_data: boolean;
};

export type Limit = {
  limit_name:
    | "earthquake"
    | "earthmovement"
    | "flood"
    | "flood_high_hazard_sfha"
    | "windstorm_named_windstorm_hurricane"
    | "building_ordinance_coverage_a"
    | "building_ordinance_coverage_b"
    | "building_ordinance_coverage_c"
    | "building_ordinance_coverage_d"
    | "civil_or_military_authority"
    | "ingress_egress"
    | "debris_removal"
    | "service_interruption"
    | "contingent_time_element"
    | "evacuation_expense"
    | "electronic_data_and_media"
    | "expediting_expenses"
    | "fines_and_penalties"
    | "property_in_the_open"
    | "pairs_and_sets"
    | "trees_shrubs_and_plants"
    | "off_premises_storage"
    | "outdoor_property"
    | "land_improvements"
    | "spoilage_perishable_foods"
    | "piers_docks_wharfs_seawalls"
    | "property_in_course_of_construction"
    | "protection_and_preservation_of_property"
    | "professional_fees"
    | "animals_held_for_research"
    | "research_and_development"
    | "fire_brigade_and_extinguishing_expenses"
    | "soft_costs"
    | "vehicles_licensed_for_highway_use"
    | "waterway_access"
    | "extra_expense"
    | "fine_arts"
    | "land_water_contamination_and_cleanup_expense"
    | "decontamination_costs"
    | "leasehold_interest"
    | "loss_adjustment_expenses"
    | "personal_property_of_officers_and_employees"
    | "employee_tools"
    | "transit"
    | "accounts_receivable"
    | "mold_and_fungus"
    | "errors_and_omissions"
    | "miscellaneous_unnamed_locations"
    | "newly_acquired_automatic_coverage"
    | "extended_period_of_indemnity"
    | "backup_of_sewers_and_drains_sump_overflow"
    | "brands_and_labels"
    | "code_upgrade_upgrade_to_green"
    | "employee_personal_effects"
    | "exhibition_trade_show_property"
    | "freeze"
    | "leader_property"
    | "money_and_securities"
    | "off_premises_property_temporary_locations"
    | "refrigerated_property_temperature_change"
    | "sprinkler_leakage"
    | "strikes_riots_and_civil_commotion"
    | "valuable_papers_and_records"
    | "ordinary_payroll"
    | "impounded_water"
    | "land_and_water_cleanup_expense"
    | "limited_pollution"
    | "contractors_equipment"
    | "rental_value"
    | "royalties"
    | "cyber"
    | "asbestos"
    | "equipment_breakdown"
    | "consequential_loss"
    | "cancellation_of_bookings";
  limit_status: "included" | "excluded" | null;
  applicability:
    | "per_occurrence"
    | "per_occurrence_and_annual_aggregate"
    | null;
  amount: number | null;
  time_limit_days: number | null;
  mile_limitation: number | null;
  qualifying_period_hours: number | null;
  document_excerpt: string | null;
};

export type limit_name =
  | "earthquake"
  | "earthmovement"
  | "flood"
  | "flood_high_hazard_sfha"
  | "windstorm_named_windstorm_hurricane"
  | "building_ordinance_coverage_a"
  | "building_ordinance_coverage_b"
  | "building_ordinance_coverage_c"
  | "building_ordinance_coverage_d"
  | "civil_or_military_authority"
  | "ingress_egress"
  | "debris_removal"
  | "service_interruption"
  | "contingent_time_element"
  | "evacuation_expense"
  | "electronic_data_and_media"
  | "expediting_expenses"
  | "fines_and_penalties"
  | "property_in_the_open"
  | "pairs_and_sets"
  | "trees_shrubs_and_plants"
  | "off_premises_storage"
  | "outdoor_property"
  | "land_improvements"
  | "spoilage_perishable_foods"
  | "piers_docks_wharfs_seawalls"
  | "property_in_course_of_construction"
  | "protection_and_preservation_of_property"
  | "professional_fees"
  | "animals_held_for_research"
  | "research_and_development"
  | "fire_brigade_and_extinguishing_expenses"
  | "soft_costs"
  | "vehicles_licensed_for_highway_use"
  | "waterway_access"
  | "extra_expense"
  | "fine_arts"
  | "land_water_contamination_and_cleanup_expense"
  | "decontamination_costs"
  | "leasehold_interest"
  | "loss_adjustment_expenses"
  | "personal_property_of_officers_and_employees"
  | "employee_tools"
  | "transit"
  | "accounts_receivable"
  | "mold_and_fungus"
  | "errors_and_omissions"
  | "miscellaneous_unnamed_locations"
  | "newly_acquired_automatic_coverage"
  | "extended_period_of_indemnity"
  | "backup_of_sewers_and_drains_sump_overflow"
  | "brands_and_labels"
  | "code_upgrade_upgrade_to_green"
  | "employee_personal_effects"
  | "exhibition_trade_show_property"
  | "freeze"
  | "leader_property"
  | "money_and_securities"
  | "off_premises_property_temporary_locations"
  | "refrigerated_property_temperature_change"
  | "sprinkler_leakage"
  | "strikes_riots_and_civil_commotion"
  | "valuable_papers_and_records"
  | "ordinary_payroll"
  | "impounded_water"
  | "land_and_water_cleanup_expense"
  | "limited_pollution"
  | "contractors_equipment"
  | "rental_value"
  | "royalties"
  | "cyber"
  | "asbestos"
  | "equipment_breakdown"
  | "consequential_loss"
  | "cancellation_of_bookings";

export const limit_name = {
  EARTHQUAKE: "earthquake",
  EARTHMOVEMENT: "earthmovement",
  FLOOD: "flood",
  FLOOD_HIGH_HAZARD_SFHA: "flood_high_hazard_sfha",
  WINDSTORM_NAMED_WINDSTORM_HURRICANE: "windstorm_named_windstorm_hurricane",
  BUILDING_ORDINANCE_COVERAGE_A: "building_ordinance_coverage_a",
  BUILDING_ORDINANCE_COVERAGE_B: "building_ordinance_coverage_b",
  BUILDING_ORDINANCE_COVERAGE_C: "building_ordinance_coverage_c",
  BUILDING_ORDINANCE_COVERAGE_D: "building_ordinance_coverage_d",
  CIVIL_OR_MILITARY_AUTHORITY: "civil_or_military_authority",
  INGRESS_EGRESS: "ingress_egress",
  DEBRIS_REMOVAL: "debris_removal",
  SERVICE_INTERRUPTION: "service_interruption",
  CONTINGENT_TIME_ELEMENT: "contingent_time_element",
  EVACUATION_EXPENSE: "evacuation_expense",
  ELECTRONIC_DATA_AND_MEDIA: "electronic_data_and_media",
  EXPEDITING_EXPENSES: "expediting_expenses",
  FINES_AND_PENALTIES: "fines_and_penalties",
  PROPERTY_IN_THE_OPEN: "property_in_the_open",
  PAIRS_AND_SETS: "pairs_and_sets",
  TREES_SHRUBS_AND_PLANTS: "trees_shrubs_and_plants",
  OFF_PREMISES_STORAGE: "off_premises_storage",
  OUTDOOR_PROPERTY: "outdoor_property",
  LAND_IMPROVEMENTS: "land_improvements",
  SPOILAGE_PERISHABLE_FOODS: "spoilage_perishable_foods",
  PIERS_DOCKS_WHARFS_SEAWALLS: "piers_docks_wharfs_seawalls",
  PROPERTY_IN_COURSE_OF_CONSTRUCTION: "property_in_course_of_construction",
  PROTECTION_AND_PRESERVATION_OF_PROPERTY:
    "protection_and_preservation_of_property",
  PROFESSIONAL_FEES: "professional_fees",
  ANIMALS_HELD_FOR_RESEARCH: "animals_held_for_research",
  RESEARCH_AND_DEVELOPMENT: "research_and_development",
  FIRE_BRIGADE_AND_EXTINGUISHING_EXPENSES:
    "fire_brigade_and_extinguishing_expenses",
  SOFT_COSTS: "soft_costs",
  VEHICLES_LICENSED_FOR_HIGHWAY_USE: "vehicles_licensed_for_highway_use",
  WATERWAY_ACCESS: "waterway_access",
  EXTRA_EXPENSE: "extra_expense",
  FINE_ARTS: "fine_arts",
  LAND_WATER_CONTAMINATION_AND_CLEANUP_EXPENSE:
    "land_water_contamination_and_cleanup_expense",
  DECONTAMINATION_COSTS: "decontamination_costs",
  LEASEHOLD_INTEREST: "leasehold_interest",
  LOSS_ADJUSTMENT_EXPENSES: "loss_adjustment_expenses",
  PERSONAL_PROPERTY_OF_OFFICERS_AND_EMPLOYEES:
    "personal_property_of_officers_and_employees",
  EMPLOYEE_TOOLS: "employee_tools",
  TRANSIT: "transit",
  ACCOUNTS_RECEIVABLE: "accounts_receivable",
  MOLD_AND_FUNGUS: "mold_and_fungus",
  ERRORS_AND_OMISSIONS: "errors_and_omissions",
  MISCELLANEOUS_UNNAMED_LOCATIONS: "miscellaneous_unnamed_locations",
  NEWLY_ACQUIRED_AUTOMATIC_COVERAGE: "newly_acquired_automatic_coverage",
  EXTENDED_PERIOD_OF_INDEMNITY: "extended_period_of_indemnity",
  BACKUP_OF_SEWERS_AND_DRAINS_SUMP_OVERFLOW:
    "backup_of_sewers_and_drains_sump_overflow",
  BRANDS_AND_LABELS: "brands_and_labels",
  CODE_UPGRADE_UPGRADE_TO_GREEN: "code_upgrade_upgrade_to_green",
  EMPLOYEE_PERSONAL_EFFECTS: "employee_personal_effects",
  EXHIBITION_TRADE_SHOW_PROPERTY: "exhibition_trade_show_property",
  FREEZE: "freeze",
  LEADER_PROPERTY: "leader_property",
  MONEY_AND_SECURITIES: "money_and_securities",
  OFF_PREMISES_PROPERTY_TEMPORARY_LOCATIONS:
    "off_premises_property_temporary_locations",
  REFRIGERATED_PROPERTY_TEMPERATURE_CHANGE:
    "refrigerated_property_temperature_change",
  SPRINKLER_LEAKAGE: "sprinkler_leakage",
  STRIKES_RIOTS_AND_CIVIL_COMMOTION: "strikes_riots_and_civil_commotion",
  VALUABLE_PAPERS_AND_RECORDS: "valuable_papers_and_records",
  ORDINARY_PAYROLL: "ordinary_payroll",
  IMPOUNDED_WATER: "impounded_water",
  LAND_AND_WATER_CLEANUP_EXPENSE: "land_and_water_cleanup_expense",
  LIMITED_POLLUTION: "limited_pollution",
  CONTRACTORS_EQUIPMENT: "contractors_equipment",
  RENTAL_VALUE: "rental_value",
  ROYALTIES: "royalties",
  CYBER: "cyber",
  ASBESTOS: "asbestos",
  EQUIPMENT_BREAKDOWN: "equipment_breakdown",
  CONSEQUENTIAL_LOSS: "consequential_loss",
  CANCELLATION_OF_BOOKINGS: "cancellation_of_bookings",
} as const;

export type LimitAndSublimitInfo = {
  extracted_limits: {
    [key: string]: {
      [key: string]: Limit;
    };
  };
  manual_limits: {
    [key: string]: Limit;
  };
  active_filename?: string | null;
};

export type LimitSummary = {
  occurrence: number | null;
  aggregate: number | null;
  zone: string;
};

export type LimitSummaryRequest = {
  report_id: string;
  address_ids: Array<string>;
};

export type LimitSummaryResponse = {
  addresses: {
    [key: string]: {
      [key: string]: LimitSummary;
    };
  };
};

export type LinkPolicyFlyApplicationRequest = {
  report_id: string;
  layer_id: string;
  suffix: string;
  url: string;
};

export type LinkPolicyFlyApplicationResponse = {
  policy_fly_info: PolicyFlyInfo;
};

export type ListAdditionalFilesResponse = {
  files: Array<AdditionalFile>;
};

export type ListBorLettersRequest = {
  active_only?: boolean;
};

export type ListClearanceMetadataRequest = {
  report_ids: Array<string>;
};

export type ListClearanceMetadataResponse = {
  items: Array<ReportClearanceEntry>;
};

export type ListContactsResponse = {
  contacts: Array<ReportContact>;
};

export type ListEndorsementsRequest = {
  report_id: string;
};

export type ListEndorsementsResponse = {
  endorsements: Array<EndorsementView>;
  totals: Array<EndorsementLedgerTotals>;
  current_tiv: number;
};

export type ListPolicyFlyApplicationsResponse = {
  applications: Array<PolicyFlyApplicationRow>;
};

export type ListReportActionLogsRequest = unknown;

export type ListReportActionLogsResponse = {
  summaries: Array<ReportActionSummaryWithName>;
};

export type ListReportJobsRequest = {
  report_ids: Array<string>;
  statuses: Array<WorkStatus>;
  include_all_runs?: boolean;
};

export type ListReportJobsResponse = {
  jobs: Array<ReportJobBatchEntry>;
};

export type ListReportsRequest = {
  lobs: Array<"property" | "builders_risk" | "cat" | "property_renewal">;
};

export type ListReportsResponse = {
  reports: Array<ReportMetadataResponse>;
};

export type ListSavedFiltersResponse = {
  filters: Array<SavedFilter>;
};

export type ListTemplatesResponse = {
  templates: Array<Template>;
};

export type ListUpdatingReportsRequest = {
  since: string;
};

export type ListUpdatingReportsResponse = {
  updates: Array<ReportBeingUpdated>;
};

export type ListUserResponse = {
  current_user: User;
  all_company_users: Array<User>;
};

export type LoadAddressesForMapRequest = {
  lobs: Array<string>;
};

export type LoadAddressesForMapResponse = {
  addresses: Array<SpatialKeyAddress>;
};

export type LocationAddress = {
  address_id: string;
  street_address: string;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  county?: string | null;
  tiv: number;
  lat?: string | null;
  long?: string | null;
};

export type LocationClassification = {
  state: string | null;
  county: string | null;
  wind_tier: "tier_1" | "tier_2" | null;
  wind_gate:
    | "Southeast Florida"
    | "Southwest Florida"
    | "Central Florida"
    | "Northeast Florida"
    | "Panhandle Florida"
    | "Georgia / South Carolina"
    | "North Carolina"
    | "Texas incl. Harris County"
    | "Louisiana"
    | "Mississippi"
    | "Alabama"
    | "Mid-Atlantic (VA/MD/DE)"
    | "Northeast (NY/NJ/CT/RI/MA/NH/ME)"
    | null;
  eq_gate:
    | "CA High Hazard"
    | "Pacific NW"
    | "Intermountain West"
    | "New Madrid"
    | "Northeast"
    | "Alabama EQ"
    | "Alaska EQ"
    | "District of Columbia EQ"
    | "Florida EQ"
    | "Georgia EQ"
    | "Guam EQ"
    | "Hawaii EQ"
    | "Iowa EQ"
    | "Kansas EQ"
    | "Louisiana EQ"
    | "Michigan EQ"
    | "Minnesota EQ"
    | "Mississippi EQ"
    | "Nebraska EQ"
    | "New Hampshire EQ"
    | "North Carolina EQ"
    | "North Dakota EQ"
    | "Ohio EQ"
    | "Oklahoma EQ"
    | "Puerto Rico EQ"
    | "South Carolina EQ"
    | "South Dakota EQ"
    | "Texas EQ"
    | "US Virgin Islands EQ"
    | "Wisconsin EQ"
    | null;
};

export type LocationClassificationsRequest = {
  locations: Array<LocationKey>;
};

export type LocationClassificationsResponse = {
  results: Array<LocationClassification>;
};

export type LocationKey = {
  state: string | null;
  county: string | null;
};

export type LogEventRequest = {
  event_name: string;
  event_properties: {
    [key: string]: string | number;
  };
};

export type LoginResponse = {
  access_token: string;
  token_type: string;
  client_id: string;
  user: User;
};

export type LossControlAnswer = {
  short_answer: string;
  long_answer: string;
};

export type LossControlReport = {
  entity_name?: string | null;
  filename: string;
  fields: {
    [key: string]: LossControlAnswer | null;
  };
};

export type LossRunInfo = {
  loss_runs_by_year: {
    [key: string]: Array<
      UserGeneratedLossRunByYear | ComputedLossRunByYear | ParsedLossRunByYear
    >;
  };
  enabled_by_source: {
    [key: string]: SourceDetailInfo;
  };
};

export type MappingInfo = {
  original_col_name: string;
  confidence: number;
};

export type MarkBorStepRequest = {
  bor_id: string;
  step:
    | "outgoing_broker_notified_at"
    | "incoming_broker_notified_at"
    | "rescind_letter_received_at"
    | "finalized_at"
    | "outgoing_broker_resolution_sent_at"
    | "incoming_broker_resolution_sent_at";
  timestamp?: string | null;
};

export type MatchingReportInfo = {
  report_name: string;
  report_id: string;
  matching_addresses: Array<string>;
  created_at_ts: string;
  matching_tiv?: number;
  total_tiv?: number;
  google_group_email?: string | null;
  effective_date?: string | null;
  prior_submission_result?: "prior_submission" | "updated_submission" | null;
};

export type MatchingReportsByAddresses = {
  matching_report_info: Array<MatchingReportInfo>;
};

export type MaterialShare = {
  material: string;
  ratio: number;
  area_sqm: number;
  confidence: number | null;
};

export type MultipleCompanyNamesFlag = {
  flag_id: "multiple_company_names";
  company_names: Array<string>;
};

export type NaicsInfo = {
  code_type?: "sic" | "naics";
  code: string;
  description: string;
};

export type code_type = "sic" | "naics";

export const code_type = {
  SIC: "sic",
  NAICS: "naics",
} as const;

/**
 * The ``IntegrationType.NEARMAP_AI`` row for one address: the material mix
 * plus the RMS modifier codes derived from it at fetch time (e.g.
 * ``{"roof_covering": "7"}``), so the enhanced cat run reads stored codes
 * instead of re-deriving.
 */
export type NearmapAiData = {
  roof_materials: RoofMaterialBreakdown;
  derived_modifiers: {
    [key: string]: string;
  };
};

/**
 * Curated subset of the Betterview Property Profile response.
 *
 * Construct from a validated Betterview envelope with :meth:`from_betterview`.
 * The model stays a plain round-trippable struct (``model_validate`` /
 * ``model_dump``) so it can be persisted and re-read without re-parsing.
 */
export type NearmapData = {
  solar_panels?: boolean | null;
  structural_damage?: boolean | null;
  ponding?: boolean | null;
  staining?: boolean | null;
  missing_shingles?: boolean | null;
  storm_report_count?: number;
  hail_event_count?: number;
  max_wind_gust_mph?: number | null;
  latest_storm_date?: string | null;
  buildings?: Array<BuildingData>;
  derived_modifiers?: {
    [key: string]: string;
  };
};

export type NearmapInitiateRequest = {
  report_id: string;
};

export type NearmapInitiateResponse = {
  request_id: string;
};

export type NearmapJobHistoryItem = {
  batch_id: string;
  status: WorkStatus;
  started_at: string;
  finished_at: string | null;
  num_addresses: number;
  enriched_count: number;
  unavailable_sources: Array<"betterview" | "nearmap_ai">;
};

export type NearmapJobHistoryResponse = {
  jobs?: Array<NearmapJobHistoryItem>;
};

export type OfacHit = {
  sanction_lists: Array<string>;
  entity_description: string;
};

export type OfacResult = {
  hits: Array<OfacHit>;
};

/**
 * Occupancy (O) from COPE — how the building is used.
 */
export type OccupancyData = {
  use_code?: string | null;
  standardized_land_use_code?: string | null;
  number_of_units?: number | null;
};

export type OccupancyMix = {
  occupancy_description: string;
  tiv: number;
  pct_tiv: number;
};

export type OverrideableListElementRawAdditionalInterestLiteralTodo = {
  original: RawAdditionalInterest | null;
  deleted: boolean;
  inserted: RawAdditionalInterest | null;
  flags: {
    [key: string]: "todo";
  };
  overrides: {
    [key: string]: string;
  };
};

export type ParsedLoss = {
  policy_year: string;
  date_of_loss: string;
  address: string | null;
  claim_id: string | null;
  loss_id: string | null;
  status: string;
  description: string | null;
  ground_up_loss: number | null;
  deductible: number | null;
  net_incurred: number | null;
  is_cat_event: boolean | null;
  claim_type: "historical" | "as_if" | null;
  source: "parsed";
  source_details: SourceDetails;
  overrides: {
    [key: string]: string;
  };
  flags: {
    [key: string]: Array<"could_not_parse">;
  };
  original_values: {
    [key: string]: string;
  };
};

export type ParsedLossRunByYear = {
  policy_year: string;
  description_of_losses: string;
  calc_mode: "historical" | "as_if" | null;
  source_details: SourceDetails;
  source: "parsed_year";
  open_claims: number;
  closed_claims: number;
  ground_up_loss: number | null;
  net_incurred: number | null;
  carrier: string | null;
  flags: {
    [key: string]: Array<"could_not_parse">;
  };
  original_values: {
    [key: string]: string;
  };
  overrides: {
    [key: string]: string;
  };
};

/**
 * A storm the NHC has finished advising on.
 */
export type PastStorm = {
  id: string;
  name: string;
  peak_intensity_kt: number;
  peaked: string;
};

/**
 * One finished storm and the path it actually took.
 *
 * No cone: every position is observed, so there is nothing left to be
 * uncertain about.
 */
export type PastStormWithTrack = {
  storm: PastStorm;
  track: Array<TrackPoint>;
};

/**
 * The season's named storms within reach, most recent first.
 */
export type PastStorms = {
  storms: Array<PastStormWithTrack>;
};

export type PerilCodePremium = {
  peril_code: string;
  aal: number;
  annual_aal: number | null;
  share_aal: number;
  multiplier: number | null;
  multiplier_reason: string;
  layer_premium: number;
  share_premium: number;
  layer_effective: number;
  share_effective: number;
  effective_base_premium: number;
  missing: boolean;
  missing_reason: string | null;
  warnings: {
    [key: string]: number;
  };
  override_type?: "multiple" | "premium" | null;
  override_multiple?: number | null;
  override_premium?: number | null;
};

/**
 * The RMS model profile a peril's results were produced on, for display.
 *
 * ``version`` is None when the stored ``model_profile_id`` isn't in current
 * config (historical/unknown) or is a version-agnostic HD profile — the
 * caller shows the peril with no version badge rather than failing.
 */
export type PerilModelProfile = {
  peril:
    | "PWH"
    | "PSH"
    | "PWH_PSH"
    | "PES"
    | "PFL"
    | "PFL_PSH"
    | "PWB"
    | "PWW"
    | "PWT";
  model_profile_id: number;
  version: "22" | "25" | null;
};

export type PerilMultipleCategories = {
  ns: "tri_county" | "fl" | "gulf_x_fl" | "baseline";
  ns_reason: string;
  eq: "eq_ca_a" | "eq_ca_b" | "ca" | "pnw" | "baseline";
  eq_reason: string;
};

export type ns = "tri_county" | "fl" | "gulf_x_fl" | "baseline";

export const ns = {
  TRI_COUNTY: "tri_county",
  FL: "fl",
  GULF_X_FL: "gulf_x_fl",
  BASELINE: "baseline",
} as const;

export type eq = "eq_ca_a" | "eq_ca_b" | "ca" | "pnw" | "baseline";

export const eq = {
  EQ_CA_A: "eq_ca_a",
  EQ_CA_B: "eq_ca_b",
  CA: "ca",
  PNW: "pnw",
  BASELINE: "baseline",
} as const;

/**
 * User override for one peril code on a layer, applied whichever modeling
 * source prices the layer.
 *
 * Exactly one of multiple or premium should be set.
 * When multiple is set: effective_premium = AAL * multiple.
 * When premium is set: effective_multiple = premium / AAL.
 */
export type PerilOverride = {
  multiple?: number | null;
  premium?: number | null;
};

/**
 * Letter grades from the HazardHub risks section for modeled perils.
 */
export type PerilScores = {
  hurricane?: string | null;
  storm_surge?: string | null;
  earthquake?: string | null;
  flood?: string | null;
  wildfire?: string | null;
  snow_load?: string | null;
  ground_snow_load?: string | null;
  wind?: string | null;
  hail?: string | null;
  tornado?: string | null;
  lightning?: string | null;
};

/**
 * Per-peril-type slice of LayerPremiumBreakdown.
 *
 * Each peril type carries three views of the same parts:
 *
 * - ``resolved`` is the cross-source rollup: data from whichever source
 * was picked for this peril type (``PerilTypePremium.source`` points at
 * that real vendor), with ``share_effective`` re-distributed via a
 * second largest-remainder pass so the per-peril shares across the
 * whole layer reconcile to ``share_total`` exactly. Always present.
 * - ``luminor_blend`` is the same rollup over a fixed source per peril
 * type rather than the report's picks. Always present.
 * - ``by_source`` is the per-real-source breakdown. Entries are only
 * present for sources that have data (or stubs) for this peril type.
 */
export type PerilTypeBreakdown = {
  resolved: PerilTypePremium;
  luminor_blend: PerilTypePremium;
  by_source: {
    [key: string]: PerilTypePremium;
  };
  offerable_sources: Array<
    "air" | "rms" | "spatialkat" | "rms_22" | "rms_22_air_50_50"
  >;
};

export type PerilTypePremium = {
  peril_type:
    | "aop"
    | "earthquake"
    | "earth_movement"
    | "named_storm"
    | "scs"
    | "flood"
    | "wildfire"
    | "equipment_breakdown"
    | "wind_and_hail";
  source: "air" | "rms" | "spatialkat" | "rms_22" | "rms_22_air_50_50";
  parts: Array<PerilCodePremium>;
  total_aal?: number;
  total_annual_aal: number | null;
  total_share_aal: number;
  share_effective: number;
  layer_effective: number;
  multiple_effective: number | null;
  model_profile: PerilModelProfile | null;
  run_term: "annual" | "policy" | null;
};

export type PlaceExposure = {
  state: string;
  county: string | null;
  locations: number;
  policies: number;
  tiv: number;
  lat: number | null;
  lon: number | null;
  min_lat: number | null;
  max_lat: number | null;
  min_lon: number | null;
  max_lon: number | null;
  wind_gate:
    | "Southeast Florida"
    | "Southwest Florida"
    | "Central Florida"
    | "Northeast Florida"
    | "Panhandle Florida"
    | "Georgia / South Carolina"
    | "North Carolina"
    | "Texas incl. Harris County"
    | "Louisiana"
    | "Mississippi"
    | "Alabama"
    | "Mid-Atlantic (VA/MD/DE)"
    | "Northeast (NY/NJ/CT/RI/MA/NH/ME)"
    | null;
  eq_gate:
    | "CA High Hazard"
    | "Pacific NW"
    | "Intermountain West"
    | "New Madrid"
    | "Northeast"
    | "Alabama EQ"
    | "Alaska EQ"
    | "District of Columbia EQ"
    | "Florida EQ"
    | "Georgia EQ"
    | "Guam EQ"
    | "Hawaii EQ"
    | "Iowa EQ"
    | "Kansas EQ"
    | "Louisiana EQ"
    | "Michigan EQ"
    | "Minnesota EQ"
    | "Mississippi EQ"
    | "Nebraska EQ"
    | "New Hampshire EQ"
    | "North Carolina EQ"
    | "North Dakota EQ"
    | "Ohio EQ"
    | "Oklahoma EQ"
    | "Puerto Rico EQ"
    | "South Carolina EQ"
    | "South Dakota EQ"
    | "Texas EQ"
    | "US Virgin Islands EQ"
    | "Wisconsin EQ"
    | null;
};

export type PolicyApplicationFieldsRequest = {
  application_id: string;
};

export type PolicyApplicationFieldsResponse = {
  application: PolicyApplicationView;
  fields: Array<ResolvedField>;
};

export type PolicyApplicationFormsRequest = {
  application_id: string;
};

export type PolicyApplicationFormsResponse = {
  policy_type: "ISO" | "Co-Participation" | "Manuscript" | null;
  tria_selection: "Accepted" | "Rejected" | null;
  forms: Array<FormSelection>;
  pairs: Array<FormPairView>;
};

export type PolicyApplicationStatus =
  | "draft"
  | "quoted"
  | "bound"
  | "issued"
  | "in_force"
  | "expired"
  | "declined"
  | "lost"
  | "withdrawn"
  | "renewed";

export const PolicyApplicationStatus = {
  DRAFT: "draft",
  QUOTED: "quoted",
  BOUND: "bound",
  ISSUED: "issued",
  IN_FORCE: "in_force",
  EXPIRED: "expired",
  DECLINED: "declined",
  LOST: "lost",
  WITHDRAWN: "withdrawn",
  RENEWED: "renewed",
} as const;

/**
 * API-facing view of a policy application row.
 */
export type PolicyApplicationView = {
  id: string;
  client_id: string;
  report_id: string;
  layer_id: string;
  suffix: string;
  policy_number: string | null;
  status: PolicyApplicationStatus;
  working_set: ApplicationWorkingSet;
  prior_application_id: string | null;
  effective_date: string | null;
  expiration_date: string | null;
  created_by: string;
  created_at_ts: string;
  modified_at_ts: string;
};

export type PolicyApplicationsResponse = {
  applications: Array<PolicyApplicationView>;
};

export type PolicyExposure = {
  report_id: string;
  named_insured: string | null;
  status:
    | "pre_underwriting_review"
    | "blocked"
    | "not_cleared_prior_submission"
    | "not_cleared_incomplete_submission"
    | "not_cleared_out_of_appetite"
    | "not_cleared_effective_date_too_far"
    | "not_cleared_unapproved_broker"
    | "not_cleared_low_tiv"
    | "not_cleared_foreign_exposure"
    | "cleared"
    | "pending"
    | "modeling_complete"
    | "referral_sent"
    | "flagged_for_review"
    | "indicated"
    | "quoted"
    | "declined"
    | "closed"
    | "closed_bor"
    | "not_reviewed"
    | "lost"
    | "quoted_not_taken_up"
    | "bound"
    | "issued";
  locations: number;
  tiv: number;
  line: number | null;
  reachable_line: number | null;
  lowest_attachment: number | null;
  layers_overlap: boolean;
  layers: Array<Layer>;
};

/**
 * Sparse patch over the report-derived base.
 *
 * ``upserts`` maps a field key to the policy-local value that wins over the
 * report; ``removals`` lists suppressed keys. A key in neither tracks the
 * report live. ``removals`` is a list (not a set) so it serializes to JSONB —
 * treat it as a set of keys.
 */
export type PolicyFieldOverlay = {
  upserts: {
    [key: string]: string | number | boolean | null;
  };
  removals: Array<string>;
};

export type PolicyFlyApplication = {
  layer_id: string;
  suffix: string;
  url?: string | null;
  request_id?: string | null;
};

export type PolicyFlyApplicationRow = {
  base_policy_id: string;
  total_premium: number;
  policy_status: string;
  report_id: string;
  lob: string;
  report_metadata: CommericalReportMetadata;
  created_at_ts: string;
  assignee: string | null;
  status:
    | "pre_underwriting_review"
    | "blocked"
    | "not_cleared_prior_submission"
    | "not_cleared_incomplete_submission"
    | "not_cleared_out_of_appetite"
    | "not_cleared_effective_date_too_far"
    | "not_cleared_unapproved_broker"
    | "not_cleared_low_tiv"
    | "not_cleared_foreign_exposure"
    | "cleared"
    | "pending"
    | "modeling_complete"
    | "referral_sent"
    | "flagged_for_review"
    | "indicated"
    | "quoted"
    | "declined"
    | "closed"
    | "closed_bor"
    | "not_reviewed"
    | "lost"
    | "quoted_not_taken_up"
    | "bound"
    | "issued";
  broker_contact: ReportContact | null;
  google_group_email: string | null;
  layer: LayerSummary | null;
  application_url: string | null;
};

export type PolicyFlyInfo = {
  applications: Array<PolicyFlyApplication>;
};

/**
 * Where one policy's locations actually sit.
 *
 * Deliberately unfiltered by geography or status: the question is the whole
 * spread of this policy, which is what makes a concentrated schedule
 * distinguishable from a national one.
 */
export type PolicyFootprint = {
  report_id: string;
  named_insured: string | null;
  status:
    | "pre_underwriting_review"
    | "blocked"
    | "not_cleared_prior_submission"
    | "not_cleared_incomplete_submission"
    | "not_cleared_out_of_appetite"
    | "not_cleared_effective_date_too_far"
    | "not_cleared_unapproved_broker"
    | "not_cleared_low_tiv"
    | "not_cleared_foreign_exposure"
    | "cleared"
    | "pending"
    | "modeling_complete"
    | "referral_sent"
    | "flagged_for_review"
    | "indicated"
    | "quoted"
    | "declined"
    | "closed"
    | "closed_bor"
    | "not_reviewed"
    | "lost"
    | "quoted_not_taken_up"
    | "bound"
    | "issued";
  locations: number;
  tiv: number;
  by_state: Array<PlaceExposure>;
  by_county: Array<PlaceExposure>;
};

export type PolicyFootprintRequest = {
  lob: string;
  report_id: string;
};

export type PolicyInfo = {
  selected_policy_form_name: string | null;
  included_perils: Array<
    | "aop"
    | "earthquake"
    | "earth_movement"
    | "named_storm"
    | "scs"
    | "flood"
    | "wildfire"
    | "equipment_breakdown"
    | "wind_and_hail"
  >;
  peril_sentence_override?: string | null;
  home_state_override?: string | null;
  deal_broker_name?: string | null;
  valuation_info?: ValuationInfo | null;
  form_selection?: "ISO" | "Co-Participation" | "Manuscript" | null;
  tria_selection?: "Accepted" | "Rejected" | null;
  broker_commission_percentage?: number | null;
  reinsurer_info?: ReinsurerInfo | null;
  peril_sources?: {
    [key: string]: "air" | "rms" | "spatialkat" | "rms_22" | "rms_22_air_50_50";
  } | null;
  surge_association?: "wind" | "flood" | "none" | "pinned";
  edited_fields?: Array<string>;
};

export type surge_association = "wind" | "flood" | "none" | "pinned";

export const surge_association = {
  WIND: "wind",
  FLOOD: "flood",
  NONE: "none",
  PINNED: "pinned",
} as const;

export type PolicyQuestion = {
  question: string;
  short_answer: string;
  long_answer: string;
  question_id: string;
  excerpt: string;
  confidence: string;
  page_location: string;
};

export type PolicyQuestionAndAnswer = {
  id: string;
  question: string;
  answer: string;
  answer_type: "Yes" | "No" | "N/A";
  excerpt: string;
};

export type answer_type = "Yes" | "No" | "N/A";

export const answer_type = {
  YES: "Yes",
  NO: "No",
  N_A: "N/A",
} as const;

export type PolicyQuestionsForDocuments = {
  questions_by_file: Array<PolicyQuestionsForFile>;
};

export type PolicyQuestionsForFile = {
  filename: string;
  questions_by_section: Array<PolicyQuestionsForSection>;
};

export type PolicyQuestionsForSection = {
  section: string;
  questions: Array<PolicyQuestionAndAnswer>;
};

export type PortfolioGateAccumulationResponse = {
  breakpoints: Array<GateBreakpoint>;
  bars: Array<GateTrackingBar>;
};

export type PrecheckResponse = {
  cat: CatPrecheckResponse;
  rater: RatePrecheckResponse;
};

export type PrecomputedMetadata = {
  num_locations: number;
  total_tiv: number;
  unique_location_states?: Array<string> | null;
  appetite?: Appetite | null;
  matching_reports_by_addresses?: MatchingReportsByAddresses | null;
  premium_categories?: PerilMultipleCategories;
  foreign_exposure_pct?: number | null;
};

export type PremiumError = {
  code: string;
  message: string;
};

/**
 * Protection (P) from COPE — fire protection characteristics.
 */
export type ProtectionData = {
  fire_protection_grade?: string | null;
  fire_protection_class?: string | null;
  hydrant_score?: string | null;
  drive_distance_to_fire_station_miles?: number | null;
  drive_time_to_fire_station_minutes?: number | null;
  fire_station_type?: string | null;
};

/**
 * Rendered quote indication email for the copy-to-clipboard flow.
 *
 * `body_html` matches the Outlook draft body exactly.
 */
export type QuoteIndicationHtmlResponse = {
  subject: string;
  body_html: string;
};

export type QuoteIndicationPreflight = {
  account_name: string;
  effective_date: string;
  expiration_date: string;
  limit_of_liability: string;
  total_insured_value: string;
  deductibles: Array<DeductibleEntry>;
  commission: string;
  bcc: string;
};

/**
 * Inputs the frontend computes locally and ships to the backend.
 *
 * `perils_insured` is the peril sentence derived by the frontend's
 * `perilSentenceFromEnabledPerils` helper. The quote letter builder carries
 * a Python port of the same helper for the letter.
 */
export type QuoteIndicationPreflightRequest = {
  report_id: string;
  layer_id: string;
  perils_insured: string;
};

export type QuoteUnknownsResponse = {
  unknowns: Array<string>;
};

/**
 * Subset of the RMS GET location response we care about.
 */
export type RmsLocationDetail = {
  locationWsDetail?: RmsLocationWsDetail;
  address?: RmsAddressDetail;
};

export type RmsLocationResult = {
  location_id?: string;
  location_number?: string;
  location_name?: string;
  street_address?: string;
  city?: string;
  aal_gu?: number;
  address_id: string;
  aal_gu_by_peril?: {
    [key: string]: number;
  };
  aal_gr_by_peril?: {
    [key: string]: number;
  };
  aal_rl_by_peril?: {
    [key: string]: number;
  };
  location_detail?: RmsLocationDetail | null;
  is_foreign_exposure?: boolean;
};

/**
 * Wind-storm detail from the RMS location detail response.
 */
export type RmsLocationWsDetail = {
  distToCoast?: number | null;
  sysDistToCoast?: number | null;
  distToCoastCov?: number | null;
  distToCoastMatch?: RmsMatchInfo | null;
  distanceToInnerCoast?: number | null;
  coastalSegment?: string | null;
  windPool?: number | null;
};

/**
 * RMS hazard match quality indicator (e.g. distToCoastMatch).
 */
export type RmsMatchInfo = {
  name: string;
};

export type RateComponentsAddressEntry = {
  address_id: string;
  premises_number?: string | null;
  location_name?: string | null;
  street_address?: string | null;
  city?: string | null;
  state?: string | null;
  sic_code?: string | null;
  components: {
    [key: string]: RateCoverageComponents;
  };
};

export type RateComponentsResponse = {
  addresses: Array<RateComponentsAddressEntry>;
  coverage_keys: Array<"BGI" | "BGII" | "SCL">;
};

/**
 * Breakdown of a single building coverage (BGI, BGII, SCL).
 *
 * base_rate is the ISO Loss Cost in dollars per $100 of building value.
 * factors maps factor name -> multiplier (both Building Premium factors and
 * the Adj Building factors). A factor > 1 is a debit, < 1 is a credit.
 * building_value is the insured building exposure in dollars.
 * The adjusted premium = base_rate * product(factors) * building_value / 100.
 */
export type RateCoverageComponents = {
  base_rate: number;
  factors: {
    [key: string]: number;
  };
  building_value: number;
};

export type RatePrecheckResponse = {
  warnings: Array<RatePrecheckWarning>;
};

export type RatePrecheckWarning = {
  summary: string;
  detail: string | null;
  blocking: boolean;
};

export type RaterRunSpec = {
  type: "rater";
  template_override_id: string | null;
  enrichment_source: "nearmap" | "cape" | null;
};

export type RawAdditionalInterest = {
  entity_name: string;
  entity_address: string;
  form_name: string;
  linked_address_id: string | null;
};

export type RawAddress = {
  street_address: string;
  tiv: number;
  state?: string | null;
  city?: string | null;
  zip?: string | null;
  county?: string | null;
  country?: string | null;
  country_scheme?: string | null;
  country_code?: string | null;
  year_built?: string | null;
  sprinkler?: string | null;
  building_value?: string | null;
  bpp_value?: string | null;
  tenant_betterments_value?: string | null;
  signs_value?: string | null;
  bi_excl_ee_value?: string | null;
  bi_incl_ee_value?: string | null;
  furniture_and_fixtures_value?: string | null;
  inventory_value?: string | null;
  contractors_equipment_value?: string | null;
  outdoor_property_and_autos_value?: string | null;
  number_of_stories?: string | null;
  construction_code_type?: string | null;
  construction_code?: string | null;
  construction_description?: string | null;
  occupancy_code_type?: string | null;
  occupancy_code?: string | null;
  occupancy_description?: string | null;
  occupancy_description_other?: string | null;
  sic_code?: string | null;
  location_name?: string | null;
  location_description?: string | null;
  lat?: string | null;
  long?: string | null;
  number_of_buildings?: string | null;
  square_footage?: string | null;
  plan_irregularity?: string | null;
  soft_story?: string | null;
  vertical_irregularity?: string | null;
  eq_cladding_type?: string | null;
  short_column?: string | null;
  urm_chimney?: string | null;
  urm_partition?: string | null;
  ornamentation?: string | null;
  cripple_walls?: string | null;
  frame_bolted_down?: string | null;
  anchoring?: string | null;
  urm_retrofit?: string | null;
  structural_upgrade?: string | null;
  engineered_foundation?: string | null;
  equipment_eq_bracing?: string | null;
  eq_construction_quality?: string | null;
  equipment_support_maintenance?: string | null;
  pounding?: string | null;
  base_isolation?: string | null;
  sprinkler_type?: string | null;
  eqsl_susceptibility?: string | null;
  ws_construction_quality?: string | null;
  roof_geometry?: string | null;
  roof_anchor?: string | null;
  roof_equipment_hurricane_bracing?: string | null;
  commercial_appurtenant_structures?: string | null;
  roof_covering?: string | null;
  roof_age_or_condition?: string | null;
  ws_cladding_type?: string | null;
  roof_sheathing_attachment?: string | null;
  frame_foundation_connection?: string | null;
  residential_appurtenant_structures?: string | null;
  ground_level_equipment?: string | null;
  opening_protection?: string | null;
  flashing_and_coping_quality?: string | null;
  floor_type?: string | null;
  flood_missiles?: string | null;
  flood_protection?: string | null;
  basement_protection?: string | null;
  vertical_exposure_distribution?: string | null;
  bi_preparedness?: string | null;
  bi_redundancy?: string | null;
  flood_zone?: string | null;
  wiring_year?: string | null;
  plumbing_year?: string | null;
  roofing_year?: string | null;
  heating_year?: string | null;
  fire_alarm_type?: string | null;
  burglar_alarm_type?: string | null;
  premises_number?: string | null;
  building_number?: string | null;
  geocoded_street_address?: string | null;
  geocoded_city?: string | null;
  geocoded_county?: string | null;
  geocoded_state?: string | null;
  geocoded_zip?: string | null;
  geocoded_country?: string | null;
};

export type RawCompanyInfo = {
  company_name: string;
  company_description: string;
  company_industry: string;
  effective_date: string;
  broker_name?: string | null;
  broker_email?: string | null;
  broker_agent_name?: string | null;
  named_insured?: string | null;
  additional_named_insureds?: Array<string> | null;
  dba?: string | null;
  entity_type?: string | null;
  expiration_date?: string | null;
  files_received_date?: string | null;
  business_description?: string | null;
  years_in_business?: string | null;
  company_address?: string | null;
  company_city?: string | null;
  company_state?: string | null;
  company_zip?: string | null;
  target_premium?: string | null;
  quote_by_date?: string | null;
  year_of_incorporation?: string | null;
};

export type ReferralText = {
  text: string | null;
};

/**
 * Per-account reinsurer participation percentages, sent to PolicyFly as the
 * application's insuring companies.
 */
export type ReinsurerInfo = {
  ren_percentage: number;
  nephila_percentage: number;
  starr_percentage: number;
};

export type RenderedBorEmail = {
  email_type:
    | "notice_to_current"
    | "ack_to_blocked"
    | "finalized_clear_to_new"
    | "finalized_block_to_former"
    | "rescind_to_current"
    | "rescind_to_blocked";
  target_report_id: string;
  subject: string;
  body_html: string;
  recipients: Array<string>;
};

export type email_type =
  | "notice_to_current"
  | "ack_to_blocked"
  | "finalized_clear_to_new"
  | "finalized_block_to_former"
  | "rescind_to_current"
  | "rescind_to_blocked";

export const email_type = {
  NOTICE_TO_CURRENT: "notice_to_current",
  ACK_TO_BLOCKED: "ack_to_blocked",
  FINALIZED_CLEAR_TO_NEW: "finalized_clear_to_new",
  FINALIZED_BLOCK_TO_FORMER: "finalized_block_to_former",
  RESCIND_TO_CURRENT: "rescind_to_current",
  RESCIND_TO_BLOCKED: "rescind_to_blocked",
} as const;

export type ReportActionLogEntryResponse = {
  id: number;
  email_message_id: string | null;
  report_id: string | null;
  user_id: string | null;
  client_id: string;
  timestamp: string;
  action_type:
    | "EMAIL_RECEIVED"
    | "REPORT_CREATED"
    | "EMAIL_ADDED_TO_REPORT"
    | "EMAIL_REJECTED_INVALID_SENDER"
    | "EMAIL_REJECTED_DUPLICATE"
    | "EMAIL_REJECTED_NO_ATTACHMENTS"
    | "EMAIL_REJECTED_REPLY_THREAD"
    | "UPDATE_REPORT"
    | "STATUS_CHANGED"
    | "ASSIGNEE_CHANGED"
    | "UPDATE_ADDRESSES"
    | "INTEGRATION_JOB_ENQUEUED"
    | "INTEGRATION_JOB_STARTED"
    | "INTEGRATION_JOB_COMPLETED"
    | "INTEGRATION_JOB_FAILED"
    | "CLEARANCE_EMAIL_SENT"
    | "QUOTE_INDICATION_DRAFTED"
    | "REFERRAL_DRAFTED"
    | "ATTACHMENT_UPLOADED"
    | "ATTACHMENT_DELETED"
    | "COMMENT_ADDED"
    | "COMMENT_EDITED"
    | "COMMENT_DELETED"
    | "BOR_CREATED"
    | "BOR_FINALIZED"
    | "BOR_RESCINDED"
    | "BOR_EMAIL_SENT"
    | "CAT_RUN_ACTIVATED"
    | "QUEUE_PRIORITY_CHANGED";
  metadata: {
    [key: string]: unknown;
  } | null;
};

export type action_type =
  | "EMAIL_RECEIVED"
  | "REPORT_CREATED"
  | "EMAIL_ADDED_TO_REPORT"
  | "EMAIL_REJECTED_INVALID_SENDER"
  | "EMAIL_REJECTED_DUPLICATE"
  | "EMAIL_REJECTED_NO_ATTACHMENTS"
  | "EMAIL_REJECTED_REPLY_THREAD"
  | "UPDATE_REPORT"
  | "STATUS_CHANGED"
  | "ASSIGNEE_CHANGED"
  | "UPDATE_ADDRESSES"
  | "INTEGRATION_JOB_ENQUEUED"
  | "INTEGRATION_JOB_STARTED"
  | "INTEGRATION_JOB_COMPLETED"
  | "INTEGRATION_JOB_FAILED"
  | "CLEARANCE_EMAIL_SENT"
  | "QUOTE_INDICATION_DRAFTED"
  | "REFERRAL_DRAFTED"
  | "ATTACHMENT_UPLOADED"
  | "ATTACHMENT_DELETED"
  | "COMMENT_ADDED"
  | "COMMENT_EDITED"
  | "COMMENT_DELETED"
  | "BOR_CREATED"
  | "BOR_FINALIZED"
  | "BOR_RESCINDED"
  | "BOR_EMAIL_SENT"
  | "CAT_RUN_ACTIVATED"
  | "QUEUE_PRIORITY_CHANGED";

export const action_type = {
  EMAIL_RECEIVED: "EMAIL_RECEIVED",
  REPORT_CREATED: "REPORT_CREATED",
  EMAIL_ADDED_TO_REPORT: "EMAIL_ADDED_TO_REPORT",
  EMAIL_REJECTED_INVALID_SENDER: "EMAIL_REJECTED_INVALID_SENDER",
  EMAIL_REJECTED_DUPLICATE: "EMAIL_REJECTED_DUPLICATE",
  EMAIL_REJECTED_NO_ATTACHMENTS: "EMAIL_REJECTED_NO_ATTACHMENTS",
  EMAIL_REJECTED_REPLY_THREAD: "EMAIL_REJECTED_REPLY_THREAD",
  UPDATE_REPORT: "UPDATE_REPORT",
  STATUS_CHANGED: "STATUS_CHANGED",
  ASSIGNEE_CHANGED: "ASSIGNEE_CHANGED",
  UPDATE_ADDRESSES: "UPDATE_ADDRESSES",
  INTEGRATION_JOB_ENQUEUED: "INTEGRATION_JOB_ENQUEUED",
  INTEGRATION_JOB_STARTED: "INTEGRATION_JOB_STARTED",
  INTEGRATION_JOB_COMPLETED: "INTEGRATION_JOB_COMPLETED",
  INTEGRATION_JOB_FAILED: "INTEGRATION_JOB_FAILED",
  CLEARANCE_EMAIL_SENT: "CLEARANCE_EMAIL_SENT",
  QUOTE_INDICATION_DRAFTED: "QUOTE_INDICATION_DRAFTED",
  REFERRAL_DRAFTED: "REFERRAL_DRAFTED",
  ATTACHMENT_UPLOADED: "ATTACHMENT_UPLOADED",
  ATTACHMENT_DELETED: "ATTACHMENT_DELETED",
  COMMENT_ADDED: "COMMENT_ADDED",
  COMMENT_EDITED: "COMMENT_EDITED",
  COMMENT_DELETED: "COMMENT_DELETED",
  BOR_CREATED: "BOR_CREATED",
  BOR_FINALIZED: "BOR_FINALIZED",
  BOR_RESCINDED: "BOR_RESCINDED",
  BOR_EMAIL_SENT: "BOR_EMAIL_SENT",
  CAT_RUN_ACTIVATED: "CAT_RUN_ACTIVATED",
  QUEUE_PRIORITY_CHANGED: "QUEUE_PRIORITY_CHANGED",
} as const;

export type ReportActionSummaryWithName = {
  report_id?: string | null;
  company_name?: string | null;
  email_message_id?: string | null;
  email_subject?: string | null;
  sender?: string | null;
  latest_action_type:
    | "EMAIL_RECEIVED"
    | "REPORT_CREATED"
    | "EMAIL_ADDED_TO_REPORT"
    | "EMAIL_REJECTED_INVALID_SENDER"
    | "EMAIL_REJECTED_DUPLICATE"
    | "EMAIL_REJECTED_NO_ATTACHMENTS"
    | "EMAIL_REJECTED_REPLY_THREAD"
    | "UPDATE_REPORT"
    | "STATUS_CHANGED"
    | "ASSIGNEE_CHANGED"
    | "UPDATE_ADDRESSES"
    | "INTEGRATION_JOB_ENQUEUED"
    | "INTEGRATION_JOB_STARTED"
    | "INTEGRATION_JOB_COMPLETED"
    | "INTEGRATION_JOB_FAILED"
    | "CLEARANCE_EMAIL_SENT"
    | "QUOTE_INDICATION_DRAFTED"
    | "REFERRAL_DRAFTED"
    | "ATTACHMENT_UPLOADED"
    | "ATTACHMENT_DELETED"
    | "COMMENT_ADDED"
    | "COMMENT_EDITED"
    | "COMMENT_DELETED"
    | "BOR_CREATED"
    | "BOR_FINALIZED"
    | "BOR_RESCINDED"
    | "BOR_EMAIL_SENT"
    | "CAT_RUN_ACTIVATED"
    | "QUEUE_PRIORITY_CHANGED";
  latest_timestamp: string;
  action_count: number;
};

export type latest_action_type =
  | "EMAIL_RECEIVED"
  | "REPORT_CREATED"
  | "EMAIL_ADDED_TO_REPORT"
  | "EMAIL_REJECTED_INVALID_SENDER"
  | "EMAIL_REJECTED_DUPLICATE"
  | "EMAIL_REJECTED_NO_ATTACHMENTS"
  | "EMAIL_REJECTED_REPLY_THREAD"
  | "UPDATE_REPORT"
  | "STATUS_CHANGED"
  | "ASSIGNEE_CHANGED"
  | "UPDATE_ADDRESSES"
  | "INTEGRATION_JOB_ENQUEUED"
  | "INTEGRATION_JOB_STARTED"
  | "INTEGRATION_JOB_COMPLETED"
  | "INTEGRATION_JOB_FAILED"
  | "CLEARANCE_EMAIL_SENT"
  | "QUOTE_INDICATION_DRAFTED"
  | "REFERRAL_DRAFTED"
  | "ATTACHMENT_UPLOADED"
  | "ATTACHMENT_DELETED"
  | "COMMENT_ADDED"
  | "COMMENT_EDITED"
  | "COMMENT_DELETED"
  | "BOR_CREATED"
  | "BOR_FINALIZED"
  | "BOR_RESCINDED"
  | "BOR_EMAIL_SENT"
  | "CAT_RUN_ACTIVATED"
  | "QUEUE_PRIORITY_CHANGED";

export const latest_action_type = {
  EMAIL_RECEIVED: "EMAIL_RECEIVED",
  REPORT_CREATED: "REPORT_CREATED",
  EMAIL_ADDED_TO_REPORT: "EMAIL_ADDED_TO_REPORT",
  EMAIL_REJECTED_INVALID_SENDER: "EMAIL_REJECTED_INVALID_SENDER",
  EMAIL_REJECTED_DUPLICATE: "EMAIL_REJECTED_DUPLICATE",
  EMAIL_REJECTED_NO_ATTACHMENTS: "EMAIL_REJECTED_NO_ATTACHMENTS",
  EMAIL_REJECTED_REPLY_THREAD: "EMAIL_REJECTED_REPLY_THREAD",
  UPDATE_REPORT: "UPDATE_REPORT",
  STATUS_CHANGED: "STATUS_CHANGED",
  ASSIGNEE_CHANGED: "ASSIGNEE_CHANGED",
  UPDATE_ADDRESSES: "UPDATE_ADDRESSES",
  INTEGRATION_JOB_ENQUEUED: "INTEGRATION_JOB_ENQUEUED",
  INTEGRATION_JOB_STARTED: "INTEGRATION_JOB_STARTED",
  INTEGRATION_JOB_COMPLETED: "INTEGRATION_JOB_COMPLETED",
  INTEGRATION_JOB_FAILED: "INTEGRATION_JOB_FAILED",
  CLEARANCE_EMAIL_SENT: "CLEARANCE_EMAIL_SENT",
  QUOTE_INDICATION_DRAFTED: "QUOTE_INDICATION_DRAFTED",
  REFERRAL_DRAFTED: "REFERRAL_DRAFTED",
  ATTACHMENT_UPLOADED: "ATTACHMENT_UPLOADED",
  ATTACHMENT_DELETED: "ATTACHMENT_DELETED",
  COMMENT_ADDED: "COMMENT_ADDED",
  COMMENT_EDITED: "COMMENT_EDITED",
  COMMENT_DELETED: "COMMENT_DELETED",
  BOR_CREATED: "BOR_CREATED",
  BOR_FINALIZED: "BOR_FINALIZED",
  BOR_RESCINDED: "BOR_RESCINDED",
  BOR_EMAIL_SENT: "BOR_EMAIL_SENT",
  CAT_RUN_ACTIVATED: "CAT_RUN_ACTIVATED",
  QUEUE_PRIORITY_CHANGED: "QUEUE_PRIORITY_CHANGED",
} as const;

export type ReportBeingUpdated = {
  update_id: string;
  report_id: string;
  running_user_id: string | null;
  start_time: string;
  outcome: "success" | "failure" | null;
  integration_type: string;
  policy_fly_url?: string | null;
  error_description?: string | null;
};

export type ReportClearanceEntry = {
  report_id: string;
  fix_sov: FixSovRunSummary | null;
  review: ClearanceReview | null;
  updated_at_ts: string;
};

export type ReportContact = {
  id: string;
  contact_type: ContactType;
  email: string;
  organization_name: string | null;
  full_name: string | null;
  office_phone: string | null;
  cell_phone: string | null;
  role: string | null;
};

export type ReportJobBatchEntry = {
  batch_id: string;
  report_id: string;
  integration_type: string;
  outcome: "success" | "failure" | null;
  started_at: string;
  finished_at: string | null;
  env: string;
  initiating_user_id: string | null;
  jobs: Array<ReportJobEntry>;
  priority: number;
  blocked_by_kinds: Array<string>;
};

export type ReportJobEntry = {
  report_integration_id: string;
  topic: string;
  status: WorkStatus;
  started_at: string;
  queue_position: number | null;
  sleeping_until: string | null;
  priority: number;
  response: {
    [key: string]: unknown;
  } | null;
  icm_progress: IcmJobProgress | null;
  cat_metadata: CatMetadata | null;
};

export type ReportMetadataResponse = {
  report_id: string;
  lob: string;
  report_metadata: CommericalReportMetadata;
  created_at_ts: string;
  modified_at_ts: string;
  assignee: string | null;
  status:
    | "pre_underwriting_review"
    | "blocked"
    | "not_cleared_prior_submission"
    | "not_cleared_incomplete_submission"
    | "not_cleared_out_of_appetite"
    | "not_cleared_effective_date_too_far"
    | "not_cleared_unapproved_broker"
    | "not_cleared_low_tiv"
    | "not_cleared_foreign_exposure"
    | "cleared"
    | "pending"
    | "modeling_complete"
    | "referral_sent"
    | "flagged_for_review"
    | "indicated"
    | "quoted"
    | "declined"
    | "closed"
    | "closed_bor"
    | "not_reviewed"
    | "lost"
    | "quoted_not_taken_up"
    | "bound"
    | "issued";
  additional_assignees: AdditionalAssigneeInfo | null;
  broker_contact: ReportContact | null;
  google_group_email: string | null;
  attachment_mappings: Array<
    | "sov_spreadsheet"
    | "acord_app"
    | "loss_run"
    | "none"
    | "screenshot"
    | "rater"
    | "loss_control_report"
    | "policy_document"
    | "manuscript_form"
    | "final_manuscript_form"
    | "specs_sheet"
    | "record_keeping"
    | "luminor_quote"
    | "luminor_tria"
    | "luminor_binder"
    | "luminor_policy"
    | "lead_quote"
    | "lead_binder"
    | "lead_policy"
    | "bound_sov"
    | "sl_letter"
    | "endorsement_file"
    | "endorsement_sov"
    | "engineering_report"
    | "rate_matrix"
    | "form_review"
  >;
  ready_for_issuance: boolean;
  share_premium: number | null;
  has_underwriting_rationale: boolean;
};

export type ReportPolicyApplicationsRequest = {
  report_id: string;
};

export type ReportResponse = {
  report_id: string;
  policy_number: string | null;
  lob: string;
  email_subject: string;
  report_json: CommercialReport;
  created_at_ts: string;
  modified_at_ts: string;
  review_info: ReviewInfo;
  assignee: string | null;
  status:
    | "pre_underwriting_review"
    | "blocked"
    | "not_cleared_prior_submission"
    | "not_cleared_incomplete_submission"
    | "not_cleared_out_of_appetite"
    | "not_cleared_effective_date_too_far"
    | "not_cleared_unapproved_broker"
    | "not_cleared_low_tiv"
    | "not_cleared_foreign_exposure"
    | "cleared"
    | "pending"
    | "modeling_complete"
    | "referral_sent"
    | "flagged_for_review"
    | "indicated"
    | "quoted"
    | "declined"
    | "closed"
    | "closed_bor"
    | "not_reviewed"
    | "lost"
    | "quoted_not_taken_up"
    | "bound"
    | "issued";
  additional_assignees: AdditionalAssigneeInfo | null;
  precomputed_metadata: PrecomputedMetadata;
  auto_assignment_style: AutoAssignmentStyle | null;
  broker_contact: ReportContact | null;
  google_group_email: string | null;
  underwriting_rationale: UnderwritingRationale | null;
  term_wording: TermWording;
};

export type RerunAttachmentOption = {
  name: string;
  is_parsed: boolean;
};

/**
 * A report component that a partial rerun can reprocess in isolation.
 *
 * The mapping of which attachment types each component reads, and which
 * attachment changes can be narrowed to which components, lives next to the
 * parsers in ``parse_attachments.py``.
 */
export type RerunComponent =
  | "sov"
  | "loss_run"
  | "loss_control"
  | "policy_terms"
  | "policy_questions";

/**
 * A report component that a partial rerun can reprocess in isolation.
 *
 * The mapping of which attachment types each component reads, and which
 * attachment changes can be narrowed to which components, lives next to the
 * parsers in ``parse_attachments.py``.
 */
export const RerunComponent = {
  SOV: "sov",
  LOSS_RUN: "loss_run",
  LOSS_CONTROL: "loss_control",
  POLICY_TERMS: "policy_terms",
  POLICY_QUESTIONS: "policy_questions",
} as const;

export type RerunComponentOptions = {
  component: RerunComponent;
  attachments: Array<RerunAttachmentOption>;
};

export type RerunOptionsResponse = {
  components: Array<RerunComponentOptions>;
};

/**
 * A projected field after the overlay is applied, carrying its source.
 */
export type ResolvedField = {
  key: string;
  section: string;
  order: number;
  label: string;
  value: string | number | boolean | null;
  editable: boolean;
  input_type: "text" | "number" | "boolean" | "select";
  options: Array<string> | null;
  removable: boolean;
  required: boolean;
  source: FieldSource;
};

export type input_type = "text" | "number" | "boolean" | "select";

export const input_type = {
  TEXT: "text",
  NUMBER: "number",
  BOOLEAN: "boolean",
  SELECT: "select",
} as const;

export type ResolvedLayerPremium = {
  layer_id: string;
  starting_point: number;
  range: number;
  participation: number | null;
  target: number | null;
  layer_total: number | null;
  share_total: number | null;
  has_missing_data: boolean;
  peril_types: {
    [key: string]: ResolvedPerilPremium;
  };
  /**
   *
   * Cat loss ratio (modeled AAL / premium) for each modeling "strategy."
   * This only includes AAL and premium allocated to cat perils.
   * Strategies with incomplete modeling results are omitted entirely.
   */
  cat_loss_ratio_by_strategy: {
    [key: string]: number;
  };
};

/**
 * Resolved view of one peril type, stripped of vendor data: no source
 * names, no per-vendor breakdowns. The resolved AAL is deliberately
 * unattributed — callers can't tell which model produced it.
 */
export type ResolvedPerilPremium = {
  resolved_layer_aal: number | null;
  resolved_share_aal: number | null;
  layer_premium: number;
  share_premium: number;
};

export type ResolvedPremiumsResponse = {
  layers: Array<ResolvedLayerPremium>;
  errors?: Array<PremiumError>;
};

export type ReviewInfo = {
  responses?: {
    [key: string]: ReviewQuestionResponse;
  };
};

export type ReviewQuestionResponse = {
  question_id: string;
  status: string;
  warning_message?: string | null;
  last_updated_ts?: string | null;
  last_updated_user_id?: string | null;
};

export type RmsAddressDetail = {
  zone1?: string | null;
  zone2?: string | null;
  zone3?: string | null;
};

/**
 * Roof materials for one location.
 *
 * ``roofs`` holds the roofs attributed to the location, largest-roof-first:
 * every roof on the parcel for a parcel query, only the roof containing the
 * point (else the nearest) for a point query. ``shares`` is the area-weighted
 * mix across those roofs and ``top_material`` the material holding the most
 * of that area.
 */
export type RoofMaterialBreakdown = {
  survey_date: string;
  system_version: string;
  geomatched_address: string | null;
  roofs: Array<RoofMaterials>;
  shares: Array<MaterialShare>;
  unclassified_ratio: number;
};

/**
 * Material mix of one roof. ``shares`` lists detected materials only,
 * largest share first (ties broken by confidence).
 */
export type RoofMaterials = {
  roof_id: string;
  area_sqm: number;
  confidence: number;
  shares: Array<MaterialShare>;
  unclassified_ratio: number;
};

export type RunFixSovRequest = {
  report_id: string;
  instructions: string | null;
};

export type RunFixSovResponse = {
  task_id: string;
};

export type Sov = {
  sov_id: string;
  addresses: Array<Address>;
  is_enabled: boolean;
  is_manual?: boolean;
  filename: string;
  sov_type?: "bound" | "submission" | "endorsement" | null;
  all_document_headers?: Array<string> | null;
  column_mappings?: {
    [key: string]: MappingInfo;
  } | null;
  tiv_totals_match_on_ingest?: boolean | null;
  tiv_target_source?: "email" | "spreadsheet" | null;
  reconciliation_function?: string | null;
  tiv_reconciliation_explanation?: string | null;
};

export type SaveQuoteTemplateResponse = {
  customized: boolean;
};

export type SavedFilter = {
  id: string;
  url: string;
  nickname: string;
  is_default: boolean;
};

export type SendClearanceEmailRequest = {
  report_id: string;
  status:
    | "pre_underwriting_review"
    | "blocked"
    | "not_cleared_prior_submission"
    | "not_cleared_incomplete_submission"
    | "not_cleared_out_of_appetite"
    | "not_cleared_effective_date_too_far"
    | "not_cleared_unapproved_broker"
    | "not_cleared_low_tiv"
    | "not_cleared_foreign_exposure"
    | "cleared"
    | "pending"
    | "modeling_complete"
    | "referral_sent"
    | "flagged_for_review"
    | "indicated"
    | "quoted"
    | "declined"
    | "closed"
    | "closed_bor"
    | "not_reviewed"
    | "lost"
    | "quoted_not_taken_up"
    | "bound"
    | "issued";
};

export type SetBatchPriorityRequest = {
  batch_id: string;
  priority: -20 | -10 | 0 | 10;
};

export type priority = -20 | -10 | 0 | 10;

export const priority = {
  "_-20": -20,
  "_-10": -10,
  _0: 0,
  _10: 10,
} as const;

export type SetBatchPriorityResponse = unknown;

export type SetFunctionInstructionsRequest = {
  report_id: string;
  filename: string;
  function_type: "limit" | "deductible";
  instructions: string | null;
};

export type function_type = "limit" | "deductible";

export const function_type = {
  LIMIT: "limit",
  DEDUCTIBLE: "deductible",
} as const;

export type SetFunctionInstructionsResponse = {
  instructions: string | null;
};

export type SindriEnqueueRequest = {
  report_id: string;
};

export type SindriEnqueueResponse = {
  batch_id: string;
};

export type SindriExplanation = {
  itv_ratio: number | null;
  itv_ratio_reason:
    | "no_signal"
    | "no_building_value"
    | "contents_or_leased_indicator"
    | "withheld"
    | null;
  itv_band: SindriItvBand | null;
};

export type SindriItvBand = {
  es_low_psf: number | null;
  es_high_psf: number | null;
  position: string | null;
};

/**
 * One location's result. A row that failed validation or scoring has
 * ``error`` set and null display fields; a row with no value has a null
 * ``luminor_rc`` and a ``failure_reason``.
 */
export type SindriRow = {
  address_id: string;
  luminor_rc: number | null;
  display_status:
    | "show"
    | "show_with_caution"
    | "withhold_pending_review"
    | null;
  display_reasons: Array<string> | null;
  display_confidence: "High" | "Standard" | null;
  explanation_text: string | null;
  insured_building: boolean | null;
  explanation: SindriExplanation | null;
  failure_reason: string | null;
  warnings: Array<string>;
  text_truncated: Array<string>;
  error: SindriRowError | null;
};

export type SindriRowError = {
  reason: string;
};

export type SourceDetailInfo = {
  source_details: SourceDetails;
  enabled: boolean;
};

export type SourceDetails = {
  filename: string;
  sheet_name: string | null;
  data_type: "claim" | "summary";
  calc_mode: "historical" | "as_if" | null;
  carrier: string | null;
};

export type data_type = "claim" | "summary";

export const data_type = {
  CLAIM: "claim",
  SUMMARY: "summary",
} as const;

export type SovDiffLocation = {
  address: string;
  tiv: number;
};

export type SovDiffRevaluedLocation = {
  address: string;
  tiv_before: number;
  tiv_after: number;
};

/**
 * Per-location AAL result stored in integration_data.
 */
export type SpatialKatLocationResult = {
  site_id: string;
  address_id: string;
  aal_gu?: number;
  aal_gr?: number;
  aal_net?: number;
  aal_gu_by_peril?: {
    [key: string]: number;
  };
  aal_gr_by_peril?: {
    [key: string]: number;
  };
  is_foreign_exposure?: boolean;
  fema_flood_zone?: string | null;
  fema_zone_subty?: string | null;
  flood_score?: number | null;
  surge_score?: number | null;
  wind_score?: number | null;
  hail_score?: number | null;
  to_score?: number | null;
  slw_score?: number | null;
};

export type SpatialKeyAddress = {
  "Policy Number": string;
  "Named Insured": string;
  "Effective Date": string;
  "Expiration Date": string;
  "Location #"?: string | null;
  "Bulding#"?: string | null;
  "Street 1"?: string | null;
  "Street 2"?: string | null;
  City?: string | null;
  State?: string | null;
  Zip?: string | null;
  County?: string | null;
  BLDG?: number | null;
  BPP?: number | null;
  BI_EE?: number | null;
  BI?: number | null;
  TIB?: number | null;
  SIGNS?: number | null;
  FF?: number | null;
  INV?: number | null;
  CE?: number | null;
  OPA?: number | null;
  "Total TIV"?: number | null;
  EQPBK?: string | null;
  TRIA?: string | null;
  LNLawOrdA?: number | null;
  LNLawOrdBC?: number | null;
  LNLawOrd?: number | null;
  Valuation?: string | null;
  Coinsurance?: string | null;
  PTY_WIND_INCL_Y_N?: string | null;
  WIND_DEDUCTIBLE_BASIS?: string | null;
  WIND_DEDUCTIBLE_AMOUNT?: string | null;
  WIND_DEDUCTIBLE_PERCENTAGE?: string | null;
  AOP_DEDUCTIBLE_BASIS?: string | null;
  AOP_DEDUCTIBLE_PERCENTAGE?: string | null;
  AOP_DEDUCTIBLE_AMOUNT?: string | null;
  CP_DEDUCTIBLE_AMOUNT?: string | null;
  CP_DEDUCTIBLE_TYPE?: string | null;
  "Latitude(SK)"?: number | null;
  "Longitude(SK)"?: number | null;
  "Geocoding Accuracy"?: string | null;
};

export type StagedAttachment = {
  name: string;
  size: number;
};

/**
 * One attachment the user chose to promote out of a staged email.
 *
 * ``original_classification`` is the machine suggestion shown in the dialog;
 * ``manual_classification`` is set when the user picked something else (or
 * when no suggestion could be computed), so the pick survives report reruns
 * the same way manual reclassification on the attachments tab does.
 */
export type StagedAttachmentSelection = {
  staging_id: string;
  attachment_name: string;
  original_classification:
    | "sov_spreadsheet"
    | "acord_app"
    | "loss_run"
    | "none"
    | "screenshot"
    | "rater"
    | "loss_control_report"
    | "policy_document"
    | "manuscript_form"
    | "final_manuscript_form"
    | "specs_sheet"
    | "record_keeping"
    | "luminor_quote"
    | "luminor_tria"
    | "luminor_binder"
    | "luminor_policy"
    | "lead_quote"
    | "lead_binder"
    | "lead_policy"
    | "bound_sov"
    | "sl_letter"
    | "endorsement_file"
    | "endorsement_sov"
    | "engineering_report"
    | "rate_matrix"
    | "form_review";
  manual_classification:
    | "sov_spreadsheet"
    | "acord_app"
    | "loss_run"
    | "none"
    | "screenshot"
    | "rater"
    | "loss_control_report"
    | "policy_document"
    | "manuscript_form"
    | "final_manuscript_form"
    | "specs_sheet"
    | "record_keeping"
    | "luminor_quote"
    | "luminor_tria"
    | "luminor_binder"
    | "luminor_policy"
    | "lead_quote"
    | "lead_binder"
    | "lead_policy"
    | "bound_sov"
    | "sl_letter"
    | "endorsement_file"
    | "endorsement_sov"
    | "engineering_report"
    | "rate_matrix"
    | "form_review"
    | null;
  conversation_id: string | null;
};

export type original_classification =
  | "sov_spreadsheet"
  | "acord_app"
  | "loss_run"
  | "none"
  | "screenshot"
  | "rater"
  | "loss_control_report"
  | "policy_document"
  | "manuscript_form"
  | "final_manuscript_form"
  | "specs_sheet"
  | "record_keeping"
  | "luminor_quote"
  | "luminor_tria"
  | "luminor_binder"
  | "luminor_policy"
  | "lead_quote"
  | "lead_binder"
  | "lead_policy"
  | "bound_sov"
  | "sl_letter"
  | "endorsement_file"
  | "endorsement_sov"
  | "engineering_report"
  | "rate_matrix"
  | "form_review";

export const original_classification = {
  SOV_SPREADSHEET: "sov_spreadsheet",
  ACORD_APP: "acord_app",
  LOSS_RUN: "loss_run",
  NONE: "none",
  SCREENSHOT: "screenshot",
  RATER: "rater",
  LOSS_CONTROL_REPORT: "loss_control_report",
  POLICY_DOCUMENT: "policy_document",
  MANUSCRIPT_FORM: "manuscript_form",
  FINAL_MANUSCRIPT_FORM: "final_manuscript_form",
  SPECS_SHEET: "specs_sheet",
  RECORD_KEEPING: "record_keeping",
  LUMINOR_QUOTE: "luminor_quote",
  LUMINOR_TRIA: "luminor_tria",
  LUMINOR_BINDER: "luminor_binder",
  LUMINOR_POLICY: "luminor_policy",
  LEAD_QUOTE: "lead_quote",
  LEAD_BINDER: "lead_binder",
  LEAD_POLICY: "lead_policy",
  BOUND_SOV: "bound_sov",
  SL_LETTER: "sl_letter",
  ENDORSEMENT_FILE: "endorsement_file",
  ENDORSEMENT_SOV: "endorsement_sov",
  ENGINEERING_REPORT: "engineering_report",
  RATE_MATRIX: "rate_matrix",
  FORM_REVIEW: "form_review",
} as const;

export type StagedEmailManifest = {
  staging_id: string;
  filename: string;
  subject: string | null;
  conversation_id: string | null;
  attachments: Array<StagedAttachment>;
};

export type StandardFormattingRequest = {
  text: string;
  field: "limit" | "deductible" | "sublimit";
  classification: "time_limit" | "distance_limit" | "waiting_period" | null;
};

export type field = "limit" | "deductible" | "sublimit";

export const field = {
  LIMIT: "limit",
  DEDUCTIBLE: "deductible",
  SUBLIMIT: "sublimit",
} as const;

export type StateSummary = {
  state: string;
  total_tiv: number;
  construction_mix: Array<ConstructionMix>;
  occupancy_mix: Array<OccupancyMix>;
  year_built_distribution: Array<YearBuiltBand>;
};

export type StatusExposure = {
  status:
    | "pre_underwriting_review"
    | "blocked"
    | "not_cleared_prior_submission"
    | "not_cleared_incomplete_submission"
    | "not_cleared_out_of_appetite"
    | "not_cleared_effective_date_too_far"
    | "not_cleared_unapproved_broker"
    | "not_cleared_low_tiv"
    | "not_cleared_foreign_exposure"
    | "cleared"
    | "pending"
    | "modeling_complete"
    | "referral_sent"
    | "flagged_for_review"
    | "indicated"
    | "quoted"
    | "declined"
    | "closed"
    | "closed_bor"
    | "not_reviewed"
    | "lost"
    | "quoted_not_taken_up"
    | "bound"
    | "issued";
  policies: number;
  locations: number;
  tiv: number;
};

/**
 * One active storm and the path the NHC currently forecasts for it.
 */
export type StormWithTrack = {
  storm: ActiveStorm;
  track: Array<TrackPoint>;
  cone: Array<Coordinate>;
};

export type Sublimit = {
  sublimit_name: string;
  sublimit_description: string;
  document_excerpt: string | null;
  manual_override?: string | null;
  is_removed?: boolean | null;
  classification?: "time_limit" | "distance_limit" | "waiting_period" | null;
  sort_key?: string | null;
  id?: string | null;
};

export type SublimitInfo = {
  extracted_sublimits: {
    [key: string]: Array<Sublimit>;
  };
  manual_sublimits: Array<Sublimit>;
};

export type SublimitSortEntry = {
  id: string | null;
  sublimit_name: string;
  classification: "time_limit" | "distance_limit" | "waiting_period" | null;
  sort_key: string;
};

/**
 * A reorder of sublimit rows within one displayed group.
 *
 * ``group="manual"`` targets ``manual_sublimits`` (``filename`` None);
 * ``group="extracted"`` targets a file's ``extracted_sublimits``. A reorder
 * that interleaves manual and extracted rows emits one update per source list.
 */
export type SublimitSortUpdate = {
  group: "manual" | "extracted";
  filename: string | null;
  entries: Array<SublimitSortEntry>;
};

export type SubmissionAnalyticsResponse = {
  reports: Array<SubmissionAnalyticsRow>;
  last_refreshed_at: string | null;
};

export type SubmissionAnalyticsRow = {
  report_id: string;
  lob: string;
  status: string;
  assignee: string | null;
  broker_organization_name: string | null;
  broker_name: string | null;
  mode_sic_prefix: string | null;
  mode_construction_code: string | null;
  sum_layer_target_premium: number | null;
  sum_policyfly_total_premium: number | null;
  total_tiv: number | null;
  num_locations: number | null;
  created_at: string;
  first_bound_at: string | null;
  furthest_stage: number;
};

export type SubmissionStatusCountsResponse = {
  pre_underwriting_review: number;
  cleared: number;
  in_progress: number;
  quoted: number;
  bound: number;
  indicated: number;
  quoted_premium: number;
  bound_premium: number;
};

export type Template = {
  template_id: string;
  client_id: string;
  author_id: string;
  use_case: string;
  created_at_ts: string;
  template_pointer: string;
  template_name: string;
  live: true | null;
};

/**
 * How a report's stored limit / sublimit / deductible text prints for its
 * policy term, so an editor can show the printed text while editing the
 * stored one (see term_wording).
 */
export type TermWording = {
  term: "annual" | "policy";
  effective_date: string | null;
  expiration_date: string | null;
  rewordings: {
    [key: string]: string;
  };
};

export type term = "annual" | "policy";

export const term = {
  ANNUAL: "annual",
  POLICY: "policy",
} as const;

export type TestPythonFunctionRequest = {
  peril_type:
    | "aop"
    | "earthquake"
    | "earth_movement"
    | "named_storm"
    | "scs"
    | "flood"
    | "wildfire"
    | "equipment_breakdown";
  extracted_filename?: string | null;
  function_type: "limit" | "deductible";
};

export type peril_type =
  | "aop"
  | "earthquake"
  | "earth_movement"
  | "named_storm"
  | "scs"
  | "flood"
  | "wildfire"
  | "equipment_breakdown";

export const peril_type = {
  AOP: "aop",
  EARTHQUAKE: "earthquake",
  EARTH_MOVEMENT: "earth_movement",
  NAMED_STORM: "named_storm",
  SCS: "scs",
  FLOOD: "flood",
  WILDFIRE: "wildfire",
  EQUIPMENT_BREAKDOWN: "equipment_breakdown",
} as const;

export type TestPythonFunctionResponse = {
  num_addresses_run: number;
};

/**
 * One forecast position along a storm's track.
 */
export type TrackPoint = {
  lat: number;
  lon: number;
  tau: number;
  max_wind_kt: number;
  classification: string;
  label: string;
  wind_radius_miles: number | null;
  radius_50kt_miles: number | null;
  radius_64kt_miles: number | null;
};

export type UnderwritingRationale = {
  text: string;
  last_updated_user_id: string;
  last_updated_ts: string;
};

export type UnknownCoverageColumnFlag = {
  flag_id: "unkonwn_coverage_column";
  columns: Array<string>;
};

export type UnknownInsuranceSubjectFlag = {
  flag_id: "non_property_insurance_value_v2";
  subjects: Array<string>;
};

export type UnlinkPolicyFlyApplicationRequest = {
  report_id: string;
  suffix: string;
};

export type UnlinkPolicyFlyApplicationResponse = {
  policy_fly_info: PolicyFlyInfo;
};

export type UpdateAddressRequest = {
  report_id: string;
  addresses: Array<Address>;
};

export type UpdateAddressResponse = {
  addresses: Array<Address>;
};

export type UpdateApplicationFormRequest = {
  application_id: string;
  form_id: string;
  enabled: boolean;
};

export type UpdateAsignee = {
  new_assignee: string | null;
};

export type UpdateBrokerContact = {
  new_broker_contact_id: string;
};

export type UpdateContactRequest = {
  full_name?: string | null;
  organization_name?: string | null;
  office_phone?: string | null;
  cell_phone?: string | null;
  role?: string | null;
};

export type UpdateContactResponse = {
  contact: ReportContact;
};

export type UpdateEndorsementPremiumRequest = {
  endorsement_id: string;
  premium_change: EndorsementPremiumChange;
};

export type UpdateEndorsementStatusRequest = {
  endorsement_id: string;
  status: EndorsementStatus;
};

export type UpdateGoogleGroup = {
  new_google_group_email: string | null;
};

export type UpdatePolicyApplicationRequest = {
  application_id: string;
  status: PolicyApplicationStatus;
};

export type UpdatePolicyFlyApplicationRequest = {
  report_id: string;
  suffix: string;
  perils_insured_description: string;
};

export type UpdatePolicyFlyApplicationResponse = {
  task_id: string;
};

export type UpdatePolicyOverlayRequest = {
  application_id: string;
  overlay: PolicyFieldOverlay;
  notes: string | null;
};

export type UpdateReportRequest = {
  report_id: string;
  update_assignee: UpdateAsignee | null;
  update_status: UpdateStatus | null;
  update_additional_assignees: AdditionalAssigneeInfo | null;
  update_sovs: Array<Sov> | null;
  update_loss_runs_info: LossRunInfo | null;
  update_review_question_response: ReviewQuestionResponse | null;
  update_company_info: CompanyInfo | null;
  update_attachment_info: AttachmentInfo | null;
  update_referral_text: ReferralText | null;
  update_underwriting_rationale: string | null;
  rerun_report: boolean | null;
  rerun_components: Array<RerunComponent> | null;
  rerun_attachments: Array<string> | null;
  update_broker_contact: UpdateBrokerContact | null;
  update_limit_info: LimitAndSublimitInfo | null;
  update_policy_info: PolicyInfo | null;
  update_sublimit_info: SublimitInfo | null;
  update_deductible_info: AdvancedDeductibleInfo | null;
  update_advanced_limit_info: AdvancedLimitInfo | null;
  update_layer_info: Array<Layer> | null;
  update_google_group_email: UpdateGoogleGroup | null;
  update_submission_recipients: Array<string> | null;
  update_limit_order: Array<ExcerptSortUpdate> | null;
  update_deductible_order: Array<ExcerptSortUpdate> | null;
  update_sublimit_order: Array<SublimitSortUpdate> | null;
};

export type UpdateReportResponse = {
  use_old_sovs: boolean;
  report: ReportResponse;
};

export type UpdateSavedFilterRequest = {
  id: string;
  is_default: boolean;
  nickname: string;
};

export type UpdateStatus = {
  new_status:
    | "pre_underwriting_review"
    | "blocked"
    | "not_cleared_prior_submission"
    | "not_cleared_incomplete_submission"
    | "not_cleared_out_of_appetite"
    | "not_cleared_effective_date_too_far"
    | "not_cleared_unapproved_broker"
    | "not_cleared_low_tiv"
    | "not_cleared_foreign_exposure"
    | "cleared"
    | "pending"
    | "modeling_complete"
    | "referral_sent"
    | "flagged_for_review"
    | "indicated"
    | "quoted"
    | "declined"
    | "closed"
    | "closed_bor"
    | "not_reviewed"
    | "lost"
    | "quoted_not_taken_up"
    | "bound"
    | "issued";
};

export type new_status =
  | "pre_underwriting_review"
  | "blocked"
  | "not_cleared_prior_submission"
  | "not_cleared_incomplete_submission"
  | "not_cleared_out_of_appetite"
  | "not_cleared_effective_date_too_far"
  | "not_cleared_unapproved_broker"
  | "not_cleared_low_tiv"
  | "not_cleared_foreign_exposure"
  | "cleared"
  | "pending"
  | "modeling_complete"
  | "referral_sent"
  | "flagged_for_review"
  | "indicated"
  | "quoted"
  | "declined"
  | "closed"
  | "closed_bor"
  | "not_reviewed"
  | "lost"
  | "quoted_not_taken_up"
  | "bound"
  | "issued";

export const new_status = {
  PRE_UNDERWRITING_REVIEW: "pre_underwriting_review",
  BLOCKED: "blocked",
  NOT_CLEARED_PRIOR_SUBMISSION: "not_cleared_prior_submission",
  NOT_CLEARED_INCOMPLETE_SUBMISSION: "not_cleared_incomplete_submission",
  NOT_CLEARED_OUT_OF_APPETITE: "not_cleared_out_of_appetite",
  NOT_CLEARED_EFFECTIVE_DATE_TOO_FAR: "not_cleared_effective_date_too_far",
  NOT_CLEARED_UNAPPROVED_BROKER: "not_cleared_unapproved_broker",
  NOT_CLEARED_LOW_TIV: "not_cleared_low_tiv",
  NOT_CLEARED_FOREIGN_EXPOSURE: "not_cleared_foreign_exposure",
  CLEARED: "cleared",
  PENDING: "pending",
  MODELING_COMPLETE: "modeling_complete",
  REFERRAL_SENT: "referral_sent",
  FLAGGED_FOR_REVIEW: "flagged_for_review",
  INDICATED: "indicated",
  QUOTED: "quoted",
  DECLINED: "declined",
  CLOSED: "closed",
  CLOSED_BOR: "closed_bor",
  NOT_REVIEWED: "not_reviewed",
  LOST: "lost",
  QUOTED_NOT_TAKEN_UP: "quoted_not_taken_up",
  BOUND: "bound",
  ISSUED: "issued",
} as const;

export type UpdateTemplate = {
  template_id: string;
  live: boolean;
};

export type UploadEmailThreadResponse = {
  was_new: boolean;
  eml_count: number;
  pf_pushed: number;
  staged_emails: Array<StagedEmailManifest>;
  reprocess_classifications: Array<
    | "sov_spreadsheet"
    | "acord_app"
    | "loss_run"
    | "none"
    | "screenshot"
    | "rater"
    | "loss_control_report"
    | "policy_document"
    | "manuscript_form"
    | "final_manuscript_form"
    | "specs_sheet"
    | "record_keeping"
    | "luminor_quote"
    | "luminor_tria"
    | "luminor_binder"
    | "luminor_policy"
    | "lead_quote"
    | "lead_binder"
    | "lead_policy"
    | "bound_sov"
    | "sl_letter"
    | "endorsement_file"
    | "endorsement_sov"
    | "engineering_report"
    | "rate_matrix"
    | "form_review"
  >;
};

export type User = {
  id: string;
  email: string;
  client_id: string;
  lobs: Array<string>;
  full_name?: string | null;
  profile_image_url?: string | null;
  phone?: string | null;
  scopes: Array<string>;
  user_type: UserType;
  is_client_admin?: boolean;
};

export type UserGeneratedLoss = {
  policy_year: string;
  date_of_loss: string;
  address: string | null;
  claim_id: string | null;
  loss_id: string | null;
  status: string;
  description: string | null;
  ground_up_loss: number | null;
  deductible: number | null;
  net_incurred: number | null;
  is_cat_event: boolean | null;
  claim_type: "historical" | "as_if" | null;
  source: "user_generated";
  source_details: SourceDetails;
};

export type UserGeneratedLossRunByYear = {
  policy_year: string;
  description_of_losses: string;
  calc_mode: "historical" | "as_if" | null;
  source_details: SourceDetails;
  open_claims: number;
  closed_claims: number;
  ground_up_loss: number | null;
  net_incurred: number | null;
  source: "user_generated";
};

/**
 * Classifies an account so non-human actors can be excluded from
 * "select a user" UIs. ``SERVICE`` covers automation / API / system accounts.
 */
export type UserType = "HUMAN" | "SERVICE";

/**
 * Classifies an account so non-human actors can be excluded from
 * "select a user" UIs. ``SERVICE`` covers automation / API / system accounts.
 */
export const UserType = {
  HUMAN: "HUMAN",
  SERVICE: "SERVICE",
} as const;

export type ValidationError = {
  loc: Array<string | number>;
  msg: string;
  type: string;
};

export type ValuationInfo = {
  physical_valuation_type_1:
    | "Excluded"
    | "Actual Cash Value"
    | "Replacement Cost"
    | "Functional Replacement Cost"
    | "Selling Price"
    | "Actual Loss Sustained"
    | "Agreed Value"
    | "Actual Cash Value, except Replacement cost if newer than 5 years old"
    | string
    | null;
  physical_valuation_type_2:
    | "Excluded"
    | "Actual Cash Value"
    | "Replacement Cost"
    | "Functional Replacement Cost"
    | "Selling Price"
    | "Actual Loss Sustained"
    | "Agreed Value"
    | "Actual Cash Value, except Replacement cost if newer than 5 years old"
    | string
    | null;
  bi_valuation_type:
    | "Excluded"
    | "Actual Cash Value"
    | "Replacement Cost"
    | "Functional Replacement Cost"
    | "Selling Price"
    | "Actual Loss Sustained"
    | "Agreed Value"
    | "Actual Cash Value, except Replacement cost if newer than 5 years old"
    | string
    | null;
  equipment_valuation_type:
    | "Excluded"
    | "Actual Cash Value"
    | "Replacement Cost"
    | "Functional Replacement Cost"
    | "Selling Price"
    | "Actual Loss Sustained"
    | "Agreed Value"
    | "Actual Cash Value, except Replacement cost if newer than 5 years old"
    | string
    | null;
};

export type VendorEnrichment = {
  vendor:
    | "spatialkat"
    | "rms_elt"
    | "rms_22"
    | "icm_ylt"
    | "rater"
    | "eqbd_rater";
  live_source: "nearmap" | "cape" | null;
  has_live_run: boolean;
  options: Array<VendorEnrichmentOption>;
};

export type VendorEnrichmentOption = {
  source: "nearmap" | "cape" | null;
  available: boolean;
};

export type VendorEnrichmentRequest = {
  selections: Array<VendorEnrichmentSelection>;
};

export type VendorEnrichmentSelection = {
  vendor:
    | "spatialkat"
    | "rms_elt"
    | "rms_22"
    | "icm_ylt"
    | "rater"
    | "eqbd_rater";
  source: "nearmap" | "cape" | null;
};

export type VendorEnrichmentState = {
  vendors: Array<VendorEnrichment>;
};

export type VendorPrecheck = {
  warnings: Array<string>;
  blocked: boolean;
  run_perils: Array<
    | "PWH"
    | "PSH"
    | "PWH_PSH"
    | "PES"
    | "PFL"
    | "PFL_PSH"
    | "PWB"
    | "PWW"
    | "PWT"
  >;
  skipped_perils: Array<
    | "PWH"
    | "PSH"
    | "PWH_PSH"
    | "PES"
    | "PFL"
    | "PFL_PSH"
    | "PWB"
    | "PWW"
    | "PWT"
  >;
  results_change: CatRunChangeResult;
};

/**
 * Status of a queued job.
 */
export type WorkStatus =
  | "pending"
  | "in_progress"
  | "done"
  | "failed"
  | "cancelled";

/**
 * Status of a queued job.
 */
export const WorkStatus = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  DONE: "done",
  FAILED: "failed",
  CANCELLED: "cancelled",
} as const;

export type YearBuiltBand = {
  band: string;
  tiv: number;
  pct_tiv: number;
};

export type YearIncorporatedDerivedFromSearch = {
  flag_id: "year_incorporated_derived_from_search";
  source: string;
};

export type BdxPerilBreakdownData = {
  formData: BodyBdxPerilBreakdown;
};

export type BdxPerilBreakdownResponse = unknown;

export type CreateBorLetterEndpointData = {
  requestBody: CreateBorLetterRequest;
};

export type CreateBorLetterEndpointResponse = BorLetterResponse;

export type GetBorLetterEndpointData = {
  requestBody: GetBorLetterRequest;
};

export type GetBorLetterEndpointResponse = BorLetterResponse;

export type GetBorLettersByReportEndpointData = {
  requestBody: GetBorLettersByReportRequest;
};

export type GetBorLettersByReportEndpointResponse = BorLetterListResponse;

export type ListBorLettersEndpointData = {
  requestBody: ListBorLettersRequest;
};

export type ListBorLettersEndpointResponse = BorLetterListResponse;

export type MarkBorStepEndpointData = {
  requestBody: MarkBorStepRequest;
};

export type MarkBorStepEndpointResponse = BorLetterResponse;

export type BorEmailPreviewEndpointData = {
  requestBody: BorEmailRequest;
};

export type BorEmailPreviewEndpointResponse = RenderedBorEmail;

export type BorSendEmailEndpointData = {
  requestBody: BorEmailRequest;
};

export type BorSendEmailEndpointResponse = BorLetterResponse;

export type ListContactsEndpointResponse = ListContactsResponse;

export type CreateContactEndpointData = {
  requestBody: CreateContactRequest;
};

export type CreateContactEndpointResponse = CreateContactResponse;

export type UpdateContactEndpointData = {
  contactId: string;
  requestBody: UpdateContactRequest;
};

export type UpdateContactEndpointResponse = UpdateContactResponse;

export type BrokerAnalyticsData = {
  dateChoice: "current_month" | "last_month" | "past_year" | "year_to_date";
  reportSelection: "created" | "brokerage_bind_rate" | "brokerage_quoted_rate";
};

export type BrokerAnalyticsResponse = GetBrokerAnalyticsResponse;

export type CrmBrokerExportResponse = unknown;

export type GetSubmissionStatusCountsResponse = SubmissionStatusCountsResponse;

export type GetPolicyflyApplicationsData = {
  statuses: string;
};

export type GetPolicyflyApplicationsResponse =
  ListPolicyFlyApplicationsResponse;

export type AsgardListReportActionLogsData = {
  requestBody: ListReportActionLogsRequest;
};

export type AsgardListReportActionLogsResponse = ListReportActionLogsResponse;

export type AsgardGetReportActionLogData = {
  requestBody: GetReportActionLogRequest;
};

export type AsgardGetReportActionLogResponse = GetReportActionLogResponse;

export type PrecheckData = {
  reportId: string;
};

export type PrecheckResponse2 = PrecheckResponse;

export type DownloadCatArtifactData = {
  /**
   * Artifact prefix to download
   */
  artifact: string;
  batchId: string;
  reportId: string;
  /**
   * Integration type: icm or rms
   */
  type: "spatialkat" | "rms_elt" | "rms_22" | "icm_ylt";
};

export type DownloadCatArtifactResponse = unknown;

export type DownloadAllCatArtifactsZipData = {
  batchId: string;
  reportId: string;
  /**
   * Integration type: icm or rms
   */
  type: "spatialkat" | "rms_elt" | "rms_22" | "icm_ylt";
};

export type DownloadAllCatArtifactsZipResponse = unknown;

export type GetCatJobHistoryData = {
  reportId: string;
  /**
   * Integration type: icm or rms.
   */
  type?: "spatialkat" | "rms_elt" | "rms_22" | "icm_ylt";
};

export type GetCatJobHistoryResponse = CatJobHistoryResponse;

export type GetLayerPmiData = {
  layerId: string;
  reportId: string;
  share: number;
};

export type GetLayerPmiResponse = LayerPmiResponse;

export type GetCatResultsData = {
  batchId?: string | null;
  reportId: string;
};

export type GetCatResultsResponse = CatResultsResponse;

export type RunCatModelData = {
  reportId: string;
  requestBody: CatRunRequest;
};

export type RunCatModelResponse = CatRunResult;

export type CatRunStatusesData = {
  requestBody: CatStatusRequest;
};

export type CatRunStatusesResponse = CatStatusResult;

export type ResumeCatPipelineData = {
  batchId: string;
  mode?: "resume" | "restart";
  reportId: string;
  type: "spatialkat" | "rms_elt" | "rms_22" | "icm_ylt";
};

export type ResumeCatPipelineResponse = CatResumeResult;

export type RerunCatFinancialData = {
  activate: boolean;
  batchId: string;
  reportId: string;
  type: "spatialkat" | "rms_elt" | "rms_22" | "icm_ylt";
};

export type RerunCatFinancialResponse = CatRerunFinancialResult;

export type ActivateCatBatchData = {
  batchId: string;
  reportId: string;
  type: "spatialkat" | "rms_elt" | "rms_22" | "icm_ylt";
};

export type ActivateCatBatchResponse = CatActivateResult;

export type GetVendorEnrichmentData = {
  reportId: string;
};

export type GetVendorEnrichmentResponse = VendorEnrichmentState;

export type SetVendorEnrichmentData = {
  reportId: string;
  requestBody: VendorEnrichmentRequest;
};

export type SetVendorEnrichmentResponse = VendorEnrichmentState;

export type GetDeductibleSummaryData = {
  requestBody: DeductibleSummaryRequest;
};

export type GetDeductibleSummaryResponse = DeductibleSummaryResponse;

export type AsgardRunFixSovData = {
  requestBody: RunFixSovRequest;
};

export type AsgardRunFixSovResponse = RunFixSovResponse;

export type AsgardSetFunctionInstructionsData = {
  requestBody: SetFunctionInstructionsRequest;
};

export type AsgardSetFunctionInstructionsResponse =
  SetFunctionInstructionsResponse;

export type AsgardListClearanceMetadataData = {
  requestBody: ListClearanceMetadataRequest;
};

export type AsgardListClearanceMetadataResponse = ListClearanceMetadataResponse;

export type AsgardIngestEmailData = {
  requestBody: {
    [key: string]: unknown;
  };
};

export type AsgardIngestEmailResponse = unknown;

export type InitiateCapeRequestData = {
  requestBody: CapeInitiateRequest;
};

export type InitiateCapeRequestResponse = CapeInitiateResponse;

export type CapeRequestStatusData = {
  requestId: string;
};

export type CapeRequestStatusResponse = IntegrationStatusResult;

export type GetCapeJobHistoryData = {
  reportId: string;
};

export type GetCapeJobHistoryResponse = CapeJobHistoryResponse;

export type EnqueueReplacementCostData = {
  requestBody: SindriEnqueueRequest;
};

export type EnqueueReplacementCostResponse = SindriEnqueueResponse;

export type GetIntegrationDataData = {
  requestBody: IntegrationDataRequest;
};

export type GetIntegrationDataResponse = IntegrationDataResponse;

export type GetFinancialModelAuditData = {
  requestBody: FinancialModelAuditRequest;
};

export type GetFinancialModelAuditResponse = FinancialModelAudit | null;

export type BulkEnrichSubmissionsData = {
  requestBody: BulkEnrichmentRequest;
};

export type BulkEnrichSubmissionsResponse = BulkEnrichmentResponse;

export type GetLimitSummaryData = {
  requestBody: LimitSummaryRequest;
};

export type GetLimitSummaryResponse = LimitSummaryResponse;

export type GetLayerGateExposureData = {
  requestBody: GateExposureRequest;
};

export type GetLayerGateExposureResponse = GateExposureResponse;

export type GetGateTrackingBaseData = {
  requestBody: GateTrackingBaseRequest;
};

export type GetGateTrackingBaseResponse = GateTrackingBaseResponse;

export type GetPortfolioGateAccumulationResponse =
  PortfolioGateAccumulationResponse;

export type GetAalPremiumGatePortfolioResponse = AalPremiumGateResponse;

export type GetAalPremiumGateTrackingData = {
  requestBody: AalPremiumGateTrackingRequest;
};

export type GetAalPremiumGateTrackingResponse = AalPremiumGateResponse;

export type StandardFormattingData = {
  requestBody: StandardFormattingRequest;
};

export type StandardFormattingResponse = FormattedText;

export type InitiateNearmapRequestData = {
  requestBody: NearmapInitiateRequest;
};

export type InitiateNearmapRequestResponse = NearmapInitiateResponse;

export type NearmapRequestStatusData = {
  requestId: string;
};

export type NearmapRequestStatusResponse = IntegrationStatusResult;

export type GetNearmapJobHistoryData = {
  reportId: string;
};

export type GetNearmapJobHistoryResponse = NearmapJobHistoryResponse;

export type PeriodicHourlyResponse = unknown;

export type PeriodicNightlyResponse = unknown;

export type PeriodicEvery5MResponse = unknown;

export type CalculatePremiumData = {
  requestBody: CalculatePremiumRequest;
};

export type CalculatePremiumResponse = CalculatedPremiumResponse;

export type GetResolvedPremiumsData = {
  requestBody: CalculatePremiumRequest;
};

export type GetResolvedPremiumsResponse = ResolvedPremiumsResponse;

export type ReconcilePolicyflyResponse = unknown;

export type DownloadRaterWorkbookData = {
  batchId: string;
  reportId: string;
};

export type DownloadRaterWorkbookResponse = unknown;

export type GetRateComponentsData = {
  batchId: string;
  reportId: string;
};

export type GetRateComponentsResponse = RateComponentsResponse;

export type GetEqbdRaterResultsData = {
  batchId: string;
  reportId: string;
};

export type GetEqbdRaterResultsResponse = EqbdRaterResultsResponse;

export type GetBookSummaryData = {
  lob: string;
};

export type GetBookSummaryResponse = BookSummaryResponse;

export type GetSubmissionAnalyticsResponse = SubmissionAnalyticsResponse;

export type GetExposureMapSummaryData = {
  requestBody: ExposureMapRequest;
};

export type GetExposureMapSummaryResponse = ExposureMapSummary;

export type GetExposureMapLocationsData = {
  requestBody: ExposureMapRequest;
};

export type GetExposureMapLocationsResponse = ExposureMapLocations;

export type GetAreaExposureData = {
  requestBody: AreaExposureRequest;
};

export type GetAreaExposureResponse = AreaExposure;

export type GetPolicyFootprintData = {
  requestBody: PolicyFootprintRequest;
};

export type GetPolicyFootprintResponse = PolicyFootprint;

export type GetActiveStormsResponse = ActiveStorms;

export type GetPastStormsResponse = PastStorms;

export type GetLocationClassificationsData = {
  requestBody: LocationClassificationsRequest;
};

export type GetLocationClassificationsResponse =
  LocationClassificationsResponse;

export type AsgardCreatePolicyApplicationsData = {
  requestBody: ReportPolicyApplicationsRequest;
};

export type AsgardCreatePolicyApplicationsResponse = PolicyApplicationsResponse;

export type AsgardListPolicyApplicationsData = {
  requestBody: ReportPolicyApplicationsRequest;
};

export type AsgardListPolicyApplicationsResponse = PolicyApplicationsResponse;

export type AsgardPolicyApplicationFieldsData = {
  requestBody: PolicyApplicationFieldsRequest;
};

export type AsgardPolicyApplicationFieldsResponse =
  PolicyApplicationFieldsResponse;

export type AsgardUpdatePolicyOverlayData = {
  requestBody: UpdatePolicyOverlayRequest;
};

export type AsgardUpdatePolicyOverlayResponse = PolicyApplicationFieldsResponse;

export type AsgardUpdatePolicyApplicationData = {
  requestBody: UpdatePolicyApplicationRequest;
};

export type AsgardUpdatePolicyApplicationResponse = PolicyApplicationView;

export type AsgardPolicyApplicationFormsData = {
  requestBody: PolicyApplicationFormsRequest;
};

export type AsgardPolicyApplicationFormsResponse =
  PolicyApplicationFormsResponse;

export type AsgardUpdateApplicationFormData = {
  requestBody: UpdateApplicationFormRequest;
};

export type AsgardUpdateApplicationFormResponse =
  PolicyApplicationFormsResponse;

export type AsgardApplicationQuoteTemplateData = {
  requestBody: PolicyApplicationFieldsRequest;
};

export type AsgardApplicationQuoteTemplateResponse = unknown;

export type AsgardSaveApplicationQuoteTemplateData = {
  formData: BodyAsgardSaveApplicationQuoteTemplate;
};

export type AsgardSaveApplicationQuoteTemplateResponse =
  SaveQuoteTemplateResponse;

export type AsgardResetApplicationQuoteTemplateData = {
  requestBody: PolicyApplicationFieldsRequest;
};

export type AsgardResetApplicationQuoteTemplateResponse =
  SaveQuoteTemplateResponse;

export type AsgardApplicationQuoteUnknownsData = {
  formData: BodyAsgardApplicationQuoteUnknowns;
};

export type AsgardApplicationQuoteUnknownsResponse = QuoteUnknownsResponse;

export type AsgardGenerateApplicationQuoteData = {
  formData: BodyAsgardGenerateApplicationQuote;
};

export type AsgardGenerateApplicationQuoteResponse = unknown;

export type AsgardListEndorsementsData = {
  requestBody: ListEndorsementsRequest;
};

export type AsgardListEndorsementsResponse = ListEndorsementsResponse;

export type AsgardCreateEndorsementData = {
  formData: BodyAsgardCreateEndorsement;
};

export type AsgardCreateEndorsementResponse = EndorsementView;

export type AsgardUploadEndorsementDocumentsData = {
  formData: BodyAsgardUploadEndorsementDocuments;
};

export type AsgardUploadEndorsementDocumentsResponse = EndorsementView;

export type AsgardSuggestEndorsementPremiumData = {
  requestBody: EndorsementRequest;
};

export type AsgardSuggestEndorsementPremiumResponse = EndorsementPremiumChange;

export type AsgardEstimateEndorsementPremiumData = {
  requestBody: EstimateEndorsementPremiumRequest;
};

export type AsgardEstimateEndorsementPremiumResponse = EndorsementPremiumChange;

export type AsgardUpdateEndorsementPremiumData = {
  requestBody: UpdateEndorsementPremiumRequest;
};

export type AsgardUpdateEndorsementPremiumResponse = EndorsementView;

export type AsgardUpdateEndorsementStatusData = {
  requestBody: UpdateEndorsementStatusRequest;
};

export type AsgardUpdateEndorsementStatusResponse = EndorsementView;

export type AsgardDeleteEndorsementData = {
  requestBody: EndorsementRequest;
};

export type AsgardDeleteEndorsementResponse = DeleteEndorsementResponse;

export type AsgardEndorsementSovDiffData = {
  requestBody: EndorsementRequest;
};

export type AsgardEndorsementSovDiffResponse = EndorsementSovDiffResponse;

export type AsgardRegenerateEndorsementData = {
  requestBody: EndorsementRequest;
};

export type AsgardRegenerateEndorsementResponse = EndorsementView;

export type HealthcheckResponse = unknown;

export type LoginData = {
  formData: BodyLogin;
};

export type LoginResponse2 = LoginResponse;

export type ListUsersResponse = ListUserResponse;

export type LogEventData = {
  requestBody: LogEventRequest;
};

export type LogEventResponse = unknown;

export type AsgardListReportsPostData = {
  requestBody: ListReportsRequest;
};

export type AsgardListReportsPostResponse = ListReportsResponse;

export type AsgardListReportData = {
  includeAddresses?: boolean;
  includeLossRun?: boolean;
  reportId: string;
};

export type AsgardListReportResponse = ReportResponse;

export type AsgardFreshnessData = {
  requestBody: FreshnessRequest;
};

export type AsgardFreshnessResponse = FreshnessResponse;

export type AsgardGoogleGroupStatsData = {
  requestBody: GoogleGroupStatsRequest;
};

export type AsgardGoogleGroupStatsResponse = GoogleGroupStats;

export type AsgardEmailThreadsData = {
  reportId: string;
};

export type AsgardEmailThreadsResponse = EmailThreadsResponse;

export type AsgardDeleteEmailThreadData = {
  reportId: string;
  requestBody: DeleteEmailThreadRequest;
};

export type AsgardDeleteEmailThreadResponse = DeleteEmailThreadResponse;

export type AsgardDownloadThreadAttachmentData = {
  requestBody: DownloadThreadAttachmentRequest;
};

export type AsgardDownloadThreadAttachmentResponse = unknown;

export type AsgardGenerateUnderwritingPdfData = {
  requestBody: GenerateUnderwritingPdfRequest;
};

export type AsgardGenerateUnderwritingPdfResponse = unknown;

export type AsgardQuoteIndicationPreflightData = {
  requestBody: QuoteIndicationPreflightRequest;
};

export type AsgardQuoteIndicationPreflightResponse = QuoteIndicationPreflight;

export type AsgardGenerateQuoteIndicationDraftData = {
  requestBody: GenerateQuoteIndicationRequest;
};

export type AsgardGenerateQuoteIndicationDraftResponse = DraftResponse;

export type AsgardQuoteIndicationHtmlData = {
  requestBody: GenerateQuoteIndicationRequest;
};

export type AsgardQuoteIndicationHtmlResponse = QuoteIndicationHtmlResponse;

export type AsgardGenerateReferralDraftData = {
  requestBody: GenerateReferralDraftRequest;
};

export type AsgardGenerateReferralDraftResponse = DraftResponse;

export type AsgardDownloadLossRunData = {
  requestBody: DownloadLossRunRequest;
};

export type AsgardDownloadLossRunResponse = unknown;

export type AsgardUpdateReportData = {
  requestBody: UpdateReportRequest;
};

export type AsgardUpdateReportResponse = UpdateReportResponse;

export type AsgardAddManualAddressData = {
  requestBody: AddManualAddressRequest;
};

export type AsgardAddManualAddressResponse = AddManualAddressResponse;

export type AsgardBatchCommentsData = {
  requestBody: BatchCommentsRequest;
};

export type AsgardBatchCommentsResponse = BatchCommentsResponse;

export type AsgardAddCommentData = {
  requestBody: AddCommentRequest;
};

export type AsgardAddCommentResponse = AddCommentResponse;

export type AsgardDeleteCommentData = {
  requestBody: DeleteCommentRequest;
};

export type AsgardDeleteCommentResponse = DeleteCommentResponse;

export type AsgardEditCommentData = {
  requestBody: EditCommentRequest;
};

export type AsgardEditCommentResponse = EditCommentResponse;

export type AsgardDeleteReportData = {
  requestBody: DeleteReportRequest;
};

export type AsgardDeleteReportResponse = DeleteReportResponse;

export type AsgardUpdateAddressData = {
  requestBody: UpdateAddressRequest;
};

export type AsgardUpdateAddressResponse = UpdateAddressResponse;

export type AsgardListAdditionalFilesData = {
  reportId: string;
};

export type AsgardListAdditionalFilesResponse = ListAdditionalFilesResponse;

export type AsgardDeleteAdditionalFilesData = {
  reportId: string;
  requestBody: DeleteAdditionalFilesRequest;
};

export type AsgardDeleteAdditionalFilesResponse = unknown;

export type AsgardDeleteAttachmentData = {
  requestBody: DeleteAttachmentRequest;
};

export type AsgardDeleteAttachmentResponse = AttachmentInfo;

export type AsgardTestPythonFunctionData = {
  reportId: string;
  requestBody: TestPythonFunctionRequest;
};

export type AsgardTestPythonFunctionResponse = TestPythonFunctionResponse;

export type AsgardUploadAdditionalFileData = {
  classificationForAllFiles?:
    | "sov_spreadsheet"
    | "acord_app"
    | "loss_run"
    | "none"
    | "screenshot"
    | "rater"
    | "loss_control_report"
    | "policy_document"
    | "manuscript_form"
    | "final_manuscript_form"
    | "specs_sheet"
    | "record_keeping"
    | "luminor_quote"
    | "luminor_tria"
    | "luminor_binder"
    | "luminor_policy"
    | "lead_quote"
    | "lead_binder"
    | "lead_policy"
    | "bound_sov"
    | "sl_letter"
    | "endorsement_file"
    | "endorsement_sov"
    | "engineering_report"
    | "rate_matrix"
    | "form_review"
    | null;
  formData: BodyAsgardUploadAdditionalFile;
  reportId: string;
  sourceForAllFiles?: "email" | "screenshot_tool" | "user_upload" | "generated";
};

export type AsgardUploadAdditionalFileResponse = unknown;

export type AsgardRerunOptionsData = {
  reportId: string;
};

export type AsgardRerunOptionsResponse = RerunOptionsResponse;

export type AsgardUploadEmailThreadData = {
  formData: BodyAsgardUploadEmailThread;
  reportId: string;
};

export type AsgardUploadEmailThreadResponse = UploadEmailThreadResponse;

export type AsgardClassifyStagedAttachmentData = {
  requestBody: ClassifyStagedAttachmentRequest;
};

export type AsgardClassifyStagedAttachmentResponse =
  ClassifyStagedAttachmentResponse;

export type AsgardExtractStagedAttachmentsData = {
  requestBody: ExtractStagedAttachmentsRequest;
};

export type AsgardExtractStagedAttachmentsResponse =
  ExtractStagedAttachmentsResponse;

export type AsgardDownloadAdditionalFileData = {
  requestBody: DownloadAdditionalFileRequest;
};

export type AsgardDownloadAdditionalFileResponse = unknown;

export type GenerateBulkEditData = {
  requestBody: BulkEditRequest;
};

export type GenerateBulkEditResponse = BulkEditResponse;

export type DownloadReportFileData = {
  requestBody: DownloadReportFileRequest;
};

export type DownloadReportFileResponse = unknown;

export type CspReportResponse = unknown;

export type AsgardListSavedFiltersResponse = ListSavedFiltersResponse;

export type AsgardDeleteSavedFilterData = {
  requestBody: DeleteSavedFilterRequest;
};

export type AsgardDeleteSavedFilterResponse = ListSavedFiltersResponse;

export type AsgardUpdateSavedFilterData = {
  requestBody: UpdateSavedFilterRequest;
};

export type AsgardUpdateSavedFilterResponse = ListSavedFiltersResponse;

export type AsgardAddSavedFilterData = {
  requestBody: AddSavedFilterRequest;
};

export type AsgardAddSavedFilterResponse = ListSavedFiltersResponse;

export type AsgardLoadAddressesForMapData = {
  requestBody: LoadAddressesForMapRequest;
};

export type AsgardLoadAddressesForMapResponse = LoadAddressesForMapResponse;

export type AsgardLoadAddressesData = {
  reportId: string;
  sovId: string;
};

export type AsgardLoadAddressesResponse = AddressList;

export type AsgardEmlPreviewData = {
  requestBody: EmlPreviewRequest;
};

export type AsgardEmlPreviewResponse = EmlPreviewResponse;

export type AsgardDownloadReportHistoryData = {
  requestBody: DownloadReportHistoryRequest;
};

export type AsgardDownloadReportHistoryResponse = unknown;

export type AsgardListUpdatingReportsData = {
  requestBody: ListUpdatingReportsRequest;
};

export type AsgardListUpdatingReportsResponse = ListUpdatingReportsResponse;

export type AsgardListReportJobsData = {
  requestBody: ListReportJobsRequest;
};

export type AsgardListReportJobsResponse = ListReportJobsResponse;

export type AsgardCancelJobData = {
  requestBody: CancelJobRequest;
};

export type AsgardCancelJobResponse = CancelJobResponse;

export type AsgardSetBatchPriorityData = {
  requestBody: SetBatchPriorityRequest;
};

export type AsgardSetBatchPriorityResponse = SetBatchPriorityResponse;

export type AsgardListTemplatesResponse = ListTemplatesResponse;

export type AsgardDeleteTemplateData = {
  requestBody: DeleteTemplate;
};

export type AsgardDeleteTemplateResponse = ListTemplatesResponse;

export type AsgardUpdateTemplateData = {
  requestBody: UpdateTemplate;
};

export type AsgardUpdateTemplateResponse = ListTemplatesResponse;

export type AsgardCreateTemplateData = {
  formData: BodyAsgardCreateTemplate;
  useCase:
    | "rater"
    | "proposal"
    | "binder"
    | "terrorism_declaration"
    | "property_declaration";
};

export type AsgardCreateTemplateResponse = ListTemplatesResponse;

export type AsgardDownloadTemplateData = {
  requestBody: DownloadTemplateRequest;
};

export type AsgardDownloadTemplateResponse = unknown;

export type AsgardGeneratePolicyNumbersData = {
  requestBody: GeneratePolicyNumbersRequest;
};

export type AsgardGeneratePolicyNumbersResponse = GeneratedPolicyNumbers;

export type AsgardGeneratePolicyFlyApplicationData = {
  requestBody: GeneratePolicyFlyApplicationRequest;
};

export type AsgardGeneratePolicyFlyApplicationResponse =
  GeneratePolicyFlyApplicationResponse;

export type AsgardUpdatePolicyFlyApplicationData = {
  requestBody: UpdatePolicyFlyApplicationRequest;
};

export type AsgardUpdatePolicyFlyApplicationResponse =
  UpdatePolicyFlyApplicationResponse;

export type AsgardLinkPolicyFlyApplicationData = {
  requestBody: LinkPolicyFlyApplicationRequest;
};

export type AsgardLinkPolicyFlyApplicationResponse =
  LinkPolicyFlyApplicationResponse;

export type AsgardUnlinkPolicyFlyApplicationData = {
  requestBody: UnlinkPolicyFlyApplicationRequest;
};

export type AsgardUnlinkPolicyFlyApplicationResponse =
  UnlinkPolicyFlyApplicationResponse;

export type AsgardClearanceEmailRecipientsData = {
  requestBody: ClearanceEmailRecipientsRequest;
};

export type AsgardClearanceEmailRecipientsResponse =
  ClearanceEmailRecipientsResponse;

export type AsgardSendClearanceEmailData = {
  requestBody: SendClearanceEmailRequest;
};

export type AsgardSendClearanceEmailResponse = GeneratedPolicyNumbers;

export type $OpenApiTs = {
  "/asgard/bdx/peril_breakdown": {
    post: {
      req: BdxPerilBreakdownData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/bor/create": {
    post: {
      req: CreateBorLetterEndpointData;
      res: {
        /**
         * Successful Response
         */
        200: BorLetterResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/bor/get": {
    post: {
      req: GetBorLetterEndpointData;
      res: {
        /**
         * Successful Response
         */
        200: BorLetterResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/bor/by_report": {
    post: {
      req: GetBorLettersByReportEndpointData;
      res: {
        /**
         * Successful Response
         */
        200: BorLetterListResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/bor/list": {
    post: {
      req: ListBorLettersEndpointData;
      res: {
        /**
         * Successful Response
         */
        200: BorLetterListResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/bor/mark_step": {
    post: {
      req: MarkBorStepEndpointData;
      res: {
        /**
         * Successful Response
         */
        200: BorLetterResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/bor/email_preview": {
    post: {
      req: BorEmailPreviewEndpointData;
      res: {
        /**
         * Successful Response
         */
        200: RenderedBorEmail;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/bor/send_email": {
    post: {
      req: BorSendEmailEndpointData;
      res: {
        /**
         * Successful Response
         */
        200: BorLetterResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/crm/contacts": {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: ListContactsResponse;
      };
    };
  };
  "/crm/contact": {
    post: {
      req: CreateContactEndpointData;
      res: {
        /**
         * Successful Response
         */
        200: CreateContactResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/crm/contact/{contact_id}": {
    post: {
      req: UpdateContactEndpointData;
      res: {
        /**
         * Successful Response
         */
        200: UpdateContactResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/crm/broker_analytics": {
    get: {
      req: BrokerAnalyticsData;
      res: {
        /**
         * Successful Response
         */
        200: GetBrokerAnalyticsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/crm/broker_export": {
    post: {
      res: {
        /**
         * Successful Response
         */
        200: unknown;
      };
    };
  };
  "/crm/submission_status_counts": {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: SubmissionStatusCountsResponse;
      };
    };
  };
  "/crm/policyfly_applications": {
    get: {
      req: GetPolicyflyApplicationsData;
      res: {
        /**
         * Successful Response
         */
        200: ListPolicyFlyApplicationsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/list_report_action_logs": {
    post: {
      req: AsgardListReportActionLogsData;
      res: {
        /**
         * Successful Response
         */
        200: ListReportActionLogsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/get_report_action_log": {
    post: {
      req: AsgardGetReportActionLogData;
      res: {
        /**
         * Successful Response
         */
        200: GetReportActionLogResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/precheck/{report_id}": {
    get: {
      req: PrecheckData;
      res: {
        /**
         * Successful Response
         */
        200: PrecheckResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/cat/download/{report_id}/{batch_id}": {
    get: {
      req: DownloadCatArtifactData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/cat/download_all/{report_id}/{batch_id}": {
    get: {
      req: DownloadAllCatArtifactsZipData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/cat/job_history/{report_id}": {
    get: {
      req: GetCatJobHistoryData;
      res: {
        /**
         * Successful Response
         */
        200: CatJobHistoryResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/cat/pmi/{report_id}/{layer_id}": {
    get: {
      req: GetLayerPmiData;
      res: {
        /**
         * Successful Response
         */
        200: LayerPmiResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/cat/results/{report_id}": {
    get: {
      req: GetCatResultsData;
      res: {
        /**
         * Successful Response
         */
        200: CatResultsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/cat/{report_id}/run": {
    post: {
      req: RunCatModelData;
      res: {
        /**
         * Successful Response
         */
        200: CatRunResult;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/cat/status": {
    post: {
      req: CatRunStatusesData;
      res: {
        /**
         * Successful Response
         */
        200: CatStatusResult;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/cat/{report_id}/resume": {
    post: {
      req: ResumeCatPipelineData;
      res: {
        /**
         * Successful Response
         */
        200: CatResumeResult;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/cat/{report_id}/rerun-financial": {
    post: {
      req: RerunCatFinancialData;
      res: {
        /**
         * Successful Response
         */
        200: CatRerunFinancialResult;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/cat/{report_id}/activate": {
    post: {
      req: ActivateCatBatchData;
      res: {
        /**
         * Successful Response
         */
        200: CatActivateResult;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/cat/{report_id}/vendor_enrichment": {
    get: {
      req: GetVendorEnrichmentData;
      res: {
        /**
         * Successful Response
         */
        200: VendorEnrichmentState;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
    put: {
      req: SetVendorEnrichmentData;
      res: {
        /**
         * Successful Response
         */
        200: VendorEnrichmentState;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/deductible_summary": {
    post: {
      req: GetDeductibleSummaryData;
      res: {
        /**
         * Successful Response
         */
        200: DeductibleSummaryResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/run_fix_sov": {
    post: {
      req: AsgardRunFixSovData;
      res: {
        /**
         * Successful Response
         */
        200: RunFixSovResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/set_function_instructions": {
    post: {
      req: AsgardSetFunctionInstructionsData;
      res: {
        /**
         * Successful Response
         */
        200: SetFunctionInstructionsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/list_clearance_metadata": {
    post: {
      req: AsgardListClearanceMetadataData;
      res: {
        /**
         * Successful Response
         */
        200: ListClearanceMetadataResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/ingest_email": {
    post: {
      req: AsgardIngestEmailData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/cape_analytics/initiate": {
    post: {
      req: InitiateCapeRequestData;
      res: {
        /**
         * Successful Response
         */
        200: CapeInitiateResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/cape_analytics/status/{request_id}": {
    get: {
      req: CapeRequestStatusData;
      res: {
        /**
         * Successful Response
         */
        200: IntegrationStatusResult;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/cape_analytics/job_history/{report_id}": {
    get: {
      req: GetCapeJobHistoryData;
      res: {
        /**
         * Successful Response
         */
        200: CapeJobHistoryResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/sindri/replacement_cost": {
    post: {
      req: EnqueueReplacementCostData;
      res: {
        /**
         * Successful Response
         */
        200: SindriEnqueueResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/integration_data": {
    post: {
      req: GetIntegrationDataData;
      res: {
        /**
         * Successful Response
         */
        200: IntegrationDataResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/financial_model_audit": {
    post: {
      req: GetFinancialModelAuditData;
      res: {
        /**
         * Successful Response
         */
        200: FinancialModelAudit | null;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/enrichment/bulk": {
    post: {
      req: BulkEnrichSubmissionsData;
      res: {
        /**
         * Successful Response
         */
        200: BulkEnrichmentResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/limit_summary": {
    post: {
      req: GetLimitSummaryData;
      res: {
        /**
         * Successful Response
         */
        200: LimitSummaryResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/layer_gate_exposure": {
    post: {
      req: GetLayerGateExposureData;
      res: {
        /**
         * Successful Response
         */
        200: GateExposureResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/gate_tracking_base": {
    post: {
      req: GetGateTrackingBaseData;
      res: {
        /**
         * Successful Response
         */
        200: GateTrackingBaseResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/portfolio_gate_accumulation": {
    post: {
      res: {
        /**
         * Successful Response
         */
        200: PortfolioGateAccumulationResponse;
      };
    };
  };
  "/aal_premium_gate_portfolio": {
    post: {
      res: {
        /**
         * Successful Response
         */
        200: AalPremiumGateResponse;
      };
    };
  };
  "/aal_premium_gate_tracking": {
    post: {
      req: GetAalPremiumGateTrackingData;
      res: {
        /**
         * Successful Response
         */
        200: AalPremiumGateResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/standard_formatting": {
    post: {
      req: StandardFormattingData;
      res: {
        /**
         * Successful Response
         */
        200: FormattedText;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/nearmap/initiate": {
    post: {
      req: InitiateNearmapRequestData;
      res: {
        /**
         * Successful Response
         */
        200: NearmapInitiateResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/nearmap/status/{request_id}": {
    get: {
      req: NearmapRequestStatusData;
      res: {
        /**
         * Successful Response
         */
        200: IntegrationStatusResult;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/nearmap/job_history/{report_id}": {
    get: {
      req: GetNearmapJobHistoryData;
      res: {
        /**
         * Successful Response
         */
        200: NearmapJobHistoryResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/periodic/hourly": {
    post: {
      res: {
        /**
         * Successful Response
         */
        200: unknown;
      };
    };
  };
  "/periodic/nightly": {
    post: {
      res: {
        /**
         * Successful Response
         */
        200: unknown;
      };
    };
  };
  "/periodic/every_5m": {
    post: {
      res: {
        /**
         * Successful Response
         */
        200: unknown;
      };
    };
  };
  "/asgard/premium/calculated": {
    post: {
      req: CalculatePremiumData;
      res: {
        /**
         * Successful Response
         */
        200: CalculatedPremiumResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/premium/resolved": {
    post: {
      req: GetResolvedPremiumsData;
      res: {
        /**
         * Successful Response
         */
        200: ResolvedPremiumsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/reconcile_policyfly": {
    post: {
      res: {
        /**
         * Successful Response
         */
        200: unknown;
      };
    };
  };
  "/rater/download/{report_id}/{batch_id}": {
    get: {
      req: DownloadRaterWorkbookData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/rater/rate_components/{report_id}/{batch_id}": {
    get: {
      req: GetRateComponentsData;
      res: {
        /**
         * Successful Response
         */
        200: RateComponentsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/eqbd_rater/results/{report_id}/{batch_id}": {
    get: {
      req: GetEqbdRaterResultsData;
      res: {
        /**
         * Successful Response
         */
        200: EqbdRaterResultsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/reinsurer_view/book_summary": {
    get: {
      req: GetBookSummaryData;
      res: {
        /**
         * Successful Response
         */
        200: BookSummaryResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/submission_analytics": {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: SubmissionAnalyticsResponse;
      };
    };
  };
  "/asgard/exposure_map/summary": {
    post: {
      req: GetExposureMapSummaryData;
      res: {
        /**
         * Successful Response
         */
        200: ExposureMapSummary;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/exposure_map/locations": {
    post: {
      req: GetExposureMapLocationsData;
      res: {
        /**
         * Successful Response
         */
        200: ExposureMapLocations;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/exposure_map/area": {
    post: {
      req: GetAreaExposureData;
      res: {
        /**
         * Successful Response
         */
        200: AreaExposure;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/exposure_map/policy_footprint": {
    post: {
      req: GetPolicyFootprintData;
      res: {
        /**
         * Successful Response
         */
        200: PolicyFootprint;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/storms/active": {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: ActiveStorms;
      };
    };
  };
  "/asgard/storms/past": {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: PastStorms;
      };
    };
  };
  "/location_classifications": {
    post: {
      req: GetLocationClassificationsData;
      res: {
        /**
         * Successful Response
         */
        200: LocationClassificationsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/policy/create_applications": {
    post: {
      req: AsgardCreatePolicyApplicationsData;
      res: {
        /**
         * Successful Response
         */
        200: PolicyApplicationsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/policy/list_applications": {
    post: {
      req: AsgardListPolicyApplicationsData;
      res: {
        /**
         * Successful Response
         */
        200: PolicyApplicationsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/policy/application_fields": {
    post: {
      req: AsgardPolicyApplicationFieldsData;
      res: {
        /**
         * Successful Response
         */
        200: PolicyApplicationFieldsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/policy/update_overlay": {
    post: {
      req: AsgardUpdatePolicyOverlayData;
      res: {
        /**
         * Successful Response
         */
        200: PolicyApplicationFieldsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/policy/update_application": {
    post: {
      req: AsgardUpdatePolicyApplicationData;
      res: {
        /**
         * Successful Response
         */
        200: PolicyApplicationView;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/policy/application_forms": {
    post: {
      req: AsgardPolicyApplicationFormsData;
      res: {
        /**
         * Successful Response
         */
        200: PolicyApplicationFormsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/policy/update_application_form": {
    post: {
      req: AsgardUpdateApplicationFormData;
      res: {
        /**
         * Successful Response
         */
        200: PolicyApplicationFormsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/policy/application_quote_template": {
    post: {
      req: AsgardApplicationQuoteTemplateData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/policy/save_application_quote_template": {
    post: {
      req: AsgardSaveApplicationQuoteTemplateData;
      res: {
        /**
         * Successful Response
         */
        200: SaveQuoteTemplateResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/policy/reset_application_quote_template": {
    post: {
      req: AsgardResetApplicationQuoteTemplateData;
      res: {
        /**
         * Successful Response
         */
        200: SaveQuoteTemplateResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/policy/application_quote_unknowns": {
    post: {
      req: AsgardApplicationQuoteUnknownsData;
      res: {
        /**
         * Successful Response
         */
        200: QuoteUnknownsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/policy/generate_application_quote": {
    post: {
      req: AsgardGenerateApplicationQuoteData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/endorsements/list": {
    post: {
      req: AsgardListEndorsementsData;
      res: {
        /**
         * Successful Response
         */
        200: ListEndorsementsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/endorsements/create": {
    post: {
      req: AsgardCreateEndorsementData;
      res: {
        /**
         * Successful Response
         */
        200: EndorsementView;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/endorsements/upload_documents": {
    post: {
      req: AsgardUploadEndorsementDocumentsData;
      res: {
        /**
         * Successful Response
         */
        200: EndorsementView;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/endorsements/suggest_premium": {
    post: {
      req: AsgardSuggestEndorsementPremiumData;
      res: {
        /**
         * Successful Response
         */
        200: EndorsementPremiumChange;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/endorsements/estimate_premium": {
    post: {
      req: AsgardEstimateEndorsementPremiumData;
      res: {
        /**
         * Successful Response
         */
        200: EndorsementPremiumChange;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/endorsements/update_premium": {
    post: {
      req: AsgardUpdateEndorsementPremiumData;
      res: {
        /**
         * Successful Response
         */
        200: EndorsementView;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/endorsements/update_status": {
    post: {
      req: AsgardUpdateEndorsementStatusData;
      res: {
        /**
         * Successful Response
         */
        200: EndorsementView;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/endorsements/delete": {
    post: {
      req: AsgardDeleteEndorsementData;
      res: {
        /**
         * Successful Response
         */
        200: DeleteEndorsementResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/endorsements/sov_diff": {
    post: {
      req: AsgardEndorsementSovDiffData;
      res: {
        /**
         * Successful Response
         */
        200: EndorsementSovDiffResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/endorsements/regenerate": {
    post: {
      req: AsgardRegenerateEndorsementData;
      res: {
        /**
         * Successful Response
         */
        200: EndorsementView;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/healthcheck": {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: unknown;
      };
    };
  };
  "/login": {
    post: {
      req: LoginData;
      res: {
        /**
         * Successful Response
         */
        200: LoginResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/list_users": {
    post: {
      res: {
        /**
         * Successful Response
         */
        200: ListUserResponse;
      };
    };
  };
  "/log_event": {
    post: {
      req: LogEventData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/list_reports": {
    post: {
      req: AsgardListReportsPostData;
      res: {
        /**
         * Successful Response
         */
        200: ListReportsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/list_report/{report_id}": {
    get: {
      req: AsgardListReportData;
      res: {
        /**
         * Successful Response
         */
        200: ReportResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/freshness": {
    post: {
      req: AsgardFreshnessData;
      res: {
        /**
         * Successful Response
         */
        200: FreshnessResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/google_group_stats": {
    post: {
      req: AsgardGoogleGroupStatsData;
      res: {
        /**
         * Successful Response
         */
        200: GoogleGroupStats;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/email_threads/{report_id}": {
    get: {
      req: AsgardEmailThreadsData;
      res: {
        /**
         * Successful Response
         */
        200: EmailThreadsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/delete_email_thread/{report_id}": {
    post: {
      req: AsgardDeleteEmailThreadData;
      res: {
        /**
         * Successful Response
         */
        200: DeleteEmailThreadResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/download_thread_attachment": {
    post: {
      req: AsgardDownloadThreadAttachmentData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/generate_underwriting_pdf": {
    post: {
      req: AsgardGenerateUnderwritingPdfData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/quote_indication_preflight": {
    post: {
      req: AsgardQuoteIndicationPreflightData;
      res: {
        /**
         * Successful Response
         */
        200: QuoteIndicationPreflight;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/generate_quote_indication_draft": {
    post: {
      req: AsgardGenerateQuoteIndicationDraftData;
      res: {
        /**
         * Successful Response
         */
        200: DraftResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/quote_indication_html": {
    post: {
      req: AsgardQuoteIndicationHtmlData;
      res: {
        /**
         * Successful Response
         */
        200: QuoteIndicationHtmlResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/generate_referral_draft": {
    post: {
      req: AsgardGenerateReferralDraftData;
      res: {
        /**
         * Successful Response
         */
        200: DraftResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/download_loss_run": {
    post: {
      req: AsgardDownloadLossRunData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/update_report": {
    post: {
      req: AsgardUpdateReportData;
      res: {
        /**
         * Successful Response
         */
        200: UpdateReportResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/add_manual_address": {
    post: {
      req: AsgardAddManualAddressData;
      res: {
        /**
         * Successful Response
         */
        200: AddManualAddressResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/comments": {
    post: {
      req: AsgardBatchCommentsData;
      res: {
        /**
         * Successful Response
         */
        200: BatchCommentsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/add_comment": {
    post: {
      req: AsgardAddCommentData;
      res: {
        /**
         * Successful Response
         */
        200: AddCommentResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/delete_comment": {
    post: {
      req: AsgardDeleteCommentData;
      res: {
        /**
         * Successful Response
         */
        200: DeleteCommentResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/edit_comment": {
    post: {
      req: AsgardEditCommentData;
      res: {
        /**
         * Successful Response
         */
        200: EditCommentResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/delete_report": {
    post: {
      req: AsgardDeleteReportData;
      res: {
        /**
         * Successful Response
         */
        200: DeleteReportResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/update_address": {
    post: {
      req: AsgardUpdateAddressData;
      res: {
        /**
         * Successful Response
         */
        200: UpdateAddressResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/list_additional_files/{report_id}": {
    post: {
      req: AsgardListAdditionalFilesData;
      res: {
        /**
         * Successful Response
         */
        200: ListAdditionalFilesResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/delete_additional_files/{report_id}": {
    post: {
      req: AsgardDeleteAdditionalFilesData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/delete_attachment": {
    post: {
      req: AsgardDeleteAttachmentData;
      res: {
        /**
         * Successful Response
         */
        200: AttachmentInfo;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/test_python_function/{report_id}": {
    post: {
      req: AsgardTestPythonFunctionData;
      res: {
        /**
         * Successful Response
         */
        200: TestPythonFunctionResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/upload_additional_files": {
    post: {
      req: AsgardUploadAdditionalFileData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/rerun_options": {
    get: {
      req: AsgardRerunOptionsData;
      res: {
        /**
         * Successful Response
         */
        200: RerunOptionsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/upload_email_thread": {
    post: {
      req: AsgardUploadEmailThreadData;
      res: {
        /**
         * Successful Response
         */
        200: UploadEmailThreadResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/classify_staged_attachment": {
    post: {
      req: AsgardClassifyStagedAttachmentData;
      res: {
        /**
         * Successful Response
         */
        200: ClassifyStagedAttachmentResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/extract_staged_attachments": {
    post: {
      req: AsgardExtractStagedAttachmentsData;
      res: {
        /**
         * Successful Response
         */
        200: ExtractStagedAttachmentsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/download_additional_file": {
    post: {
      req: AsgardDownloadAdditionalFileData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/generate_bulk_edit": {
    post: {
      req: GenerateBulkEditData;
      res: {
        /**
         * Successful Response
         */
        200: BulkEditResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/download_report_file": {
    post: {
      req: DownloadReportFileData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/csp-report": {
    post: {
      res: {
        /**
         * Successful Response
         */
        200: unknown;
      };
    };
  };
  "/asgard/list_saved_filters": {
    post: {
      res: {
        /**
         * Successful Response
         */
        200: ListSavedFiltersResponse;
      };
    };
  };
  "/asgard/delete_saved_filter": {
    post: {
      req: AsgardDeleteSavedFilterData;
      res: {
        /**
         * Successful Response
         */
        200: ListSavedFiltersResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/update_saved_filter": {
    post: {
      req: AsgardUpdateSavedFilterData;
      res: {
        /**
         * Successful Response
         */
        200: ListSavedFiltersResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/add_saved_filter": {
    post: {
      req: AsgardAddSavedFilterData;
      res: {
        /**
         * Successful Response
         */
        200: ListSavedFiltersResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/load_addresses_for_map": {
    post: {
      req: AsgardLoadAddressesForMapData;
      res: {
        /**
         * Successful Response
         */
        200: LoadAddressesForMapResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/load_addresses/{report_id}/{sov_id}": {
    get: {
      req: AsgardLoadAddressesData;
      res: {
        /**
         * Successful Response
         */
        200: AddressList;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/eml_preview": {
    post: {
      req: AsgardEmlPreviewData;
      res: {
        /**
         * Successful Response
         */
        200: EmlPreviewResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/download_report_history": {
    post: {
      req: AsgardDownloadReportHistoryData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/list_updating_reports": {
    post: {
      req: AsgardListUpdatingReportsData;
      res: {
        /**
         * Successful Response
         */
        200: ListUpdatingReportsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/list_report_jobs": {
    post: {
      req: AsgardListReportJobsData;
      res: {
        /**
         * Successful Response
         */
        200: ListReportJobsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/cancel_job": {
    post: {
      req: AsgardCancelJobData;
      res: {
        /**
         * Successful Response
         */
        200: CancelJobResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/set_batch_priority": {
    post: {
      req: AsgardSetBatchPriorityData;
      res: {
        /**
         * Successful Response
         */
        200: SetBatchPriorityResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/list_templates": {
    post: {
      res: {
        /**
         * Successful Response
         */
        200: ListTemplatesResponse;
      };
    };
  };
  "/asgard/delete_template": {
    post: {
      req: AsgardDeleteTemplateData;
      res: {
        /**
         * Successful Response
         */
        200: ListTemplatesResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/update_template": {
    post: {
      req: AsgardUpdateTemplateData;
      res: {
        /**
         * Successful Response
         */
        200: ListTemplatesResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/create_template": {
    post: {
      req: AsgardCreateTemplateData;
      res: {
        /**
         * Successful Response
         */
        200: ListTemplatesResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/download_template": {
    post: {
      req: AsgardDownloadTemplateData;
      res: {
        /**
         * Successful Response
         */
        200: unknown;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/generate_policy_numbers": {
    post: {
      req: AsgardGeneratePolicyNumbersData;
      res: {
        /**
         * Successful Response
         */
        200: GeneratedPolicyNumbers;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/generate_policy_fly_application": {
    post: {
      req: AsgardGeneratePolicyFlyApplicationData;
      res: {
        /**
         * Successful Response
         */
        200: GeneratePolicyFlyApplicationResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/update_policy_fly_application": {
    post: {
      req: AsgardUpdatePolicyFlyApplicationData;
      res: {
        /**
         * Successful Response
         */
        200: UpdatePolicyFlyApplicationResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/link_policy_fly_application": {
    post: {
      req: AsgardLinkPolicyFlyApplicationData;
      res: {
        /**
         * Successful Response
         */
        200: LinkPolicyFlyApplicationResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/unlink_policy_fly_application": {
    post: {
      req: AsgardUnlinkPolicyFlyApplicationData;
      res: {
        /**
         * Successful Response
         */
        200: UnlinkPolicyFlyApplicationResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/clearance_email_recipients": {
    post: {
      req: AsgardClearanceEmailRecipientsData;
      res: {
        /**
         * Successful Response
         */
        200: ClearanceEmailRecipientsResponse;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
  "/asgard/send_clearance_email": {
    post: {
      req: AsgardSendClearanceEmailData;
      res: {
        /**
         * Successful Response
         */
        200: GeneratedPolicyNumbers;
        /**
         * Validation Error
         */
        422: HttpValidationError;
      };
    };
  };
};
